<script>
	import "./main.css";
	import { onMount } from "svelte";
	import CrestImage from "./CrestImage.svelte";

	const backendURL = "https://ifceventhubbackend-c8725a4af42e.herokuapp.com";

	const EVENT_TYPES = [
		{ value: "party", label: "Party" },
		{ value: "brotherhood", label: "Brotherhood" },
		{ value: "datedash", label: "Date Dash" },
		{ value: "rush", label: "Rush" },
	];

	const HOURS = [
		"1",
		"2",
		"3",
		"4",
		"5",
		"6",
		"7",
		"8",
		"9",
		"10",
		"11",
		"12",
	];
	const MINUTES = ["00", "15", "30", "45"];

	const FRAT_OPTIONS = [
		"Alpha Chi Omega",
		"Alpha Epsilon Pi",
		"Alpha Kappa Lambda",
		"Alpha Phi",
		"Alpha Sigma Phi",
		"Alpha Tau Omega",
		"Beta Theta Pi",
		"Chi Omega",
		"Chi Psi",
		"Delta Delta Delta",
		"Delta Gamma",
		"Delta Kappa Epsilon",
		"Delta Sigma Phi",
		"Delta Tau Delta",
		"Gamma Phi Beta",
		"Kappa Alpha Order",
		"Kappa Alpha Theta",
		"Kappa Delta",
		"Kappa Kappa Gamma",
		"Lambda Chi Alpha",
		"Phi Delta Theta",
		"Phi Gamma Delta",
		"Phi Kappa Psi",
		"Pi Beta Phi",
		"Pi Kappa Alpha",
		"Pi Kappa Phi",
		"Sigma Alpha Epsilon",
		"Sigma Chi",
		"Sigma Kappa",
		"Sigma Nu",
		"Sigma Phi Epsilon",
		"Tau Kappa Epsilon",
		"Theta Chi",
		"Theta Xi",
		"Zeta Beta Tau",
		"Zeta Tau Alpha",
	];

	const FRAT_SET = new Set(FRAT_OPTIONS);

	function normalizeFratName(s) {
		return String(s || "").trim();
	}

	const MAX_EMERGENCY_PHONES = 8;

	function buildDateTime(date, hour, minute, ampm) {
		if (!date || !hour || !minute || !ampm) return "";

		let h = parseInt(hour, 10);
		if (ampm === "PM" && h !== 12) h += 12;
		if (ampm === "AM" && h === 12) h = 0;

		const [year, month, day] = date.split("-");
		if (!year || !month || !day) return "";

		const hh = String(h).padStart(2, "0");
		const mm = String(minute).padStart(2, "0");
		const mon = String(month).padStart(2, "0");
		const dd = String(day).padStart(2, "0");

		return `${year}-${mon}-${dd}T${hh}:${mm}:00`;
	}

	let startDate = "";
	let startHour = "8";
	let startMinute = "00";
	let startAmPm = "PM";

	let endDate = "";
	let endHour = "11";
	let endMinute = "00";
	let endAmPm = "PM";

	$: newEventStart = buildDateTime(
		startDate,
		startHour,
		startMinute,
		startAmPm,
	);
	$: newEventEnd = buildDateTime(endDate, endHour, endMinute, endAmPm);
	let buzzRows = [];

	$: {
		buzzData?.events;
		buzzRows = buzzScheduleRows();
	}

	let token = "";
	// currentUser shape (relevant fields):
	// { username, role, fratName, fratAddress, emergencyPhones: string[], contacts: [{fullName,email,phone}] }
	let currentUser = null;
	let isDark = false;

	let showAccessModal = false;

	function openAccessModal() {
		showAccessModal = true;
		if (typeof document !== "undefined")
			document.body.classList.add("modal-open");
	}

	function closeAccessModal() {
		showAccessModal = false;
		if (typeof document !== "undefined")
			document.body.classList.remove("modal-open");
	}

	onMount(() => {
		const onKeyDown = (e) => {
			if (e.key === "Escape" && showAccessModal) closeAccessModal();
			if (e.key === "Escape" && showSignupModal) closeSignupModal();
			if (e.key === "Escape" && showNewEventModal) closeNewEventModal();
			if (e.key === "Escape" && showBuzzEventModal) closeBuzzEventModal();
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	});

	// -----------------------------
	// Signup modal (verification request)
	// -----------------------------
	let showSignupModal = false;
	let signupStep = "form"; // "form" | "verify" | "done"

	let suFratName = "";
	let suFullName = "";
	let suPhone = "";
	let suEmail = "";
	let suPosition = "";

	let suBusy = false;
	let suError = "";
	let suSuccess = "";
	let suAddress = "";

	function openSignupModal() {
		showSignupModal = true;
		suPhoneConfirm = "";
		suAddress = "";
		suPosition = "";

		signupStep = "form";
		suFratName = "";
		suFullName = "";
		suPhone = "";
		suEmail = "";
		suBusy = false;
		suError = "";
		suSuccess = "";
		if (typeof document !== "undefined")
			document.body.classList.add("modal-open");
	}

	function closeSignupModal() {
		showSignupModal = false;
		if (typeof document !== "undefined")
			document.body.classList.remove("modal-open");
	}

	function normalizeUSPhone(raw) {
		const d = (raw || "").replace(/\D/g, "");
		if (!d) return "";
		if (d.length === 10) return `+1${d}`;
		if (d.length === 11 && d.startsWith("1")) return `+${d}`;
		if (raw.trim().startsWith("+")) return raw.trim();
		return raw.trim();
	}

	// Display-only: shows (XXX) XXX-XXXX and hides leading +1/1 if present.
	// IMPORTANT: this does NOT modify what you store unless the user types into the field.
	function formatPhoneForDisplay(raw) {
		const s = String(raw ?? "").trim();
		if (!s) return "";

		const digits = s.replace(/\D/g, "");
		if (!digits) return s;

		let d = digits;
		if (d.length === 11 && d.startsWith("1")) d = d.slice(1); // drop leading 1 for display

		if (d.length !== 10) return s;

		return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
	}

	function sanitizeEmergencyPhones(list) {
		const arr = Array.isArray(list) ? list : [];
		const norm = arr
			.map((x) => normalizeUSPhone(String(x || "")))
			.map((x) => x.trim())
			.filter((x) => !!x);

		const seen = new Set();
		const out = [];
		for (const p of norm) {
			const key = p.replace(/\D/g, "");
			if (!key) continue;
			if (seen.has(key)) continue;
			seen.add(key);
			out.push(p);
			if (out.length >= MAX_EMERGENCY_PHONES) break;
		}
		return out;
	}

	function ensureEditablePhoneList(list) {
		const cleaned = Array.isArray(list)
			? list.map((x) => String(x ?? ""))
			: [];
		if (cleaned.length === 0) return [""];
		return cleaned.slice(0, MAX_EMERGENCY_PHONES);
	}

	function addPhoneRow(listSetter, listGetter) {
		const cur = listGetter();
		if (cur.length >= MAX_EMERGENCY_PHONES) return;
		listSetter([...cur, ""]);
	}

	function removePhoneRow(listSetter, listGetter, idx) {
		const cur = listGetter();
		const next = cur.filter((_, i) => i !== idx);
		listSetter(next.length ? next : [""]);
	}

	function validateSignup() {
		if (!suFratName) return "Select your fraternity.";
		if (!suFullName.trim()) return "Full name is required.";
		if (!suEmail.trim()) return "Email is required.";
		if (!suPhone.trim()) return "Phone number is required.";
		if (!suPhoneConfirm.trim()) return "Confirm phone number is required.";
		if (!suAddress.trim()) return "Address is required.";
		if (!suPosition.trim()) return "Position is required.";

		const p1 = normalizeUSPhone(suPhone);
		const p2 = normalizeUSPhone(suPhoneConfirm);

		if (!p1 || !p2) return "Invalid phone number.";
		if (p1.replace(/\D/g, "") !== p2.replace(/\D/g, ""))
			return "Phone numbers do not match.";

		return "";
	}

	async function submitSignup() {
		suError = "";
		suSuccess = "";

		const err = validateSignup();
		if (err) {
			suError = err;
			return;
		}

		suBusy = true;
		try {
			const res = await fetch(`${backendURL}/signup/chapter-exec`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					fratName: suFratName,
					fullName: suFullName.trim(),
					position: suPosition.trim(),
					email: suEmail.trim(),
					phone: normalizeUSPhone(suPhone),
					address: suAddress.trim(),
				}),
			});

			const body = await res.json().catch(() => ({}));

			if (!res.ok) {
				suError = body.error || "Signup failed.";
				suBusy = false;
				return;
			}

			suSuccess =
				"Submitted for verification. You will receive an email when IFC approves your request.";
			signupStep = "done";
		} catch (e) {
			console.error(e);
			suError = "Signup failed.";
		} finally {
			suBusy = false;
		}
	}
	const BUZZ_TZ = "America/Denver";

	// -----------------------------
	// Login
	// -----------------------------
	let loginUsername = "";
	let loginPassword = "";
	let loginError = "";

	let copyStatus = "";
	let copyStatusType = ""; // "success" | "error"

	let events = [];
	let loadingEvents = false;
	let selectedEvent = null;

	let showNewEventModal = false;
	let newEventTitle = "";
	let newEventType = "party";
	let newEventStart = "";
	let newEventEnd = "";
	let newEventError = "";
	let newEventCapacity = "";
	let newEventHasOrganizer = false;
let newEventOrganizerName = ""; // Add this new variable
	let newEventTheme = "";
	let newEventStreetAddress = "";
	let newEventSecurityPlanUrl = "";
	let newEventRegistrantName = "";
	let newEventRegistrantEmail = "";
	let newEventRegistrantPhone = "";
	let newEventRegistrantPickerPhone = ""; // dropdown selection (emergency contact phone)

	let showContactPopover = false;
	let showUserMenu = false;
	let activePage = "dashboard"; // "dashboard" | "account" | "chapters" | "requests"
	let suPhoneConfirm = "";

	// Account page fields
	let accountFratName = "";
	let addressSuggestions = [];
	let addressSearchTimeout;
	let selectedAddress = null;
	let accountAddress = "";
	let accountEmergencyPhones = [""]; // <= up to 8
	let accountSaving = false;
	let accountMessage = "";

	let accountSecurityCompany = "";
	let accountRiskPolicy = "";

	// Neighbor account edit fields
	let neighborEditName = "";
	let neighborEditEmail = "";
	let neighborEditPhone = "";
	let neighborEditAddress = "";
	let neighborEditSaving = false;
	let neighborEditMessage = "";

	let passwordCurrent = "";
	let passwordNew = "";
	let passwordConfirm = "";
	let passwordMessage = "";

	let chapters = [];
	let loadingChapters = false;
	let selectedChapter = null;

	let editingUsername = "";
	let editingName = "";
	let editingAddress = "";
	let editingEmergencyPhones = [""]; // <= up to 8
	let editingEnabled = true;
	let editingCrestKey = "";
	let editingPassword = ""; // optional, blank = keep same
	let chapterMessage = "";
	let chapterSaving = false;
	let creatingNew = false;

	let crestUploadBusy = false;
	let crestUploadError = "";
	let chapterSearch = "";

	let showChapterPassword = false;

	// Admin: pending signup requests
	let signupRequests = [];
	let loadingSignupRequests = false;
	let signupRequestsError = "";
	let eventRequests = [];
	let loadingEventRequests = false;
	let eventRequestsError = "";

	// IFC Admin: click Approve once → shows Confirm? / Cancel
	let confirmApproveEventRequestId = "";
	async function useDemo() {
		loginUsername = "demo";
		loginPassword = "demo";
		await login(); // auto sign-in
	}

	let neighborName = "";
	let neighborPhone = "";
	let neighborAddress = "";
	let neighborSaving = false;
	let neighborMessage = "";

	// Keep local fields synced when user loads/switches
	$: if (currentUser && isNeighbor()) {
		neighborName = currentUser?.name || "";
		neighborPhone = currentUser?.phone || "";
		neighborAddress = currentUser?.address || "";
	}

	// Wire this to your existing backend route for neighbor updates
	async function saveNeighborAccount() {
		neighborSaving = true;
		neighborMessage = "";
		try {
			// TODO: replace path/body with your actual neighbor update endpoint
			const res = await api("/neighbor/account", {
				method: "POST",
				body: JSON.stringify({
					name: neighborName,
					phone: neighborPhone,
					address: neighborAddress,
				}),
			});

			if (!res.ok) throw new Error(await res.text());

			neighborMessage = "Saved.";
			// Optional: refresh currentUser from backend if you have a loader
		} catch (e) {
			neighborMessage = `Save failed: ${e?.message || e}`;
		} finally {
			neighborSaving = false;
		}
	}

	// -----------------------------
	// BuzzCrew (IFC admin) — editable weekend roster + export
	// -----------------------------
	let buzzWeekendLabel = "Weekend of (12/4) - (12/6)";
	let buzzBackupCrew = "Andrew Mosser";

	const BUZZ_STORAGE_KEY = "ifc_eventhub_buzzcrew_v1";
	const DOW_NAMES = [
		"Sunday",
		"Monday",
		"Tuesday",
		"Wednesday",
		"Thursday",
		"Friday",
		"Saturday",
	];

	const BUZZ_DOW_MAP = {
		Sun: 0,
		Mon: 1,
		Tue: 2,
		Wed: 3,
		Thu: 4,
		Fri: 5,
		Sat: 6,
	};

	function denverYMD(d) {
		// "YYYY-MM-DD" in Denver
		return new Intl.DateTimeFormat("en-CA", {
			timeZone: BUZZ_TZ,
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
		}).format(d);
	}

	function getDenverDow(d) {
		// Deterministic: compute DOW from the Denver calendar date (never falls back to viewer timezone)
		const ymd = denverYMD(d);
		const [y, m, dd] = ymd.split("-").map(Number);
		return new Date(Date.UTC(y, m - 1, dd)).getUTCDay(); // 0..6
	}

	function addDaysYMD(ymd, days) {
		const [y, m, dd] = ymd.split("-").map(Number);
		const dt = new Date(Date.UTC(y, m - 1, dd));
		dt.setUTCDate(dt.getUTCDate() + days);
		return dt.toISOString().slice(0, 10);
	}

	function mdFromYMD(ymd) {
		const [, m, d] = ymd.split("-");
		return `${Number(m)}/${Number(d)}`;
	}

	function fmtMD(d) {
		// "M/D" in Denver
		return mdFromYMD(denverYMD(d));
	}

	function fmtTimeShort(d) {
		// "10:00pm" in Denver
		return new Intl.DateTimeFormat("en-US", {
			timeZone: BUZZ_TZ,
			hour: "numeric",
			minute: "2-digit",
		})
			.format(d)
			.replace(/\s/g, "")
			.toLowerCase();
	}

	function getBuzzWeekendWindow() {
		// Denver calendar window: Thu/Fri/Sat. (Return YMD strings so it never depends on viewer timezone.)
		const now = new Date();
		const todayYMD = denverYMD(now);
		const todayDow = getDenverDow(now); // 0..6 in Denver

		let thuYMD;
		if (todayDow >= 4 && todayDow <= 6) {
			// this week's Thu
			thuYMD = addDaysYMD(todayYMD, -(todayDow - 4));
		} else {
			// next Thu
			const daysUntilThu = (4 - todayDow + 7) % 7;
			thuYMD = addDaysYMD(todayYMD, daysUntilThu);
		}

		const satYMD = addDaysYMD(thuYMD, 2);
		return { thuYMD, satYMD };
	}
async function updateComplaintNotes() {
	if (!selectedComplaint) return;

	resolvingComplaint = true;

	try {
		const res = await api(
			`/admin/complaints/${selectedComplaint.id}/update-notes`,
			{
				method: "POST",
				body: JSON.stringify({
					adminNotes: complaintAdminNotes.trim(),
				}),
			},
		);

		if (!res.ok) {
			const body = await res.json().catch(() => ({}));
			alert(body.error || "Failed to update notes");
			resolvingComplaint = false;
			return;
		}

		closeComplaintDetailModal();
		await loadAllComplaints();
	} catch (err) {
		console.error(err);
		alert("Failed to update notes");
	} finally {
		resolvingComplaint = false;
	}
}
	function makeWeekendLabel(thuYMD) {
		const satYMD = addDaysYMD(thuYMD, 2);
		return `Weekend of (${mdFromYMD(thuYMD)}) - (${mdFromYMD(satYMD)})`;
	}

	async function syncBuzzEventsFromBackend() {
		try {
			if (buzzSyncInFlight) return;
			buzzSyncInFlight = true;

			if ((!events || !events.length) && !loadingEvents)
				await loadEvents();

			const { thuYMD, satYMD } = getBuzzWeekendWindow();
			const weekLabel = makeWeekendLabel(thuYMD);

			const parties = (events || []).filter((e) => {
				if (
					!e ||
					String(e.type).toLowerCase() !== "party" ||
					!e.startTime
				)
					return false;

				const start = parseEventInstantDenver(e.startTime);
				if (!start) return false;

				const partyDay = getBuzzPartyCalendarDay(start);

				if (partyDay.dow < 4 || partyDay.dow > 6) return false;
				if (partyDay.ymd < thuYMD || partyDay.ymd > satYMD)
					return false;
				if (String(e.status || "").toLowerCase() === "cancelled")
					return false;

				const chapterLabel = String(
					bestEffortFratNameFromEvent(e) ||
						e.fratName ||
						e.chapterUsername ||
						"",
				).trim();
				if (isTestChapterName(chapterLabel)) return false;

				return true;
			});

			const prevById = new Map(
				(buzzData?.events || []).map((r) => [String(r?.id || ""), r]),
			);

			const editableKeys = [
				"eventType",
				"theme",
				"address",
				"time",
				"oicPosition",
				"oicName",
				"oicPhone",
				"otherOicName",
				"otherOicPhone",
			];

			const isNA = (v) => {
				const s = String(v ?? "").trim();
				return !s || s.toUpperCase() === "N/A";
			};

			const mapped = parties
				.map((e) => {
					const base = buzzRowFromEvent(e);
					if (!base) return null;

					const prev = prevById.get(String(base.id)) || null;
					if (!prev) return base;

					// Keep read-only fields from base, preserve edits for editable fields.
					const merged = { ...base };
					for (const k of editableKeys) {
						if (k in prev) merged[k] = prev[k];
					}

					// If user didn't fill OICs, keep auto-populated values.
					if (isNA(merged.oicPosition))
						merged.oicPosition = base.oicPosition;
					if (isNA(merged.oicName)) merged.oicName = base.oicName;
					if (isNA(merged.oicPhone)) merged.oicPhone = base.oicPhone;
					if (isNA(merged.otherOicName))
						merged.otherOicName = base.otherOicName;
					if (isNA(merged.otherOicPhone))
						merged.otherOicPhone = base.otherOicPhone;

					return merged;
				})
				.filter(Boolean);

			buzzData = {
				...buzzData,
				weekendLabel: weekLabel,
				events: mapped,
			};

			queueBuzzSave();
		} catch (err) {
			console.error("syncBuzzEventsFromBackend error:", err);
		} finally {
			buzzSyncInFlight = false;
		}
	}

	function parseEventInstantDenver(value) {
		if (!value) return null;

		// Firestore timestamp
		if (value && typeof value.toDate === "function") return value.toDate();
		if (typeof value === "object" && value._seconds)
			return new Date(value._seconds * 1000);

		// ISO string - parse as-is (already in UTC)
		return new Date(value);
	}

	function isTestChapterName(name) {
		const s = String(name || "")
			.trim()
			.toLowerCase();
		return s.includes("test");
	}

	function getBuzzPartyCalendarDay(startInstant) {
		if (!startInstant) return { dow: 0, ymd: denverYMD(new Date()) };

		// Get the hour in Denver timezone
		const denverHour = Number(
			new Intl.DateTimeFormat("en-US", {
				timeZone: BUZZ_TZ,
				hour: "2-digit",
				hour12: false,
			}).format(startInstant),
		);

		// Get Denver date
		const ymd = denverYMD(startInstant);
		const dow = getDenverDow(startInstant);

		// If it's early Sunday morning (before 6am), treat as Saturday night
		if (dow === 0 && denverHour < 6) {
			const adjustedDate = new Date(
				startInstant.getTime() - 24 * 3600 * 1000,
			);
			return {
				dow: getDenverDow(adjustedDate),
				ymd: denverYMD(adjustedDate),
			};
		}

		return { dow, ymd };
	}

	function buzzRowFromEvent(ev) {
		const start = parseEventInstantDenver(ev?.startTime);
		const end = parseEventInstantDenver(ev?.endTime);

		if (!start) return null;

		const partyDay = getBuzzPartyCalendarDay(start);
		const day = DOW_NAMES[partyDay.dow];

		const fmtOrNA = (v) =>
			v && String(v).trim() ? String(v).trim() : "N/A";

		const chapterLabel = String(
			bestEffortFratNameFromEvent(ev) || "",
		).trim();
		const contactsRaw = bestEffortChapterContactsForBuzzEvent(
			ev,
			chapterLabel,
		);
		const [o1, o2] = pickTopTwoOICContacts(contactsRaw);

		const otherDisp = o2
			? [o2.position, o2.fullName].filter(Boolean).join(" — ")
			: "";

		return {
			id: ev.id || crypto.randomUUID(),
			date: fmtOrNA(mdFromYMD(partyDay.ymd)),
			day: fmtOrNA(day),
			chapter: fmtOrNA(chapterLabel),
			eventType: fmtOrNA(ev.type || "Party"),
			theme: fmtOrNA(ev.title),
			address: fmtOrNA(bestEffortFratAddressFromEvent(ev)),
			time:
				start && end
					? `${fmtTimeShort(start)} - ${fmtTimeShort(end)}`
					: "N/A",

			// Auto-populate from chapter exec contacts (falls back to contacts-sheet index)
			oicPosition: fmtOrNA(o1?.position),
			oicName: fmtOrNA(o1?.fullName),
			oicPhone: fmtOrNA(o1?.phone),
			otherOicName: fmtOrNA(otherDisp),
			otherOicPhone: fmtOrNA(o2?.phone),
		};
	}

	//end new

	function seedBuzzData() {
		return {
			weekendLabel: buzzWeekendLabel,
			backupCrew: buzzBackupCrew,

			events: [],

			ifcContacts: [
				{
					title: "IFC President",
					name: "Rob Gosser",
					email: "president@coloradoifc.org",
					phone: "(847) 863-0940",
				},
				{
					title: "Vice President of External Affairs",
					name: "TJ Sannazzaro",
					email: "externalaffairs@coloradoifc.org",
					phone: "(978) 987-3658",
				},
				{
					title: "Vice President of Internal Affairs",
					name: "Joe Padilla",
					email: "internalaffairs@coloradoifc.org",
					phone: "(720) 737-3180",
				},
				{
					title: "Vice Presdident of Finance and Operations",
					name: "Harrison Minor",
					email: "finance@coloradoifc.org",
					phone: "(719) 371-3230",
				},
				{
					title: "Chief Justice",
					name: "Andrew Mosser",
					email: "justice@coloradoifc.org",
					phone: "(512) 294-0470",
				},
				{
					title: "Vice President of Philanthropy",
					name: "David Yaccino",
					email: "ifcphilanthropy@coloradoifc.org",
					phone: "(949) 910-3259",
				},
				{
					title: "Vice President of Communications",
					name: "Jude Marino",
					email: "communications@coloradoifc.org",
					phone: "(720) 737-7546",
				},
				{
					title: "Director of Technology",
					name: "JP Warner",
					email: "tech@coloradoifc.org",
					phone: "(310) 750-7768",
				},
				{
					title: "Director of Recruitment",
					name: "Brighton Navarez",
					email: "recruitment@coloradoifc.org",
					phone: "(303) 317-7178",
				},
				{
					title: "Director of Academics",
					name: "Adrian Robinson",
					email: "academics@coloradoifc.org",
					phone: "(973) 307-5447",
				},
				{
					title: "Director of Athletics and Sportsmanship",
					name: "Robert Hurley",
					email: "sportmanship@coloradoifc.org",
					phone: "(508) 736-7602",
				},
				{
					title: "Director of Health and Wellness",
					name: "Michael Geiger",
					email: "mentalhealth@coloradoifc.org",
					phone: "(614) 203-4362",
				},
			],

			resources: [
				{
					title: "Greek Advocate",
					name: "Mike Smith",
					email: "greekadvocate@gmail.com",
					phone: "(303) 489-6584",
				},
				{
					title: "Police Officer Stephens",
					name: "",
					email: "Police-NIT@bouldercolorado.gov",
					phone: "N/A",
				},
				{
					title: "CU Party Registration",
					name: "Jeff Morris",
					email: "partyreg@colorado.edu",
					phone: "N/A",
				},
			],

			// “House info + president contact” (kept editable; can be expanded any time)
			houses: [
				{
					chapter: "Alpha Epsilon Pi",
					greekLetters: "ΑΕΠ",
					address: "1012 14th St",
					presidentName: "Ari Brodsky",
					presidentEmail: "arbr7747@colorado.edu",
					presidentPhone: "(720) 646-5477",
					liaisons: "Myers",
				},
				{
					chapter: "Alpha Kappa Lambda",
					greekLetters: "ΑΚΛ",
					address: "1164 10th St",
					presidentName: "Camden Smith",
					presidentEmail: "casm5944@colorado.edu",
					presidentPhone: "(561) 866-6803",
					liaisons: "Stephens",
				},
				{
					chapter: "Alpha Sigma Phi",
					greekLetters: "ΑΣΦ",
					address: "1019 14th St",
					presidentName: "Hayden Nordstrom",
					presidentEmail: "hnordstromalphasig@gmail.com",
					presidentPhone: "(720) 582-2749",
					liaisons: "Mulhall",
				},
				{
					chapter: "Alpha Tau Omega",
					greekLetters: "ΑΤΩ",
					address: "1068 13th St",
					presidentName: "Aneesh Palagati",
					presidentEmail: "aneeshpalagati@gmail.com",
					presidentPhone: "(303) 264-7793",
					liaisons: "Myers",
				},
				{
					chapter: "Chi Psi",
					greekLetters: "ΧΨ",
					address: "1080 14th St",
					presidentName: "Ethan Currie",
					presidentEmail: "ethancurrie@gmail.com",
					presidentPhone: "(408) 896-7352",
					liaisons: "Mulhall",
				},
				{
					chapter: "Delta Kappa Epsilon",
					greekLetters: "ΔΚE",
					address: "972 14th St",
					presidentName: "Devin Schupp",
					presidentEmail: "desc7587@colorado.edu",
					presidentPhone: "(724) 996-2200",
					liaisons: "Diaz",
				},
				{
					chapter: "Delta Sigma Phi",
					greekLetters: "ΔΣΦ",
					address: "965 Pleasant St",
					presidentName: "Devin Severance",
					presidentEmail: "devinseverance@gmail.com",
					presidentPhone: "(651) 336-2511",
					liaisons: "Diaz",
				},
				{
					chapter: "Kappa Alpha Order",
					greekLetters: "ΚΑ",
					address: "1100 Pennsylvania Ave",
					presidentName: "Griffyn Weber",
					presidentEmail: "griffyn.weber@gmail.com",
					presidentPhone: "(303) 704-5601",
					liaisons: "Myers",
				},
				{
					chapter: "Pi Kappa Alpha",
					greekLetters: "ΠΚΑ",
					address: "1010 12th St",
					presidentName: "Sebastistan Edwards",
					presidentEmail: "seed6775@colorado.edu",
					presidentPhone: "(303) 919-8997",
					liaisons: "Mulhall",
				},
				{
					chapter: "Pi Kappa Phi",
					greekLetters: "ΠΚΦ",
					address: "1441 Broadway St",
					presidentName: "Pierce Bowlin",
					presidentEmail: "pierce.bowlin2004@gmail.com",
					presidentPhone: "(225) 954-4624",
					liaisons: "Stephens",
				},
				{
					chapter: "Phi Gamma Delta",
					greekLetters: "ΦΓΔ",
					address: "1114 12th St",
					presidentName: "Keaton Smith",
					presidentEmail: "kesm2653@colorado.edu",
					presidentPhone: "(970) 485-0885",
					liaisons: "Stephens",
				},
				{
					chapter: "Phi Kappa Psi",
					greekLetters: "ΦΚΨ",
					address: "1131 University Ave",
					presidentName: "Joey Whitcombe",
					presidentEmail: "jowh9010@colorado.edu",
					presidentPhone: "(862) 213-2706",
					liaisons: "Diaz",
				},
				{
					chapter: "Sigma Alpha Epsilon",
					greekLetters: "ΣΑΕ",
					address: "1005 Broadway St",
					presidentName: "Noah Morris",
					presidentEmail: "noahmorris5@icloud.com",
					presidentPhone: "(949) 430-9748",
					liaisons: "Myers",
				},
				{
					chapter: "Sigma Nu",
					greekLetters: "ΣΝ",
					address: "1043 Pleasant St",
					presidentName: "Max Hughes",
					presidentEmail: "hughesmax10@gmail.com",
					presidentPhone: "(206) 962-7821",
					liaisons: "Diaz",
				},
				{
					chapter: "Tau Kappa Epsilon",
					greekLetters: "ΤΚΕ",
					address: "1127 Broadway St",
					presidentName: "Jake Siemsen",
					presidentEmail: "jakesiemsen@gmail.com",
					presidentPhone: "(443) 686-0990",
					liaisons: "Stephens",
				},
				{
					chapter: "Theta Chi",
					greekLetters: "ΘΧ",
					address: "1044 13th St",
					presidentName: "Will Schultz",
					presidentEmail: "schultzwill105@gmail.com",
					presidentPhone: "(630) 376-8224",
					liaisons: "Mulhall",
				},
				{
					chapter: "Theta Xi",
					greekLetters: "ΘΞ",
					address: "1146 12th St",
					presidentName: "Adrien Burley",
					presidentEmail: "adbu1167@colorado.edu",
					presidentPhone: "(865) 801-1293",
					liaisons: "Mulhall",
				},
				{
					chapter: "Zeta Beta Tau",
					greekLetters: "ZBT",
					address: "911 17th St",
					presidentName: "Bob Kilburg",
					presidentEmail: "roki9610@colorado.edu",
					presidentPhone: "(708) 427-8100",
					liaisons: "Myers",
				},
			],
		};
	}

	let buzzData = seedBuzzData();
	let buzzSyncInFlight = false;
	let buzzSearch = "";
	let buzzSelectedEventId = "";
	let showBuzzEventModal = false;
	let buzzEditEvent = null;

	function saveBuzzToStorage() {
		try {
			localStorage.setItem(BUZZ_STORAGE_KEY, JSON.stringify(buzzData));
		} catch (e) {
			// ignore
		}
	}
	let buzzSaving = false;
	let buzzSaveError = "";
	let buzzSaveTimer = null;

	function isoDate(d) {
		const yyyy = d.getFullYear();
		const mm = String(d.getMonth() + 1).padStart(2, "0");
		const dd = String(d.getDate()).padStart(2, "0");
		return `${yyyy}-${mm}-${dd}`;
	}

	// Key is the Thursday date for the BuzzCrew window: "YYYY-MM-DD"
	function getBuzzWeekKey() {
		const { thuYMD } = getBuzzWeekendWindow();
		return thuYMD;
	}

	function toTitleRow(x) {
		return {
			title: String(x?.title ?? x?.position ?? ""),
			name: String(x?.name ?? x?.fullName ?? ""),
			email: String(x?.email ?? ""),
			phone: String(x?.phone ?? ""),
		};
	}

	function findPresidentContact(contacts) {
		const list = Array.isArray(contacts) ? contacts : [];
		return (
			list.find((c) =>
				String(c?.position || "")
					.toLowerCase()
					.includes("president"),
			) ||
			list.find(
				(c) => String(c?.position || "").toLowerCase() === "pres",
			) ||
			null
		);
	}

	function resolveBuzzBackupCrewFromIFCContacts(ifcContacts) {
		const list = Array.isArray(ifcContacts) ? ifcContacts : [];
		const norm = (s) =>
			String(s || "")
				.trim()
				.toLowerCase();

		// Prefer Chief Justice (your "backup crew")
		const cj =
			list.find((c) =>
				norm(c?.title || c?.position).includes("chief justice"),
			) ||
			list.find((c) => norm(c?.title || c?.position) === "chief justice");

		if (cj && (cj.name || cj.fullName))
			return String(cj.name || cj.fullName).trim();

		// Fallbacks if you ever rename the role
		const backup =
			list.find((c) =>
				norm(c?.title || c?.position).includes("backup"),
			) ||
			list.find((c) => norm(c?.title || c?.position).includes("justice"));

		if (backup && (backup.name || backup.fullName))
			return String(backup.name || backup.fullName).trim();

		return "";
	}

	async function loadContactsSheetIntoBuzzData() {
		try {
			const res = await api("/contacts-sheet", { method: "GET" });
			if (!res.ok) return;

			const data = await res.json().catch(() => ({}));
			const ifc = data?.ifc || {};
			const chaptersFromSheet = Array.isArray(data?.chapters)
				? data.chapters
				: [];

			// Preserve greekLetters + liaisons from current buzzData.houses, keyed by chapter name
			const prevHousesByKey = new Map(
				(buzzData?.houses || []).map((h) => [
					String(h?.chapter || "")
						.trim()
						.toLowerCase(),
					h,
				]),
			);

			// IFC + resources (support either {contacts/resources} or old {ifcContacts/resources})
			const ifcContactsIncoming = Array.isArray(ifc?.contacts)
				? ifc.contacts.map(toTitleRow)
				: Array.isArray(ifc?.ifcContacts)
					? ifc.ifcContacts.map(toTitleRow)
					: [];

			const resourcesIncoming = Array.isArray(ifc?.resources)
				? ifc.resources.map(toTitleRow)
				: Array.isArray(data?.resources)
					? data.resources.map(toTitleRow)
					: [];

			// Default to hardcoded seed until backend provides real data
			const ifcContacts = ifcContactsIncoming.length
				? ifcContactsIncoming
				: buzzData?.ifcContacts || [];
			const resources = resourcesIncoming.length
				? resourcesIncoming
				: buzzData?.resources || [];

			// Chapters -> houses (chapter name/address + president derived from chapter contacts)
			// Build chapter->contacts index for BuzzCrew OIC autofill
			setBuzzChapterContactsIndex(chaptersFromSheet);

			// Chapters -> houses (chapter name/address + president derived from chapter contacts)
			const houses = chaptersFromSheet
				.filter((ch) => {
					const chapterName = String(
						ch?.chapterName ||
							ch?.chapterUsername ||
							ch?.fratName ||
							ch?.name ||
							"",
					);
					return !isTestChapterName(chapterName);
				})
				.map((ch) => {
					const chapterName = String(
						ch?.chapterName || ch?.chapterUsername || "",
					);
					const key = chapterName.trim().toLowerCase();
					const prev = prevHousesByKey.get(key) || {};

					const pres = findPresidentContact(ch?.contacts) || {};

					return {
						chapter: chapterName,
						greekLetters: canonicalGreekLetters(
							chapterName,
							ch?.greekLetters || prev?.greekLetters || "",
						),
						address: String(ch?.fratAddress || prev?.address || ""),
						presidentName: String(
							pres?.fullName || prev?.presidentName || "",
						),
						presidentEmail: String(
							pres?.email || prev?.presidentEmail || "",
						),
						presidentPhone: String(
							pres?.phone || prev?.presidentPhone || "",
						),
						liaisons: String(prev?.liaisons || ""),
					};
				});
			const backupCrewName =
				resolveBuzzBackupCrewFromIFCContacts(ifcContacts);

			buzzData = {
				...buzzData,
				ifcContacts,
				resources,
				houses: houses.length ? houses : buzzData?.houses || [],

				// Always pull from contacts sheet if present
				backupCrew: backupCrewName || buzzData?.backupCrew || "",
			};

			// Populate missing OIC fields for display (does NOT save)
			autofillBuzzOICsFromChapterContacts();
		} catch (e) {
			console.error("loadContactsSheetIntoBuzzData:", e);
		}
	}

	async function loadBuzzFromBackend() {
		buzzSaveError = "";
		try {
			const weekKey = getBuzzWeekKey();

			// 1) Load weekend-specific sheet (events + label + backup)
			const res = await api(
				`/buzzcrew/sheet?weekKey=${encodeURIComponent(weekKey)}`,
				{ method: "GET" },
			);
			if (res.ok) {
				const data = await res.json().catch(() => ({}));
				const sheet =
					data?.sheet && typeof data.sheet === "object"
						? data.sheet
						: null;

				if (sheet) {
					buzzData = {
						...buzzData,
						weekendLabel: String(
							sheet.weekendLabel || buzzData.weekendLabel || "",
						),
						backupCrew: String(
							sheet.backupCrew || buzzData.backupCrew || "",
						),
						events: Array.isArray(sheet.events)
							? sheet.events
							: buzzData.events || [],
						// NOTE: do NOT trust sheet contacts as source-of-truth; contacts come from contactsSheet below
					};
				}
			}

			// 2) Load contacts from unified contacts sheet (source-of-truth)
			await loadContactsSheetIntoBuzzData();
		} catch (e) {
			console.error("loadBuzzFromBackend:", e);
		}
	}

	async function saveBuzzToBackendNow() {
		if (!isIFCAdmin()) return;
		buzzSaving = true;
		buzzSaveError = "";
		try {
			const weekKey = getBuzzWeekKey();
			const res = await api("/buzzcrew/sheet", {
				method: "PUT",
				body: JSON.stringify({ weekKey, sheet: buzzData }),
			});
			if (!res.ok) {
				const body = await res.json().catch(() => ({}));
				buzzSaveError = body?.error || "Failed to save BuzzCrew sheet.";
			}
		} catch (e) {
			console.error("saveBuzzToBackendNow:", e);
			buzzSaveError = "Failed to save BuzzCrew sheet.";
		} finally {
			buzzSaving = false;
		}
	}

	// Debounced autosave (call this on each input)
	function queueBuzzSave() {
		if (buzzSaveTimer) clearTimeout(buzzSaveTimer);
		buzzSaveTimer = setTimeout(() => {
			saveBuzzToBackendNow();
		}, 2000);
	}

	async function goBuzzCrew() {
		activePage = "buzzcrew";
		showUserMenu = false;

		// load weekend sheet + contacts sheet
		await loadBuzzFromBackend();

		// re-sync Thu/Fri/Sat parties, preserving edits
		await syncBuzzEventsFromBackend();

		// re-pull contacts after sync (keeps houses/IFC fresh if backend changed)
		await loadContactsSheetIntoBuzzData();
	}

	function buzzScheduleRows() {
		const list = Array.isArray(buzzData?.events)
			? buzzData.events.filter((e) => !!e && !!e.date)
			: [];

		// Sort (this doesn't mutate if we create a copy first)
		const sorted = [...list].sort((a, b) => {
			const [am, ad] = (a.date || "0/0").split("/").map(Number);
			const [bm, bd] = (b.date || "0/0").split("/").map(Number);
			if (am !== bm) return am - bm;
			if (ad !== bd) return ad - bd;
			return (a.time || "").localeCompare(b.time || "");
		});

		const out = [];
		let lastKey = "";
		for (const ev of sorted) {
			const k = `${ev.date} ${ev.day}`;
			if (k !== lastKey) {
				out.push({ kind: "day", key: `day-${k}`, label: k });
				lastKey = k;
			}
			out.push({ kind: "row", key: ev.id, ev });
		}

		return out;
	}

	function groupedBuzzEvents() {
		const q = buzzSearch.trim().toLowerCase();
		const list = (buzzData?.events || []).filter((ev) => {
			if (!q) return true;
			const hay =
				`${ev.date} ${ev.day} ${ev.chapter} ${ev.eventType} ${ev.theme} ${ev.address} ${ev.time}`.toLowerCase();
			return hay.includes(q);
		});

		const groups = {};
		for (const ev of list) {
			const k = `${ev.date} ${ev.day}`;
			if (!groups[k]) groups[k] = [];
			groups[k].push(ev);
		}
		return Object.entries(groups).map(([k, items]) => ({ key: k, items }));
	}

	function openBuzzEvent(ev) {
		if (!ev) return;
		buzzSelectedEventId = ev.id;
		buzzEditEvent = JSON.parse(JSON.stringify(ev));
		showBuzzEventModal = true;
		if (typeof document !== "undefined")
			document.body.classList.add("modal-open");
	}

	function closeBuzzEventModal() {
		showBuzzEventModal = false;
		buzzEditEvent = null;
		if (typeof document !== "undefined")
			document.body.classList.remove("modal-open");
	}

	function saveBuzzEventEdits() {
		if (!buzzEditEvent) return;
		const idx = (buzzData.events || []).findIndex(
			(x) => x.id === buzzEditEvent.id,
		);
		if (idx >= 0) {
			buzzData.events[idx] = { ...buzzEditEvent };
			buzzData = { ...buzzData, events: [...buzzData.events] };
			queueBuzzSave();
		}
		closeBuzzEventModal();
	}
	async function exportBuzzCrewSpreadsheet() {
		let XLSX;
		try {
			XLSX = await import("xlsx");
		} catch (e) {
			alert('Missing dependency "xlsx". Run: npm i xlsx');
			return;
		}

		const wb = XLSX.utils.book_new();

		const MAX_COLS = 12;
		const padRow = (arr) => {
			const out = Array.isArray(arr) ? [...arr] : [];
			while (out.length < MAX_COLS) out.push("");
			return out.slice(0, MAX_COLS);
		};

		const merges = [];
		const mergeFullRow = (r) =>
			merges.push({ s: { r, c: 0 }, e: { r, c: MAX_COLS - 1 } });

		const title = String(buzzData?.weekendLabel || "Buzz Crew").trim();
		const backup = String(buzzData?.backupCrew || "").trim();

		const scheduleHeader = [
			"Date",
			"Day",
			"Chapter",
			"Event Type",
			"Theme",
			"Address",
			"Time",
			"OIC Position",
			"OIC Name",
			"OIC Phone #",
			"Other OIC",
			"Other OIC #",
		];

		const aoa = [];

		// Title row
		const rTitle = aoa.length;
		aoa.push(
			padRow([`${title}${backup ? `    Backup Crew: ${backup}` : ""}`]),
		);
		mergeFullRow(rTitle);

		aoa.push(padRow([])); // spacer

		// Schedule section
		const rSchedTitle = aoa.length;
		aoa.push(padRow(["Positions and Contact Information for Buzz Crew"]));
		mergeFullRow(rSchedTitle);

		aoa.push(padRow(scheduleHeader));

		for (const ev of buzzData?.events || []) {
			aoa.push(
				padRow([
					ev?.date || "",
					ev?.day || "",
					ev?.chapter || "",
					ev?.eventType || "",
					ev?.theme || "",
					ev?.address || "",
					ev?.time || "",
					ev?.oicPosition || "",
					ev?.oicName || "",
					formatPhoneForDisplay(ev?.oicPhone || ""),
					ev?.otherOicName || "",
					formatPhoneForDisplay(ev?.otherOicPhone || ""),
				]),
			);
		}

		aoa.push(padRow([])); // spacer

		// IFC Contacts
		const rIfcTitle = aoa.length;
		aoa.push(padRow(["IFC Contacts"]));
		mergeFullRow(rIfcTitle);

		aoa.push(padRow(["Title", "Name", "Email", "Phone #"]));
		for (const c of buzzData?.ifcContacts || []) {
			aoa.push(
				padRow([
					c?.title || "",
					c?.name || "",
					c?.email || "",
					formatPhoneForDisplay(c?.phone || ""),
				]),
			);
		}

		aoa.push(padRow([])); // spacer

		// Resources
		const rResTitle = aoa.length;
		aoa.push(padRow(["Necessary Resources Info"]));
		mergeFullRow(rResTitle);

		aoa.push(padRow(["Title", "Name", "Email", "Phone #"]));
		for (const r of buzzData?.resources || []) {
			aoa.push(
				padRow([
					r?.title || "",
					r?.name || "",
					r?.email || "",
					formatPhoneForDisplay(r?.phone || ""),
				]),
			);
		}

		aoa.push(padRow([])); // spacer

		// Houses
		const rHouseTitle = aoa.length;
		aoa.push(
			padRow([
				"FRATERNITY HOUSE INFORMATION + PRESIDENT CONTACT INFORMATION",
			]),
		);
		mergeFullRow(rHouseTitle);

		aoa.push(
			padRow([
				"Fraternity",
				"Greek Letters",
				"Fraternity Address",
				"President Name",
				"Email",
				"Phone #",
				"Liaisons",
			]),
		);

		for (const h of buzzData?.houses || []) {
			aoa.push(
				padRow([
					h?.chapter || "",
					h?.greekLetters || "",
					h?.address || "",
					h?.presidentName || "",
					h?.presidentEmail || "",
					formatPhoneForDisplay(h?.presidentPhone || ""),
					h?.liaisons || "",
				]),
			);
		}

		aoa.push(padRow([])); // spacer

		const rNote1 = aoa.length;
		aoa.push(
			padRow([
				"*In the case of police or other emergency personnel arriving at an event, both the IFC President and Greek Advocate should be IMMEDIATELY contacted by the Chapter President",
			]),
		);
		mergeFullRow(rNote1);

		const rNote2 = aoa.length;
		aoa.push(
			padRow([
				"*Note: Other fraternities on the hill such as Kappa Sigma, Sigma Pi and Phi Kappa Tau are not affiliated with the Undergraduate Interfraternity Council at the University of Colorado, Inc.",
			]),
		);
		mergeFullRow(rNote2);

		const ws = XLSX.utils.aoa_to_sheet(aoa);
		ws["!merges"] = merges;

		// Reasonable column widths for the schedule header (12 cols)
		ws["!cols"] = [
			{ wch: 6 }, // Date
			{ wch: 10 }, // Day
			{ wch: 18 }, // Chapter
			{ wch: 12 }, // Event Type
			{ wch: 26 }, // Theme
			{ wch: 30 }, // Address
			{ wch: 14 }, // Time
			{ wch: 16 }, // OIC Position
			{ wch: 18 }, // OIC Name
			{ wch: 14 }, // OIC Phone
			{ wch: 22 }, // Other OIC
			{ wch: 14 }, // Other OIC Phone
		];

		XLSX.utils.book_append_sheet(wb, ws, "Buzz Crew");

		const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
		const blob = new Blob([out], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		});

		const safe = title.replace(/[^\w\-()]+/g, "_");
		const a = document.createElement("a");
		a.href = URL.createObjectURL(blob);
		a.download = `Buzz_Crew_${safe}.xlsx`;
		document.body.appendChild(a);
		a.click();
		a.remove();
		URL.revokeObjectURL(a.href);
	}
	// Add after the existing state variables (around line 100-200)

	// Neighbor state
	let neighborEmail = "";
	let neighborPassword = "";
	let showNeighborSignup = false;
	let neighborSignupData = {
		name: "",
		email: "",
		phone: "",
		address: "",
		password: "",
		confirmPassword: "",
	};
	let neighborSignupError = "";
	let neighborSignupBusy = false;

	// Neighbor dashboard
	let nearbyChapters = [];
	let loadingNearbyChapters = false;
	let showComplaintModal = false;
	let complaintText = "";
	let complaintError = "";
	let complaintBusy = false;
	let neighborComplaints = [];
	let loadingNeighborComplaints = false;

	// Admin complaints
	let allComplaints = [];
	let loadingComplaints = false;
	let selectedComplaint = null;
	let showComplaintDetailModal = false;
	let complaintAdminNotes = "";
	let resolvingComplaint = false;

	// Admin neighbors
	let allNeighbors = [];
	let loadingNeighbors = false;
	let selectedNeighbor = null;
	let showNeighborProfileModal = false;
	let neighborProfileComplaints = [];

	function isNeighbor() {
		return currentUser && currentUser.role === "neighbor";
	}

	// Neighbor signup modal functions
	function openNeighborSignupModal() {
		showNeighborSignup = true;
		neighborSignupData = {
			name: "",
			email: "",
			phone: "",
			address: "",
			password: "",
			confirmPassword: "",
		};
		neighborSignupError = "";
		if (typeof document !== "undefined")
			document.body.classList.add("modal-open");
	}

	function closeNeighborSignupModal() {
		showNeighborSignup = false;
		if (typeof document !== "undefined")
			document.body.classList.remove("modal-open");
	}

	async function submitNeighborSignup() {
		neighborSignupError = "";

		const { name, email, phone, address, password, confirmPassword } =
			neighborSignupData;

		if (
			!name.trim() ||
			!email.trim() ||
			!phone.trim() ||
			!address.trim() ||
			!password
		) {
			neighborSignupError = "All fields are required";
			return;
		}

		if (password !== confirmPassword) {
			neighborSignupError = "Passwords do not match";
			return;
		}

		if (password.length < 6) {
			neighborSignupError = "Password must be at least 6 characters";
			return;
		}

		neighborSignupBusy = true;

		try {
			const res = await fetch(`${backendURL}/signup/neighbor`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					name: name.trim(),
					email: email.trim(),
					phone: phone.trim(),
					address: address.trim(),
					password,
				}),
			});

			const body = await res.json().catch(() => ({}));

			if (!res.ok) {
				neighborSignupError = body.error || "Signup failed";
				neighborSignupBusy = false;
				return;
			}

			// Auto-login
			neighborEmail = email.trim();
			neighborPassword = password;
			closeNeighborSignupModal();
			await loginNeighbor();
		} catch (e) {
			console.error(e);
			neighborSignupError = "Signup failed";
		} finally {
			neighborSignupBusy = false;
		}
	}

	async function loginNeighbor() {
		loginError = "";

		try {
			const res = await fetch(`${backendURL}/login`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					username: neighborEmail.trim(),
					password: neighborPassword,
				}),
			});

			if (!res.ok) {
				loginError = "Invalid email or password";
				return;
			}

			const data = await res.json();
			token = data.token;
			currentUser = data.user;
			activePage = "dashboard";

			await loadNearbyChapters();
			await loadNeighborComplaints();
		} catch (err) {
			console.error(err);
			loginError = "Login failed";
		}
	}

	// Neighbor dashboard functions
	async function loadNearbyChapters() {
		if (!token || !isNeighbor()) return;

		loadingNearbyChapters = true;

		try {
			const res = await api("/neighbors/nearby-chapters", {
				method: "GET",
			});
			if (!res.ok) {
				console.error("Failed to load nearby chapters");
				loadingNearbyChapters = false;
				return;
			}

			const data = await res.json();
			nearbyChapters = data.chapters || [];
		} catch (err) {
			console.error(err);
		} finally {
			loadingNearbyChapters = false;
		}
	}

	async function loadNeighborComplaints() {
		if (!token || !isNeighbor()) return;

		loadingNeighborComplaints = true;

		try {
			const res = await api("/neighbors/complaints", { method: "GET" });
			if (!res.ok) {
				console.error("Failed to load complaints");
				loadingNeighborComplaints = false;
				return;
			}

			const data = await res.json();
			neighborComplaints = data.complaints || [];
		} catch (err) {
			console.error(err);
		} finally {
			loadingNeighborComplaints = false;
		}
	}

	function openComplaintModal(chapter) {
		selectedChapter = chapter;
		complaintText = "";
		complaintError = "";
		showComplaintModal = true;
		if (typeof document !== "undefined")
			document.body.classList.add("modal-open");
	}

	function closeComplaintModal() {
		showComplaintModal = false;
		selectedChapter = null;
		if (typeof document !== "undefined")
			document.body.classList.remove("modal-open");
	}

	async function submitComplaint() {
		complaintError = "";

		const wordCount = complaintText
			.trim()
			.split(/\s+/)
			.filter((w) => w.length > 0).length;

		if (wordCount < 10) {
			complaintError = `Complaint must be at least 10 words (currently ${wordCount})`;
			return;
		}

		complaintBusy = true;

		try {
			const res = await api("/neighbors/complaints", {
				method: "POST",
				body: JSON.stringify({
					chapterUsername: selectedChapter.username,
					complaintText: complaintText.trim(),
				}),
			});

			const body = await res.json().catch(() => ({}));

			if (!res.ok) {
				complaintError = body.error || "Failed to submit complaint";
				complaintBusy = false;
				return;
			}

			closeComplaintModal();
			await loadNearbyChapters();
			await loadNeighborComplaints();
		} catch (e) {
			console.error(e);
			complaintError = "Failed to submit complaint";
		} finally {
			complaintBusy = false;
		}
	}

	// Chapter complaints
	let chapterComplaints = [];
	let loadingChapterComplaints = false;

	async function loadChapterComplaints() {
		if (!token || !isChapter()) return;

		loadingChapterComplaints = true;

		try {
			const res = await api("/chapters/complaints", { method: "GET" });
			if (!res.ok) {
				console.error("Failed to load chapter complaints");
				loadingChapterComplaints = false;
				return;
			}

			const data = await res.json();
			chapterComplaints = data.complaints || [];
		} catch (err) {
			console.error(err);
		} finally {
			loadingChapterComplaints = false;
		}
	}

	function goChapterComplaints() {
		activePage = "chapter-complaints";
		loadChapterComplaints();
	}

	// Admin complaints
	async function loadAllComplaints() {
		if (!token || !isIFCAdmin()) return;

		loadingComplaints = true;

		try {
			const res = await api("/admin/complaints", { method: "GET" });
			if (!res.ok) {
				console.error("Failed to load complaints");
				loadingComplaints = false;
				return;
			}

			const data = await res.json();
			allComplaints = data.complaints || [];
		} catch (err) {
			console.error(err);
		} finally {
			loadingComplaints = false;
		}
	}

	function openComplaintDetail(complaint) {
		selectedComplaint = complaint;
		complaintAdminNotes = complaint.adminNotes || "";
		showComplaintDetailModal = true;
		if (typeof document !== "undefined")
			document.body.classList.add("modal-open");
	}

	function closeComplaintDetailModal() {
		showComplaintDetailModal = false;
		selectedComplaint = null;
		if (typeof document !== "undefined")
			document.body.classList.remove("modal-open");
	}

	async function resolveComplaint() {
		if (!selectedComplaint) return;

		resolvingComplaint = true;

		try {
			const res = await api(
				`/admin/complaints/${selectedComplaint.id}/resolve`,
				{
					method: "POST",
					body: JSON.stringify({
						adminNotes: complaintAdminNotes.trim(),
					}),
				},
			);

			if (!res.ok) {
				const body = await res.json().catch(() => ({}));
				alert(body.error || "Failed to resolve complaint");
				resolvingComplaint = false;
				return;
			}

			closeComplaintDetailModal();
			await loadAllComplaints();
		} catch (err) {
			console.error(err);
			alert("Failed to resolve complaint");
		} finally {
			resolvingComplaint = false;
		}
	}

	function goAdminComplaints() {
		activePage = "admin-complaints";
		loadAllComplaints();
	}

	// Admin neighbors
	async function loadAllNeighbors() {
		if (!token || !isIFCAdmin()) return;

		loadingNeighbors = true;

		try {
			const res = await api("/admin/neighbors", { method: "GET" });
			if (!res.ok) {
				console.error("Failed to load neighbors");
				loadingNeighbors = false;
				return;
			}

			const data = await res.json();
			allNeighbors = data.neighbors || [];
		} catch (err) {
			console.error(err);
		} finally {
			loadingNeighbors = false;
		}
	}

	async function openNeighborProfile(neighborId) {
		try {
			const res = await api(`/admin/neighbors/${neighborId}`, {
				method: "GET",
			});
			if (!res.ok) {
				alert("Failed to load neighbor profile");
				return;
			}

			const data = await res.json();
			selectedNeighbor = data.neighbor;
			neighborProfileComplaints = data.complaints || [];
			showNeighborProfileModal = true;
			if (typeof document !== "undefined")
				document.body.classList.add("modal-open");
		} catch (err) {
			console.error(err);
			alert("Failed to load neighbor profile");
		}
	}

	function closeNeighborProfileModal() {
		showNeighborProfileModal = false;
		selectedNeighbor = null;
		neighborProfileComplaints = [];
		if (typeof document !== "undefined")
			document.body.classList.remove("modal-open");
	}

	async function toggleNeighborEnabled(neighborId, currentlyEnabled) {
		try {
			const res = await api(`/admin/neighbors/${neighborId}/toggle`, {
				method: "PUT",
				body: JSON.stringify({ enabled: !currentlyEnabled }),
			});

			if (!res.ok) {
				alert("Failed to update neighbor status");
				return;
			}

			await loadAllNeighbors();

			if (selectedNeighbor && selectedNeighbor.id === neighborId) {
				selectedNeighbor.enabled = !currentlyEnabled;
			}
		} catch (err) {
			console.error(err);
			alert("Failed to update neighbor status");
		}
	}

	function goAdminNeighbors() {
		activePage = "admin-neighbors";
		loadAllNeighbors();
	}
	// Greek letter mapping (add this right after FRAT_SET)
	const GREEK_MAP = {
		"alpha delta phi": "ΑΔΦ",
		"alpha epsilon pi": "ΑΕΠ",
		"alpha gamma omega": "ΑΓΩ",
		"alpha kappa lambda": "ΑΚΛ",
		"alpha phi delta": "ΑΦΔ",
		"alpha sigma phi": "ΑΣΦ",
		"alpha tau omega": "ΑΤΩ",
		"chi psi": "ΧΨ",
		"delta kappa epsilon": "ΔΚΕ",
		"delta sigma phi": "ΔΣΦ",
		"kappa alpha order": "ΚΑ",
		"phi gamma delta": "ΦΓΔ",
		"phi kappa psi": "ΦΚΨ",
		"pi kappa alpha": "ΠΚΑ",
		"pi kappa phi": "ΠΚΦ",
		"sigma alpha epsilon": "ΣΑΕ",
		"sigma nu": "ΣΝ",
		"sigma tau gamma": "ΣΤΓ",
		"tau kappa epsilon": "ΤΚΕ",
		"theta chi": "ΘΧ",
		"theta xi": "ΘΞ",
		"zeta beta tau": "ΖΒΤ",
	};

	function canonicalGreekLetters(chapterName, greekLettersRaw) {
		const key = String(chapterName || "")
			.trim()
			.toLowerCase();
		const canon = GREEK_MAP[key];
		if (canon) return canon;

		// If no match, return empty string (fallback)
		return "";
	}

	function fixGreekLetters(chapterName, corruptedLetters) {
		// First, try direct lookup by chapter name
		if (GREEK_MAP[chapterName]) {
			return GREEK_MAP[chapterName];
		}

		// If already valid Greek letters, return as-is
		if (/[\u0370-\u03FF\u1F00-\u1FFF]/.test(corruptedLetters)) {
			return corruptedLetters;
		}

		// Return the corrupted string if we can't fix it
		return corruptedLetters || "";
	}
	function loadBuzzFromStorage() {
		try {
			const raw = localStorage.getItem(BUZZ_STORAGE_KEY);
			if (!raw) return;

			const parsed = JSON.parse(raw);
			if (parsed && typeof parsed === "object") {
				buzzData = parsed;

				// Fix Greek letters on load
				if (Array.isArray(buzzData?.houses)) {
					buzzData.houses = buzzData.houses
						.filter((h) => !isTestChapterName(h?.chapter))
						.map((h) => ({
							...h,
							greekLetters: canonicalGreekLetters(
								h?.chapter,
								h?.greekLetters,
							),
						}));
				}

				// Persist the repair
				saveBuzzToStorage();
			}
		} catch (e) {
			// ignore
		}
	}

	function hasGreek(s) {
		return /[\u0370-\u03FF\u1F00-\u1FFF]/.test(String(s || ""));
	}

	async function exportBuzzCrewPDF() {
		let jsPDF, autoTable;
		try {
			({ jsPDF } = await import("jspdf"));
			const m = await import("jspdf-autotable");
			autoTable = m.default || m;
		} catch (e) {
			alert(
				'Missing dependency "jspdf" / "jspdf-autotable". Run: npm i jspdf jspdf-autotable',
			);
			return;
		}

		const title = String(buzzData?.weekendLabel || "Buzz Crew").trim();
		const backup = String(buzzData?.backupCrew || "").trim();

		const doc = new jsPDF({
			orientation: "landscape",
			unit: "pt",
			format: "letter",
		});
		await ensureUnicodePdfFont(doc);

		const marginX = 36;
		let y = 40;

		doc.setFont("helvetica", "bold");
		doc.setFontSize(18);
		doc.text(title, marginX, y);

		doc.setFont("helvetica", "normal");
		doc.setFontSize(12);
		if (backup) doc.text(`Backup Crew: ${backup}`, marginX, y + 18);

		y += 38;

		const sectionTitle = (t) => {
			doc.setFont("helvetica", "bold");
			doc.setFontSize(13);
			doc.text(t, marginX, y);
			y += 10;
		};

		// 1) Weekend schedule
		sectionTitle("Positions and Contact Information for Buzz Crew");

		autoTable(doc, {
			startY: y + 10,
			margin: { left: marginX, right: marginX },
			theme: "grid",
			styles: { fontSize: 8, cellPadding: 3, overflow: "linebreak" },
			headStyles: { fontStyle: "bold" },
			head: [
				[
					"Date",
					"Day",
					"Chapter",
					"Event Type",
					"Theme",
					"Address",
					"Time",
					"OIC Position",
					"OIC Name",
					"OIC Phone #",
					"Other OIC",
					"Other OIC #",
				],
			],
			body: (buzzData?.events || []).map((ev) => [
				ev?.date || "",
				ev?.day || "",
				ev?.chapter || "",
				ev?.eventType || "",
				ev?.theme || "",
				ev?.address || "",
				ev?.time || "",
				ev?.oicPosition || "",
				ev?.oicName || "",
				formatPhoneForDisplay(ev?.oicPhone || ""),
				ev?.otherOicName || "",
				formatPhoneForDisplay(ev?.otherOicPhone || ""),
			]),
		});

		y = (doc.lastAutoTable?.finalY || y) + 22;

		// 2) IFC contacts
		sectionTitle("IFC Contacts");
		autoTable(doc, {
			startY: y + 10,
			margin: { left: marginX, right: marginX },
			theme: "grid",
			styles: { fontSize: 9, cellPadding: 4, overflow: "linebreak" },
			headStyles: { fontStyle: "bold" },
			head: [["Title", "Name", "Email", "Phone #"]],
			body: (buzzData?.ifcContacts || []).map((c) => [
				c?.title || "",
				c?.name || "",
				c?.email || "",
				formatPhoneForDisplay(c?.phone || ""),
			]),
		});

		y = (doc.lastAutoTable?.finalY || y) + 22;

		// 3) Resources
		sectionTitle("Necessary Resources Info");
		autoTable(doc, {
			startY: y + 10,
			margin: { left: marginX, right: marginX },
			theme: "grid",
			styles: { fontSize: 9, cellPadding: 4, overflow: "linebreak" },
			headStyles: { fontStyle: "bold" },
			head: [["Title", "Name", "Email", "Phone #"]],
			body: (buzzData?.resources || []).map((r) => [
				r?.title || "",
				r?.name || "",
				r?.email || "",
				formatPhoneForDisplay(r?.phone || ""),
			]),
		});

		y = (doc.lastAutoTable?.finalY || y) + 22;

		// 4) Houses (WITHOUT Greek Letters column)
		sectionTitle(
			"FRATERNITY HOUSE INFORMATION + PRESIDENT CONTACT INFORMATION",
		);
		autoTable(doc, {
			startY: y + 10,
			margin: { left: marginX, right: marginX },
			theme: "grid",
			styles: { fontSize: 9, cellPadding: 4, overflow: "linebreak" },
			headStyles: { fontStyle: "bold" },
			head: [
				[
					"Fraternity",
					"Fraternity Address",
					"President Name",
					"Email",
					"Phone #",
					"Liaisons",
				],
			],
			body: (buzzData?.houses || []).map((h) => [
				h?.chapter || "",
				h?.address || "",
				h?.presidentName || "",
				h?.presidentEmail || "",
				formatPhoneForDisplay(h?.presidentPhone || ""),
				h?.liaisons || "",
			]),
		});

		y = (doc.lastAutoTable?.finalY || y) + 18;

		doc.setFont("helvetica", "normal");
		doc.setFontSize(9);
		doc.text(
			"*In the case of police or other emergency personnel arriving at an event, both the IFC President and Greek Advocate should be IMMEDIATELY contacted by the Chapter President",
			marginX,
			y,
			{ maxWidth: doc.internal.pageSize.getWidth() - marginX * 2 },
		);
		y += 14;
		doc.text(
			"*Note: Other fraternities on the hill such as Kappa Sigma, Sigma Pi and Phi Kappa Tau are not affiliated with the Undergraduate Interfraternity Council at the University of Colorado, Inc.",
			marginX,
			y,
			{ maxWidth: doc.internal.pageSize.getWidth() - marginX * 2 },
		);

		const safe = title.replace(/[^\w\-()]+/g, "_");
		doc.save(`Buzz_Crew_${safe}.pdf`);
	}

	function isIFCAdmin() {
		return currentUser && currentUser.role === "ifc_admin";
	}

	function isChapter() {
		return currentUser && currentUser.role === "chapter";
	}

	function isPolice() {
		return currentUser && currentUser.role === "police";
	}

	let buzzChapterContactsByKey = new Map();

	function normalizeBuzzContact(c) {
		return {
			fullName: String(c?.fullName ?? c?.name ?? "").trim(),
			position: String(c?.position ?? c?.title ?? "").trim(),
			email: String(c?.email ?? "").trim(),
			phone: String(c?.phone ?? "").trim(),
		};
	}
	async function ensureUnicodePdfFont(doc) {
		// Put the font file at: public/fonts/NotoSans-Regular.ttf
		const res = await fetch("/fonts/NotoSans-Regular.ttf");
		if (!res.ok) return false;

		const buf = await res.arrayBuffer();
		const bytes = new Uint8Array(buf);

		// Safe base64 (chunked)
		let binary = "";
		const CHUNK = 0x8000;
		for (let i = 0; i < bytes.length; i += CHUNK) {
			binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
		}
		const b64 = btoa(binary);

		doc.addFileToVFS("NotoSans-Regular.ttf", b64);
		doc.addFont("NotoSans-Regular.ttf", "NotoSans", "normal");
		doc.setFont("NotoSans", "normal");
		return true;
	}

	function setBuzzChapterContactsIndex(chaptersFromSheet) {
		buzzChapterContactsByKey = new Map();
		const arr = Array.isArray(chaptersFromSheet) ? chaptersFromSheet : [];

		for (const ch of arr) {
			const nameProbe = String(
				ch?.chapterName ||
					ch?.chapterUsername ||
					ch?.fratName ||
					ch?.name ||
					"",
			);
			if (isTestChapterName(nameProbe)) continue;
			const raw = Array.isArray(ch?.contacts) ? ch.contacts : [];
			const contacts = raw
				.map(normalizeBuzzContact)
				.filter((c) => c.fullName || c.position || c.phone || c.email);

			if (!contacts.length) continue;

			const k1 = keyify(ch?.chapterName || "");
			const k2 = keyify(ch?.chapterUsername || "");
			const k3 = keyify(ch?.fratName || ch?.name || "");

			if (k1) buzzChapterContactsByKey.set(k1, contacts);
			if (k2) buzzChapterContactsByKey.set(k2, contacts);
			if (k3) buzzChapterContactsByKey.set(k3, contacts);
		}
	}

	function pickTopTwoOICContacts(contacts) {
		const list = Array.isArray(contacts)
			? contacts.map(normalizeBuzzContact)
			: [];

		const scorePos = (pos) => {
			const p = String(pos || "").toLowerCase();
			let s = 0;
			if (p.includes("risk")) s += 100;
			if (p.includes("security")) s += 95;
			if (p.includes("president")) s += 90;
			if (p.includes("vice") || p === "vp") s += 80;
			if (p.includes("secretary")) s += 70;
			if (p.includes("treasurer") || p.includes("finance")) s += 70;
			return s;
		};

		const scored = list.map((c, idx) => ({
			c,
			idx,
			s: scorePos(c.position) + (c.phone ? 10 : 0) + (c.fullName ? 5 : 0),
		}));

		scored.sort((a, b) => b.s - a.s || a.idx - b.idx);

		const out = [];
		for (const it of scored) {
			const c = it.c;
			if (!(c.fullName || c.position || c.phone)) continue;
			out.push(c);
			if (out.length === 2) break;
		}
		return out;
	}

	function bestEffortChapterContactsForBuzzEvent(ev, chapterLabel) {
		// 1) directly on event (if backend included it)
		if (Array.isArray(ev?.chapterContacts) && ev.chapterContacts.length)
			return ev.chapterContacts;
		if (Array.isArray(ev?.chapter?.contacts) && ev.chapter.contacts.length)
			return ev.chapter.contacts;

		// 2) from contacts sheet index (by chapter label / username / etc)
		const label =
			String(chapterLabel || "").trim() ||
			String(bestEffortFratNameFromEvent(ev) || "").trim() ||
			String(ev?.chapterUsername || "").trim() ||
			String(ev?.chapterName || "").trim();

		const k = keyify(label);
		if (k && buzzChapterContactsByKey.has(k))
			return buzzChapterContactsByKey.get(k);

		const ku = keyify(ev?.chapterUsername || "");
		if (ku && buzzChapterContactsByKey.has(ku))
			return buzzChapterContactsByKey.get(ku);

		return [];
	}

	function autofillBuzzOICsFromChapterContacts() {
		if (
			!buzzData ||
			!Array.isArray(buzzData.events) ||
			!buzzData.events.length
		)
			return;

		const isNA = (v) => {
			const s = String(v ?? "").trim();
			return !s || s.toUpperCase() === "N/A";
		};

		let changed = false;

		const next = buzzData.events.map((row) => {
			if (!row) return row;

			const contactsRaw = bestEffortChapterContactsForBuzzEvent(
				null,
				row.chapter,
			);
			const [o1, o2] = pickTopTwoOICContacts(contactsRaw);

			if (!o1 && !o2) return row;

			const out = { ...row };

			if (o1) {
				if (isNA(out.oicPosition) && o1.position) {
					out.oicPosition = o1.position;
					changed = true;
				}
				if (isNA(out.oicName) && o1.fullName) {
					out.oicName = o1.fullName;
					changed = true;
				}
				if (isNA(out.oicPhone) && o1.phone) {
					out.oicPhone = o1.phone;
					changed = true;
				}
			}

			if (o2) {
				const disp = [o2.position, o2.fullName]
					.filter(Boolean)
					.join(" — ");
				if (isNA(out.otherOicName) && disp) {
					out.otherOicName = disp;
					changed = true;
				}
				if (isNA(out.otherOicPhone) && o2.phone) {
					out.otherOicPhone = o2.phone;
					changed = true;
				}
			}

			return out;
		});

		if (changed) buzzData = { ...buzzData, events: next };
	}

	function bestEffortFratNameFromEvent(ev) {
		if (!ev) return "";

		// prefer event-provided name
		if (typeof ev.fratName === "string" && ev.fratName.trim())
			return ev.fratName.trim();
		if (typeof ev.chapterName === "string" && ev.chapterName.trim())
			return ev.chapterName.trim();

		// map via chapters list (reflects admin edits)
		const uname = String(ev.chapterUsername || "").trim();
		if (uname && Array.isArray(chapters) && chapters.length) {
			const ch = chapters.find(
				(c) => String(c?.username || "").trim() === uname,
			);
			if (ch)
				return String(
					ch.name || ch.fratName || ch.username || "",
				).trim();
		}

		// fallback
		return uname || "";
	}

	function bestEffortFratAddressFromEvent(ev) {
		if (!ev) return "";

		if (typeof ev.fratAddress === "string" && ev.fratAddress.trim())
			return ev.fratAddress.trim();
		if (typeof ev.address === "string" && ev.address.trim())
			return ev.address.trim();

		if (
			ev.chapter &&
			typeof ev.chapter.fratAddress === "string" &&
			ev.chapter.fratAddress.trim()
		)
			return ev.chapter.fratAddress.trim();
		if (
			ev.chapter &&
			typeof ev.chapter.address === "string" &&
			ev.chapter.address.trim()
		)
			return ev.chapter.address.trim();

		const ch =
			chapters &&
			chapters.find(
				(c) =>
					c.username === ev.chapterUsername ||
					c.fratName === ev.fratName ||
					c.name === ev.fratName,
			);

		if (ch && typeof ch.fratAddress === "string" && ch.fratAddress.trim())
			return ch.fratAddress.trim();
		if (ch && typeof ch.address === "string" && ch.address.trim())
			return ch.address.trim();

		if (
			currentUser &&
			typeof currentUser.fratAddress === "string" &&
			currentUser.fratAddress.trim()
		)
			return currentUser.fratAddress.trim();

		return "";
	}

	function bestEffortEmergencyPhonesFromUser(u) {
		if (!u) return [];
		if (Array.isArray(u.emergencyPhones) && u.emergencyPhones.length)
			return u.emergencyPhones;
		if (Array.isArray(u.emergencyPhone) && u.emergencyPhone.length)
			return u.emergencyPhone; // just in case
		if (typeof u.emergencyPhone === "string" && u.emergencyPhone.trim())
			return [u.emergencyPhone.trim()];
		return [];
	}

	function bestEffortEmergencyPhonesFromEvent(ev) {
		if (!ev) return [];
		if (Array.isArray(ev.emergencyPhones) && ev.emergencyPhones.length)
			return ev.emergencyPhones;
		if (
			Array.isArray(ev.chapterEmergencyPhones) &&
			ev.chapterEmergencyPhones.length
		)
			return ev.chapterEmergencyPhones;
		if (typeof ev.emergencyPhone === "string" && ev.emergencyPhone.trim())
			return [ev.emergencyPhone.trim()];
		if (
			typeof ev.chapterEmergencyPhone === "string" &&
			ev.chapterEmergencyPhone.trim()
		)
			return [ev.chapterEmergencyPhone.trim()];
		if (typeof ev.emergency_phone === "string" && ev.emergency_phone.trim())
			return [ev.emergency_phone.trim()];

		if (ev.chapter) {
			if (
				Array.isArray(ev.chapter.emergencyPhones) &&
				ev.chapter.emergencyPhones.length
			)
				return ev.chapter.emergencyPhones;
			if (
				typeof ev.chapter.emergencyPhone === "string" &&
				ev.chapter.emergencyPhone.trim()
			)
				return [ev.chapter.emergencyPhone.trim()];
			if (
				typeof ev.chapter.emergency_phone === "string" &&
				ev.chapter.emergency_phone.trim()
			)
				return [ev.chapter.emergency_phone.trim()];
		}

		const ch =
			chapters &&
			chapters.find(
				(c) =>
					c.username === ev.chapterUsername ||
					c.fratName === ev.fratName,
			);

		if (ch) {
			const fromCh = bestEffortEmergencyPhonesFromUser(ch);
			if (fromCh.length) return fromCh;
		}

		if (
			currentUser &&
			currentUser.role === "chapter" &&
			(!ev.chapterUsername || ev.chapterUsername === currentUser.username)
		) {
			const fromMe = bestEffortEmergencyPhonesFromUser(currentUser);
			if (fromMe.length) return fromMe;
		}

		return [];
	}

	$: eventEmergencyPhones = (() => {
		if (!selectedEvent) {
			return bestEffortEmergencyPhonesFromUser(currentUser);
		}
		return bestEffortEmergencyPhonesFromEvent(selectedEvent);
	})();

	$: filteredChapters = !chapterSearch.trim()
		? chapters
		: chapters.filter((c) => {
				const name = (c.fratName || c.username || "").toLowerCase();
				return name.includes(chapterSearch.trim().toLowerCase());
			});

	function keyify(s) {
		return String(s || "")
			.trim()
			.toLowerCase();
	}

	$: pendingPhonesByFratKey = (() => {
		const map = {};

		for (const r of signupRequests || []) {
			if (!r || r.status !== "pending") continue;

			// KEY BY FULL FRAT NAME
			const k = keyify(r.fratName);
			if (!k) continue;

			const p = normalizeUSPhone(r.phone || "");
			if (!p) continue;

			if (!map[k]) map[k] = [];
			map[k].push(p);
		}

		// de-dupe per frat
		for (const k of Object.keys(map)) {
			const seen = new Set();
			map[k] = map[k].filter((p) => {
				const d = String(p).replace(/\D/g, "");
				if (!d || seen.has(d)) return false;
				seen.add(d);
				return true;
			});
		}

		return map;
	})();
	$: pendingPhonesForSelectedChapter = (() => {
		if (!selectedChapter) return [];
		const k = keyify(
			selectedChapter.name ||
				selectedChapter.fratName ||
				selectedChapter.username,
		);
		return pendingPhonesByFratKey[k] || [];
	})();
	$: pendingPhonesForSignupSelection =
		pendingPhonesByFratKey[keyify(suFratName)] || [];

	// Blackout dates for Spring 2026
	const BLACKOUT_DATES = [
		{
			start: new Date("2026-01-08"),
			end: new Date("2026-01-11"),
			reason: "First Weekend Back",
		},
		{
			start: new Date("2026-03-17"),
			end: new Date("2026-03-17"),
			reason: "Saint Patrick's Day",
		},
		{
			start: new Date("2026-04-20"),
			end: new Date("2026-04-20"),
			reason: "4/20",
		},
		{
			start: new Date("2026-04-24"),
			end: new Date("2026-04-26"),
			reason: "Last Weekend After Classes",
		},
	];

	function getChapterContactsForSelectedEvent() {
		if (!selectedEvent) return [];
		if (selectedEvent.chapterContacts) return selectedEvent.chapterContacts;
		if (selectedEvent.chapter && selectedEvent.chapter.contacts)
			return selectedEvent.chapter.contacts;
		return [];
	}

	function isBlackoutDate(date) {
		const dateOnly = new Date(
			date.getFullYear(),
			date.getMonth(),
			date.getDate(),
		);
		for (const blackout of BLACKOUT_DATES) {
			const blackoutStart = new Date(
				blackout.start.getFullYear(),
				blackout.start.getMonth(),
				blackout.start.getDate(),
			);
			const blackoutEnd = new Date(
				blackout.end.getFullYear(),
				blackout.end.getMonth(),
				blackout.end.getDate(),
			);
			if (dateOnly >= blackoutStart && dateOnly <= blackoutEnd)
				return blackout.reason;
		}
		return null;
	}

	function frontEndValidateEvent() {
	// Validate required chapter setup for parties
	if (isParty(newEventType)) {
		if (!currentUser.securityCompany || !currentUser.securityCompany.trim()) {
			return "Third Party Security Company is required. Please complete your chapter account settings before registering a party.";
		}
		if (!currentUser.riskPolicy || !currentUser.riskPolicy.trim()) {
			return "Risk Management Plan is required. Please complete your chapter account settings before registering a party.";
		}
		if (!newEventCapacity || String(newEventCapacity).trim() === "") {
			return "Expected event capacity is required for parties.";
		}
		if (!newEventStreetAddress || String(newEventStreetAddress).trim() === "") {
			return "Event street address is required for parties.";
		}

		if (!newEventRegistrantName || String(newEventRegistrantName).trim() === "") {
			return "Name of person filling out the form is required for parties.";
		}
		const regPhone = normalizeUSPhone(String(newEventRegistrantPhone || ""));
		const regEmail = String(newEventRegistrantEmail || "").trim();
		if (!regPhone && !regEmail) {
			return "Provide a phone number or email for the person filling out the form.";
		}
	}

	if (!newEventTitle.trim()) return "Event title is required.";
	if (!newEventStart || !newEventEnd)
		return "Start and end times are required.";
	const start = new Date(newEventStart);
	const end = new Date(newEventEnd);
	if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()))
		return "Invalid start or end time.";
	if (end <= start) return "End time must be after start.";

	if (isParty(newEventType)) {
		const wd = new Intl.DateTimeFormat("en-US", {
			timeZone: BUZZ_TZ,
			weekday: "short",
		}).format(start);

		const startDay = {
			Sun: 0,
			Mon: 1,
			Tue: 2,
			Wed: 3,
			Thu: 4,
			Fri: 5,
			Sat: 6,
		}[wd];

		if (startDay < 4 || startDay > 6)
			return "Parties must take place Thursday through Saturday night.";

		const durationHours = (end - start) / (1000 * 60 * 60);
		if (durationHours > 4.0001)
			return "Parties can last at most 4 hours.";

		const endHour = end.getHours();
		const endMinute = end.getMinutes();
		if (endHour > 2 || (endHour === 2 && endMinute > 0)) {
			if (endHour < 12)
				return "Parties must end by 2:00 AM at the latest.";
		}

		const blackoutReason = isBlackoutDate(start);
		if (blackoutReason)
			return `Cannot register party on a blackout date: ${blackoutReason}`;
	}
	return "";
}
	async function refreshMe() {
		const res = await api("/me");
		if (!res.ok) return;
		const data = await res.json().catch(() => ({}));
		if (data.user) currentUser = data.user;
	}

	async function api(path, options = {}) {
		const headers = options.headers || {};
		if (token) headers["Authorization"] = `Bearer ${token}`;
		return fetch(`${backendURL}${path}`, {
			...options,
			headers: {
				"Content-Type": "application/json",
				...headers,
			},
		});
	}

	// In the login function in your Svelte file, update the error handling:

	// Add this after the login function
	async function login() {
		loginError = "";
		try {
			const res = await fetch(`${backendURL}/login`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					username: loginUsername.trim(),
					password: loginPassword,
				}),
			});
			if (!res.ok) {
				let msg = "Invalid username or password.";
				try {
					const body = await res.json();
					if (body && body.error) msg = body.error;
				} catch (_) {}
				loginError = msg;
				return;
			}
			const data = await res.json();
			token = data.token;
			currentUser = data.user;
			activePage = "dashboard";

			// Background loading based on role
			if (isIFCAdmin()) {
				await loadChapters();
				loadSignupRequests(); // Background load
				loadEventRequests(); // Background load
				loadAllComplaints(); // Background load
				await loadContactsSheetIntoBuzzData();
			} else if (isChapter()) {
				loadChapterComplaints(); // Background load for chapters
			}

			await loadEvents();
		} catch (err) {
			console.error(err);
			loginError = "Login failed.";
		}
	}

	// Add reactive statements to count pending items
	$: pendingSignupRequestsCount = signupRequests.filter(
		(r) => r.status === "pending",
	).length;
	$: pendingEventRequestsCount = eventRequests.length; // All event requests are pending
	$: pendingChapterComplaintsCount = chapterComplaints.filter(
		(c) => c.status === "pending",
	).length;
	$: pendingAdminComplaintsCount = allComplaints.filter(
		(c) => c.status === "pending",
	).length;

	async function loadEvents() {
		if (!token) return;
		loadingEvents = true;
		try {
			const res = await api("/events", { method: "GET" });
			if (!res.ok) {
				console.error("Failed to load events");
				loadingEvents = false;
				return;
			}
			const data = await res.json();
			events = data.events || [];
			console.log(events);
			if (currentUser && currentUser.username !== "demo") {
				events = events.filter((e) => e.chapterUsername !== "demo");
			}
			if (selectedEvent) {
				const updated = events.find((e) => e.id === selectedEvent.id);
				if (updated) selectedEvent = updated;
			}
		} catch (err) {
			console.error(err);
		} finally {
			loadingEvents = false;
		}
	}

	async function refreshSelectedEvent() {
		if (!selectedEvent) return;
		try {
			const res = await api(`/events/${selectedEvent.id}`, {
				method: "GET",
			});
			if (!res.ok) return;
			const data = await res.json();
			selectedEvent = data.event;
			events = events.map((e) =>
				e.id === data.event.id ? data.event : e,
			);
		} catch (err) {
			console.error(err);
		}
	}

	function formatDateTime(dt) {
		if (!dt) return "";

		let d = null;

		// Firestore Timestamp instance
		if (typeof dt === "object" && dt && typeof dt.toDate === "function") {
			d = dt.toDate();
		}
		// Firestore Timestamp plain object
		else if (typeof dt === "object" && dt) {
			const sec = dt.seconds ?? dt._seconds;
			const nsec = dt.nanoseconds ?? dt._nanoseconds ?? 0;
			if (typeof sec === "number")
				d = new Date(sec * 1000 + Math.floor(nsec / 1e6));
		}
		// string/number/date
		else {
			d = new Date(dt);
		}

		if (!d || Number.isNaN(d.getTime())) return "";
		return new Intl.DateTimeFormat("en-US", {
			timeZone: BUZZ_TZ, // "America/Denver"
			month: "short",
			day: "numeric",
			year: "numeric",
			hour: "numeric",
			minute: "2-digit",
		}).format(d);
	}


	function formatDateOnly(dt) {
		if (!dt) return "";
		let d = null;
		if (typeof dt === "object" && dt && typeof dt.toDate === "function") d = dt.toDate();
		else if (typeof dt === "object" && dt) {
			const sec = dt.seconds ?? dt._seconds;
			const nsec = dt.nanoseconds ?? dt._nanoseconds ?? 0;
			if (typeof sec === "number") d = new Date(sec * 1000 + Math.floor(nsec / 1e6));
		} else d = new Date(dt);
		if (!d || Number.isNaN(d.getTime())) return "";
		return new Intl.DateTimeFormat("en-US", {
			timeZone: BUZZ_TZ,
			month: "short",
			day: "numeric",
			year: "numeric",
		}).format(d);
	}

	function formatTimeOnlyLower(dt) {
		if (!dt) return "";
		let d = null;
		if (typeof dt === "object" && dt && typeof dt.toDate === "function") d = dt.toDate();
		else if (typeof dt === "object" && dt) {
			const sec = dt.seconds ?? dt._seconds;
			const nsec = dt.nanoseconds ?? dt._nanoseconds ?? 0;
			if (typeof sec === "number") d = new Date(sec * 1000 + Math.floor(nsec / 1e6));
		} else d = new Date(dt);
		if (!d || Number.isNaN(d.getTime())) return "";
		const s = new Intl.DateTimeFormat("en-US", {
			timeZone: BUZZ_TZ,
			hour: "numeric",
			minute: "2-digit",
		}).format(d);
		return s.replace(/\s?(AM|PM)$/i, (m) => m.toLowerCase().replace(" ", ""));
	}

	function formatTimeRangeLower(start, end) {
		const a = formatTimeOnlyLower(start);
		const b = formatTimeOnlyLower(end);
		if (!a && !b) return "";
		return `${a}-${b}`;
	}

	function last10Digits(raw) {
		const d = String(raw || "").replace(/\D/g, "");
		if (!d) return "";
		return d.length >= 10 ? d.slice(-10) : d;
	}

function getEmergencyContactDropdownOptions(u) {
		const contacts = u && Array.isArray(u.contacts) ? u.contacts : [];
		const emPhones = bestEffortEmergencyPhonesFromUser(u) || [];
		const emSet = new Set(emPhones.map(normalizeUSPhone).filter(Boolean));

		// Prefer true "Emergency Contacts" (phones listed on Account page),
		// but fall back to all chapter contacts if none match yet.
		const emergencyContacts = contacts.filter((c) =>
			emSet.has(normalizeUSPhone(c?.phone || ""))
		);
		return emergencyContacts.length ? emergencyContacts : contacts;
	}

	function syncRegistrantFromEmergencyPicker() {
		const pick = String(newEventRegistrantPickerPhone || "");
		if (!pick) return;

		const opts = getEmergencyContactDropdownOptions(currentUser);
		const chosen = opts.find(
			(c) => normalizeUSPhone(c?.phone || "") === pick
		);
		if (!chosen) return;

		newEventRegistrantName = String(chosen.fullName || "").trim();
		if (String(chosen.email || "").trim()) {
			newEventRegistrantEmail = String(chosen.email || "").trim();
		}
		if (String(chosen.phone || "").trim()) {
			newEventRegistrantPhone = String(chosen.phone || "").trim();
		}
	}

	function openNewEventModal() {

	newEventTitle = "";
	newEventType = "party";
	startDate = "";
	endDate = "";
	startHour = "8";
	startMinute = "00";
	startAmPm = "PM";
	endHour = "11";
	endMinute = "00";
	endAmPm = "PM";
	newEventCapacity = "";
	newEventHasOrganizer = false;
	newEventOrganizerName = "";
	newEventTheme = "";
	newEventStreetAddress =
		(currentUser && (currentUser.fratAddress || currentUser.address)) || "";
	newEventSecurityPlanUrl =
		(currentUser && currentUser.riskPolicy) ? String(currentUser.riskPolicy) : "";
	// Default registrant to first emergency contact (fallback: first chapter contact)
	const opts = getEmergencyContactDropdownOptions(currentUser);
	const firstC = opts && opts.length ? opts[0] : null;

	newEventRegistrantPickerPhone = normalizeUSPhone(firstC?.phone || "");
	newEventRegistrantName = String(firstC?.fullName || "").trim();
	newEventRegistrantEmail = String(firstC?.email || "").trim();
	newEventRegistrantPhone = String(firstC?.phone || "").trim();
	newEventError = "";
	showNewEventModal = true;
}

	function closeNewEventModal() {
		showNewEventModal = false;
	}

	function isParty(type) {
		return type === "party";
	}

	function resetStateOnLogout() {
		token = "";
		currentUser = null;
		events = [];
		selectedEvent = null;
		activePage = "dashboard";
		loginPassword = "";
		chapters = [];
		selectedChapter = null;
		signupRequests = [];
	}

	function logout() {
		resetStateOnLogout();
	}

	function selectEvent(ev) {
		selectedEvent = ev;
	}
	function toISODateLocal(d) {
		const yyyy = d.getFullYear();
		const mm = String(d.getMonth() + 1).padStart(2, "0");
		const dd = String(d.getDate()).padStart(2, "0");
		return `${yyyy}-${mm}-${dd}`;
	}

	function toISODateTz(d, tz = BUZZ_TZ) {
		const parts = new Intl.DateTimeFormat("en-CA", {
			timeZone: tz,
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
		}).formatToParts(d);
		const y = parts.find((p) => p.type === "year")?.value;
		const m = parts.find((p) => p.type === "month")?.value;
		const da = parts.find((p) => p.type === "day")?.value;
		return `${y}-${m}-${da}`;
	}

	function denverDow(d = new Date()) {
		const wd = new Intl.DateTimeFormat("en-US", {
			timeZone: BUZZ_TZ,
			weekday: "short",
		}).format(d);
		return (
			{ Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[wd] ??
			d.getDay()
		);
	}

	function tzOffsetHHMM(d, tz = BUZZ_TZ) {
		// returns like "-07:00" / "-06:00"
		const parts = new Intl.DateTimeFormat("en-US", {
			timeZone: tz,
			timeZoneName: "shortOffset",
		}).formatToParts(d);
		const off =
			parts.find((p) => p.type === "timeZoneName")?.value || "GMT";
		const m = off.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
		if (!m) return "Z";
		const sign = m[1];
		const hh = String(m[2]).padStart(2, "0");
		const mm = String(m[3] || "00").padStart(2, "0");
		return `${sign}${hh}:${mm}`;
	}

	function makeDenverISO(ymd, hh, mm, tz = BUZZ_TZ) {
		// Build an ISO string with the correct Denver offset for that date
		const probe = new Date(`${ymd}T12:00:00Z`); // safe midday probe
		const off = tzOffsetHHMM(probe, tz);
		const H = String(hh).padStart(2, "0");
		const M = String(mm).padStart(2, "0");
		return `${ymd}T${H}:${M}:00${off}`;
	}

	// targetDow: Thu=4, Fri=5, Sat=6 (Sun=0..Sat=6)
	function applyPartyPreset(targetDow) {
		const now = new Date();

		// "Today" in Denver
		const todayYMD = toISODateTz(now, BUZZ_TZ);
		const todayDow = denverDow(now);

		let daysAhead = (targetDow - todayDow + 7) % 7;
		if (daysAhead === 0) daysAhead = 7; // always next Thu/Fri/Sat

		// Move forward in a UTC-safe way, then re-render date in Denver
		const startMiddayUtc = new Date(`${todayYMD}T12:00:00Z`);
		startMiddayUtc.setUTCDate(startMiddayUtc.getUTCDate() + daysAhead);
		const endMiddayUtc = new Date(startMiddayUtc);
		endMiddayUtc.setUTCDate(endMiddayUtc.getUTCDate() + 1);

		startDate = toISODateTz(startMiddayUtc, BUZZ_TZ);
		endDate = toISODateTz(endMiddayUtc, BUZZ_TZ);

		startHour = "10";
		startMinute = "00";
		startAmPm = "PM";

		endHour = "2";
		endMinute = "00";
		endAmPm = "AM";
	}

	async function createEvent() {
	newEventError = frontEndValidateEvent();
	if (newEventError) return;
	try {
		const body = {
			title: newEventTitle.trim(),
			type: newEventType,
			startTime: newEventStart,
			endTime: newEventEnd,
		};

		if (isParty(newEventType)) {
			body.capacity = String(newEventCapacity).trim();
			body.hasOrganizer = newEventHasOrganizer;
			if (newEventHasOrganizer && newEventOrganizerName.trim()) {
				body.organizerName = newEventOrganizerName.trim();
			}

			// Theme is just the title (keep storing theme for backwards-compat)
			body.theme = String(body.title || "").trim();

			body.eventAddress = String(newEventStreetAddress || "").trim();

			// Always pull these from Account settings at submit-time
			body.securityPlanUrl = String(currentUser?.riskPolicy || "").trim();
			body.securityCompany = String(currentUser?.securityCompany || "").trim();
			body.registrantName = String(newEventRegistrantName || "").trim();
			body.registrantEmail = String(newEventRegistrantEmail || "").trim();
			body.registrantPhone = String(newEventRegistrantPhone || "").trim();
		}

		const res = await api("/events", {
			method: "POST",
			body: JSON.stringify(body),
		});
		if (!res.ok) {
			const e = await res.json().catch(() => ({}));
			newEventError = e.error || "Failed to create event.";
			return;
		}
		const data = await res.json();
		events = [data.event, ...events];
		showNewEventModal = false;
		selectedEvent = data.event;
	} catch (err) {
		console.error(err);
		newEventError = "Failed to create event.";
	}
}
	async function issueTicket(kind) {
		if (!selectedEvent) return;
		try {
			const res = await api(`/events/${selectedEvent.id}/tickets`, {
				method: "POST",
				body: JSON.stringify({ kind }),
			});
			if (!res.ok) {
				const e = await res.json().catch(() => ({}));
				alert(e.error || "Failed to send ticket SMS.");
				return;
			}
			await refreshSelectedEvent();
		} catch (err) {
			console.error(err);
			alert("Failed to send ticket SMS.");
		}
	}

	function toggleContactPopover() {
		showContactPopover = !showContactPopover;
	}

	function toggleUserMenu() {
		showUserMenu = !showUserMenu;
	}

	function goAccount() {
		activePage = "account";

		if (!currentUser) return;

		// Neighbor vs chapter display
		if (isNeighbor()) {
			accountFratName =
				currentUser.name ||
				currentUser.email ||
				currentUser.username ||
				"";
			accountAddress = currentUser.address || "";
			accountContacts = [];
		} else {
			accountFratName =
				currentUser.fratName || currentUser.username || "";
			accountAddress = currentUser.address || "";
			accountContacts = Array.isArray(currentUser.contacts)
				? currentUser.contacts
				: [];
			accountSecurityCompany = currentUser.securityCompany || "";
			accountRiskPolicy = currentUser.riskPolicy || "";
		}
	}

	// async function saveNeighborAccount() {
	// 	neighborEditSaving = true;
	// 	neighborEditMessage = "";
	// 	try {
	// 		const res = await api("/me", {
	// 			method: "PUT",
	// 			body: JSON.stringify({
	// 				name: neighborEditName.trim(),
	// 				email: neighborEditEmail.trim(),
	// 				phone: neighborEditPhone.trim(),
	// 				address: neighborEditAddress.trim(),
	// 			}),
	// 		});

	// 		if (!res.ok) {
	// 			const data = await res.json().catch(() => ({}));
	// 			throw new Error(data.error || `Save failed (${res.status})`);
	// 		}

	// 		neighborEditMessage = "Saved.";
	// 		await refreshMe();
	// 	} catch (e) {
	// 		neighborEditMessage = e?.message || "Failed to save.";
	// 	} finally {
	// 		neighborEditSaving = false;
	// 	}
	// }

	function goDashboard() {
		activePage = "dashboard";
	}

	function goRequests() {
		activePage = "requests";
	}

	const MAX_CONTACTS = 25; // UI cap; emergency phones still cap at 8
	let accountContacts = [blankContact()];
	let editingContacts = [blankContact()];

	function blankContact() {
		return { fullName: "", position: "", email: "", phone: "" };
	}

	function ensureEditableContactList(list) {
		const arr = Array.isArray(list)
			? list.map((c) => ({
					fullName: String(c?.fullName || ""),
					position: String(c?.position || ""),
					email: String(c?.email || ""),
					phone: String(c?.phone || ""),
				}))
			: [];
		return arr.length ? arr.slice(0, MAX_CONTACTS) : [blankContact()];
	}

	function sanitizeContacts(list) {
		const arr = Array.isArray(list) ? list : [];
		const out = [];

		for (const c of arr) {
			const fullName = String(c?.fullName || "").trim();
			const position = String(c?.position || "").trim();
			const email = String(c?.email || "").trim();
			const phone = normalizeUSPhone(String(c?.phone || ""));

			// ignore totally empty rows
			if (!fullName && !position && !email && !phone) continue;

			out.push({ fullName, position, email, phone });
			if (out.length >= MAX_CONTACTS) break;
		}
		return out;
	}

	function addContactRow(listSetter, listGetter) {
		const cur = listGetter();
		if (cur.length >= MAX_CONTACTS) return;
		listSetter([...cur, blankContact()]);
	}

	function removeContactRow(listSetter, listGetter, idx) {
		const cur = listGetter();

		// Require at least one emergency contact
		if (!cur || cur.length <= 1) return;

		const next = cur.filter((_, i) => i !== idx);
		listSetter(next.length ? next : [blankContact()]);
	}

	// Emergency phones are derived from contacts
	function deriveEmergencyPhonesFromContacts(contacts) {
		return sanitizeEmergencyPhones((contacts || []).map((c) => c?.phone));
	}

	// Inline status for cancel
	let eventDeleteMessage = "";
	let eventDeleteIsError = false;

	$: canCancelSelectedEvent = (() => {
		if (!selectedEvent || !currentUser) return false;
		if (selectedEvent.status === "cancelled") return false;

		const start = new Date(selectedEvent.startTime);
		const now = new Date();
		if (isNaN(start.getTime())) return false;

		const diffMs = start.getTime() - now.getTime();
		const diffHours = diffMs / (1000 * 60 * 60);
		const diffMinutes = diffMs / (1000 * 60);

		if (currentUser.role === "chapter") return diffHours >= 3;

		if (
			currentUser.role === "ifc_admin" ||
			currentUser.username === "IFC_ADMIN"
		) {
			return true;
		}

		return false;
	})();

	let cancelConfirmEventId = null;
	let cancelConfirmTimer = null;

	function armCancelConfirm(eventId) {
		cancelConfirmEventId = eventId;
		if (cancelConfirmTimer) clearTimeout(cancelConfirmTimer);
		cancelConfirmTimer = setTimeout(() => {
			cancelConfirmEventId = null;
			cancelConfirmTimer = null;
		}, 4000);
	}

	function clearCancelConfirm() {
		cancelConfirmEventId = null;
		if (cancelConfirmTimer) {
			clearTimeout(cancelConfirmTimer);
			cancelConfirmTimer = null;
		}
	}

	// If they switch which event is selected, drop the confirm state
	$: if (
		selectedEvent &&
		cancelConfirmEventId &&
		selectedEvent.id !== cancelConfirmEventId
	) {
		clearCancelConfirm();
	}

	async function cancelEvent(eventId) {
		clearCancelConfirm();
		eventDeleteMessage = "";
		eventDeleteIsError = false;

		try {
			const res = await api(`/events/${eventId}/cancel`, {
				method: "POST",
			});
			const body = await res.json().catch(() => ({}));

			if (!res.ok) {
				eventDeleteMessage = body.error || "Failed to cancel event.";
				eventDeleteIsError = true;
				return;
			}

			const updated = body.event;
			events = events.map((e) => (e.id === updated.id ? updated : e));
			if (selectedEvent && selectedEvent.id === updated.id)
				selectedEvent = updated;

			eventDeleteMessage = "Event cancelled.";
			eventDeleteIsError = false;
		} catch (err) {
			console.error(err);
			eventDeleteMessage = "Failed to cancel event.";
			eventDeleteIsError = true;
		}
	}

$: isSelectedEventCancelled = !!(
	selectedEvent && selectedEvent.status === "cancelled"
);

// Add this new reactive statement
$: isSelectedEventDenied = !!(
	selectedEvent && selectedEvent.status === "denied"
);

$: isSelectedEventCancelledOrDenied = !!(
	selectedEvent && (selectedEvent.status === "cancelled" || selectedEvent.status === "denied")
);

	async function saveAccount() {
		accountMessage = "";
		accountSaving = true;
		try {
			const cleanContacts = sanitizeContacts(accountContacts);
			const body = {
				name: accountFratName.trim(),
				contacts: cleanContacts,
				emergencyPhones:
					deriveEmergencyPhonesFromContacts(cleanContacts),
				securityCompany: accountSecurityCompany.trim(),
				riskPolicy: accountRiskPolicy.trim(),
			};

			const res = await api("/me/account-basic", {
				method: "PUT",
				body: JSON.stringify(body),
			});
			if (!res.ok) {
				const e = await res.json().catch(() => ({}));
				accountMessage = e.error || "Failed to save.";
				accountSaving = false;
				return;
			}
			const data = await res.json();
			currentUser = data.user;

			const phones = bestEffortEmergencyPhonesFromUser(currentUser);
			accountEmergencyPhones = ensureEditablePhoneList(
				phones.length ? phones : [""],
			);

			accountMessage = "Saved.";
		} catch (err) {
			console.error(err);
			accountMessage = "Failed to save.";
		} finally {
			accountSaving = false;
		}
	}

	async function copyToClipboard(text, label) {
		copyStatus = "";
		copyStatusType = "";
		try {
			await navigator.clipboard.writeText(text);
			copyStatus = `${label} copied to clipboard.`;
			copyStatusType = "success";
		} catch (err) {
			console.error("Failed to copy:", err);
			copyStatus = `Failed to copy ${label.toLowerCase()}.`;
			copyStatusType = "error";
		}
	}

	function openInMaps(address) {
		if (!address) {
			alert("No address available");
			return;
		}
		const encoded = encodeURIComponent(address);
		window.open(
			`https://www.google.com/maps/search/?api=1&query=${encoded}`,
			"_blank",
		);
	}
	async function fetchAddressSuggestions(query) {
		addressSuggestions = [];
		const q = (query || "").trim();
		if (q.length < 3) return;

		try {
			const res = await api(
				`/address-search?q=${encodeURIComponent(q)}`,
				{
					method: "GET",
				},
			);

			if (!res.ok) {
				console.error("address-search HTTP error:", res.status);
				return;
			}

			const data = await res.json().catch(() => ({ results: [] }));
			addressSuggestions = data.results || [];
		} catch (err) {
			console.error("address search error", err);
		}
	}

	function handleAddressInput(event) {
		accountAddress = event.target.value;
		selectedAddress = null;
		clearTimeout(addressSearchTimeout);
		addressSearchTimeout = setTimeout(
			() => fetchAddressSuggestions(accountAddress),
			150,
		);
	}

	function chooseAddress(suggestion) {
		accountAddress = suggestion.formatted;
		selectedAddress = suggestion;
		addressSuggestions = [];
	}
	async function saveAddress() {
		try {
			const res = await api("/me/address", {
				method: "PUT",
				body: JSON.stringify({ address: accountAddress.trim() }),
			});
			if (!res.ok) {
				const e = await res.json().catch(() => ({}));
				accountMessage =
					e.error || "Address update failed (admin-only).";
				return;
			}
			const data = await res.json();
			currentUser = data.user;
			accountMessage = "Address updated.";
		} catch (err) {
			console.error(err);
			accountMessage = "Address update failed.";
		}
	}

	async function changePassword() {
		passwordMessage = "";
		if (!passwordNew || passwordNew !== passwordConfirm) {
			passwordMessage = "Passwords do not match.";
			return;
		}
		try {
			const res = await api("/me/password", {
				method: "PUT",
				body: JSON.stringify({
					currentPassword: passwordCurrent,
					newPassword: passwordNew,
				}),
			});
			if (!res.ok) {
				const e = await res.json().catch(() => ({}));
				passwordMessage = e.error || "Failed to change password.";
				return;
			}
			passwordMessage = "Password updated.";
			passwordCurrent = "";
			passwordNew = "";
			passwordConfirm = "";
		} catch (err) {
			console.error(err);
			passwordMessage = "Failed to change password.";
		}
	}

	// -----------------------------
	// IFC Admin: Chapters
	// -----------------------------
	async function loadChapters() {
		if (!token || !isIFCAdmin()) return;
		loadingChapters = true;
		try {
			const res = await api("/admin/chapters", { method: "GET" });
			if (!res.ok) {
				let body = {};
				try {
					body = await res.json();
				} catch (e) {}
				console.error(
					"Failed to load chapters",
					"status:",
					res.status,
					"body:",
					body,
				);
				loadingChapters = false;
				return;
			}
			const data = await res.json();
			chapters = data.chapters || [];
		} catch (err) {
			console.error("loadChapters error:", err);
		} finally {
			loadingChapters = false;
		}
	}

	function selectChapter(ch) {
		selectedChapter = ch;
		creatingNew = false;
		showChapterPassword = false;
		if (!ch) return;
		editingUsername = ch.username;
		editingName = ch.name || ch.fratName || ch.username;
		editingAddress = ch.fratAddress || "";

		const phones = bestEffortEmergencyPhonesFromUser(ch);
		editingContacts = ensureEditableContactList(ch?.contacts || []);

		editingEnabled = ch.enabled !== false;
		editingCrestKey = ch.crestKey || "";
		editingPassword = "";
		chapterMessage = "";
	}

	async function handleCrestFileChange(event) {
		const file = event.target.files && event.target.files[0];
		if (!file || !selectedChapter) return;

		crestUploadBusy = true;
		crestUploadError = "";
		try {
			const ext = (file.name.split(".").pop() || "png").toLowerCase();

			const res = await api(
				`/admin/chapters/${selectedChapter.username}/crest-upload-url`,
				{
					method: "POST",
					body: JSON.stringify({ ext }),
				},
			);
			if (!res.ok) {
				const e = await res.json().catch(() => ({}));
				crestUploadError = e.error || "Failed to get upload URL.";
				crestUploadBusy = false;
				return;
			}
			const { uploadUrl, key } = await res.json();

			const putRes = await fetch(uploadUrl, {
				method: "PUT",
				headers: { "Content-Type": file.type || "image/png" },
				body: file,
			});
			if (!putRes.ok) {
				crestUploadError = "Upload to S3 failed.";
				crestUploadBusy = false;
				return;
			}

			const saveRes = await api(
				`/admin/chapters/${selectedChapter.username}`,
				{
					method: "PUT",
					body: JSON.stringify({ crestKey: key }),
				},
			);
			if (!saveRes.ok) {
				const e = await saveRes.json().catch(() => ({}));
				crestUploadError = e.error || "Failed to save crest key.";
				crestUploadBusy = false;
				return;
			}
			const data = await saveRes.json();
			const updated = data.user;

			chapters = chapters.map((c) =>
				c.username === updated.username ? updated : c,
			);
			selectedChapter = updated;
			editingCrestKey = updated.crestKey || "";
			crestUploadError = "";
		} catch (err) {
			console.error(err);
			crestUploadError = "Crest upload failed.";
		} finally {
			crestUploadBusy = false;
			event.target.value = "";
		}
	}

	function startNewChapter() {
		selectedChapter = null;
		creatingNew = true;
		showChapterPassword = false;
		editingUsername = "";
		editingName = "";
		editingAddress = "";
		editingEmergencyPhones = [""];
		editingEnabled = true;
		editingCrestKey = "";
		editingPassword = "";
		chapterMessage = "";
	}

	async function saveChapter() {
		chapterMessage = "";
		chapterSaving = true;

		const cleanContacts = sanitizeContacts(editingContacts);
		const derivedPhones = deriveEmergencyPhonesFromContacts(cleanContacts);

		try {
			if (creatingNew) {
				const body = {
					username: editingUsername.trim(),
					name: editingName.trim(),
					fratAddress: editingAddress.trim(),
					contacts: cleanContacts,
					emergencyPhones: derivedPhones,
					password: editingPassword,
					crestKey: editingCrestKey.trim() || null,
				};

				const res = await api("/admin/chapters", {
					method: "POST",
					body: JSON.stringify(body),
				});

				if (!res.ok) {
					const e = await res.json().catch(() => ({}));
					chapterMessage = e.error || "Failed to create chapter.";
					chapterSaving = false;
					return;
				}
			} else if (selectedChapter) {
				const body = {
					name: editingName.trim(),
					fratAddress: editingAddress.trim(),
					crestKey: editingCrestKey.trim() || null,
					enabled: editingEnabled,
					contacts: cleanContacts,
					emergencyPhones: derivedPhones,
				};

				if (editingPassword) body.password = editingPassword;

				const res = await api(
					`/admin/chapters/${selectedChapter.username}`,
					{
						method: "PUT",
						body: JSON.stringify(body),
					},
				);

				if (!res.ok) {
					const e = await res.json().catch(() => ({}));
					chapterMessage = e.error || "Failed to save chapter.";
					chapterSaving = false;
					return;
				}
			}

			await loadChapters();

			if (selectedChapter) {
				const next = chapters.find(
					(c) => c.username === selectedChapter.username,
				);
				if (next) {
					selectedChapter = next;
					editingContacts = ensureEditableContactList(
						next.contacts || [],
					);
				}
			}

			// If you changed a chapter name, refresh events + re-sync BuzzCrew
			await loadEvents();
			await syncBuzzEventsFromBackend();

			chapterMessage = "Saved.";
		} catch (err) {
			console.error(err);
			chapterMessage = "Error saving chapter.";
		} finally {
			chapterSaving = false;
		}
	}

	async function deleteChapter() {
		if (!selectedChapter) return;
		if (!confirm(`Delete chapter ${selectedChapter.username}?`)) return;
		chapterSaving = true;
		chapterMessage = "";
		try {
			const res = await api(
				`/admin/chapters/${selectedChapter.username}`,
				{ method: "DELETE" },
			);
			if (!res.ok) {
				const e = await res.json().catch(() => ({}));
				chapterMessage = e.error || "Failed to delete chapter.";
				chapterSaving = false;
				return;
			}
			selectedChapter = null;
			creatingNew = false;
			await loadChapters();
		} catch (err) {
			console.error(err);
			chapterMessage = "Error deleting chapter.";
		} finally {
			chapterSaving = false;
		}
	}

	// -----------------------------
	// IFC Admin: Pending requests
	// -----------------------------
	async function loadSignupRequests() {
		if (!token || !isIFCAdmin()) return;
		loadingSignupRequests = true;
		signupRequestsError = "";
		try {
			const res = await api("/admin/signup-requests", { method: "GET" });
			if (!res.ok) {
				const e = await res.json().catch(() => ({}));
				signupRequestsError = e.error || "Failed to load requests.";
				loadingSignupRequests = false;
				return;
			}
			const data = await res.json();
			signupRequests = data.requests || [];
		} catch (err) {
			console.error(err);
			signupRequestsError = "Failed to load requests.";
		} finally {
			loadingSignupRequests = false;
		}
	}
	async function loadEventRequests() {
		if (!token || !isIFCAdmin()) return;
		loadingEventRequests = true;
		eventRequestsError = "";
		try {
			const res = await api("/admin/event-requests", { method: "GET" });
			if (!res.ok) {
				const e = await res.json().catch(() => ({}));
				eventRequestsError =
					e.error || "Failed to load event requests.";
				loadingEventRequests = false;
				return;
			}
			const data = await res.json();
			eventRequests = data.requests || [];
		} catch (err) {
			console.error(err);
			eventRequestsError = "Failed to load event requests.";
		} finally {
			loadingEventRequests = false;
		}
	}
	function formatMaybeTimestamp(ts) {
		if (!ts) return "";

		// Firestore Timestamp instance (if ever passed through)
		if (ts && typeof ts.toDate === "function") {
			const d = ts.toDate();
			return d instanceof Date && !Number.isNaN(d.getTime())
				? d.toLocaleString()
				: "";
		}

		// Plain Date
		if (ts instanceof Date) {
			return !Number.isNaN(ts.getTime()) ? ts.toLocaleString() : "";
		}

		// ISO string / number millis
		if (typeof ts === "string" || typeof ts === "number") {
			const d = new Date(ts);
			return !Number.isNaN(d.getTime()) ? d.toLocaleString() : "";
		}

		// Firestore Timestamp serialized forms
		if (typeof ts === "object") {
			const sec =
				typeof ts.seconds === "number"
					? ts.seconds
					: typeof ts._seconds === "number"
						? ts._seconds
						: null;

			const nsec =
				typeof ts.nanoseconds === "number"
					? ts.nanoseconds
					: typeof ts._nanoseconds === "number"
						? ts._nanoseconds
						: 0;

			if (sec !== null) {
				const ms = sec * 1000 + Math.floor(nsec / 1e6);
				const d = new Date(ms);
				return !Number.isNaN(d.getTime()) ? d.toLocaleString() : "";
			}
		}

		return "";
	}

	async function approveSignupRequest(id) {
		if (!confirm("Approve this request and email the chapter password?"))
			return;
		try {
			const res = await api(`/admin/signup-requests/${id}/approve`, {
				method: "POST",
			});
			const body = await res.json().catch(() => ({}));
			if (!res.ok) {
				alert(body.error || "Failed to approve request.");
				return;
			}
			await loadSignupRequests();
			await loadChapters();
		} catch (err) {
			console.error(err);
			alert("Failed to approve request.");
		}
	}
		function armApproveEventRequest(id) {
		confirmApproveEventRequestId = id;
	}
	function cancelApproveEventRequest() {
		confirmApproveEventRequestId = "";
	}

	async function approveEventRequest(id) {
		try {
			const res = await api(`/admin/event-requests/${id}/approve`, {
				method: "POST",
			});
			const body = await res.json().catch(() => ({}));
			if (!res.ok) {
				alert(body.error || "Failed to approve event.");
				return;
			}
			confirmApproveEventRequestId = "";
			await loadEventRequests();
			await loadEvents();
		} catch (err) {
			console.error(err);
			alert("Failed to approve event.");
		}
	}


	async function denyEventRequest(id) {
		if (!confirm("Deny this event?")) return;
		try {
			const res = await api(`/admin/event-requests/${id}/deny`, {
				method: "POST",
			});
			const body = await res.json().catch(() => ({}));
			if (!res.ok) {
				alert(body.error || "Failed to deny event.");
				return;
			}
			await loadEventRequests();
		} catch (err) {
			console.error(err);
			alert("Failed to deny event.");
		}
	}
	async function denySignupRequest(id) {
		if (!confirm("Deny this request?")) return;
		try {
			const res = await api(`/admin/signup-requests/${id}/deny`, {
				method: "POST",
			});
			const body = await res.json().catch(() => ({}));
			if (!res.ok) {
				alert(body.error || "Failed to deny request.");
				return;
			}
			await loadSignupRequests();
		} catch (err) {
			console.error(err);
			alert("Failed to deny request.");
		}
	}
</script>

<div class={`app ${isDark ? "dark" : "light"}`}>
	<header class="navbar">
		<div class="navbar-inner">
			<div class="navbar-left">
				<div class="navbar-logo-wrap">
					<img
						src="/favicon.png"
						alt="CU Boulder"
						class="navbar-logo"
					/>
				</div>
				<div class="navbar-titles">
					<div class="navbar-title-main">CU Boulder</div>
					<div class="navbar-title-sub">CU EventHub™</div>
				</div>
			</div>

			<div class="navbar-right">
				<div class="nav-help">
					<button
						class="icon-button"
						on:click={toggleContactPopover}
						aria-label="Help">?</button
					>
					{#if showContactPopover}
						<div class="help-popover">
							<div
								class="help-popover-title"
								style="color:black;"
							>
								Contact Us
							</div>
							<a
								href="mailto:tech@coloradoifc.org"
								class="help-popover-link"
							>
								tech@coloradoifc.org
							</a>
						</div>
					{/if}
				</div>

				{#if currentUser}
					<div class="user-menu">
						<button class="user-pill" on:click={toggleUserMenu}>
							<span class="user-pill-initials">
								{#if currentUser.role === "neighbor"}
									{currentUser.name
										? currentUser.name
												.charAt(0)
												.toUpperCase()
										: "N"}
								{:else if currentUser.fratName}
									{currentUser.fratName
										.charAt(0)
										.toUpperCase()}
								{:else if currentUser.username}
									{currentUser.username
										.charAt(0)
										.toUpperCase()}
								{:else}
									?
								{/if}
							</span>
							<CrestImage
								username={currentUser.username}
								fratName={currentUser.fratName}
								className="user-pill-crest"
							/>
						</button>
						{#if showUserMenu}
							<div class="user-menu-dropdown">
								<div class="user-menu-header">
									<div class="user-menu-name">
										{currentUser.fratName ||
											currentUser.username ||
											currentUser.name}
									</div>
									<div class="user-menu-role">
										{currentUser.role === "chapter"
											? "Chapter"
											: currentUser.role === "police"
												? "Police"
												: currentUser.role ===
													  "ifc_admin"
													? "IFC Admin"
													: currentUser.role}
									</div>
								</div>
								<button
									class="user-menu-item"
									on:click={goAccount}>Profile</button
								>
								<button class="user-menu-item" on:click={logout}
									>Log Out</button
								>
							</div>
						{/if}
					</div>
				{/if}
			</div>
		</div>
	</header>

	<main class="main">
		{#if !currentUser}
			<div class="login-container">
				<div class="login-grid">
					<!-- Fraternity Login -->
					<div class="login-card">
						<h3>Fraternity Login</h3>
						<div class="field">
							<label>Username</label>
							<input
								class="input"
								placeholder="Chapter username"
								bind:value={loginUsername}
							/>
						</div>
						<div class="field">
							<label>Password</label>
							<input
								type="password"
								class="input"
								bind:value={loginPassword}
							/>
						</div>
						{#if loginError && !neighborEmail}
							<div class="login-error">{loginError}</div>
						{/if}
						<div
							style="display:flex; justify-content:center; gap:10px; align-items:center; margin-top:8px;"
						>
							<button class="button primary" on:click={login}
								>Sign In</button
							>
							<button
								class="button primary"
								type="button"
								on:click={useDemo}>Use demo</button
							>
						</div>
						<div
							style="display:flex; justify-content:center; margin-top:12px;"
						>
							<button
								class="button small"
								type="button"
								on:click={openSignupModal}
							>
								Request access for your chapter
							</button>
						</div>
					</div>

					<!-- Neighbor Login -->
					<div class="login-card">
						<h3>Neighbor Login</h3>
						<div class="field">
							<label>Email</label>
							<input
								class="input"
								type="email"
								placeholder="your@email.com"
								bind:value={neighborEmail}
							/>
						</div>
						<div class="field">
							<label>Password</label>
							<input
								type="password"
								class="input"
								bind:value={neighborPassword}
							/>
						</div>
						{#if loginError && neighborEmail}
							<div class="login-error">{loginError}</div>
						{/if}
						<div
							style="display:flex; justify-content:center; margin-top:8px;"
						>
							<button
								class="button primary"
								on:click={loginNeighbor}>Sign In</button
							>
						</div>
						<div
							style="display:flex; justify-content:center; margin-top:12px;"
						>
							<button
								class="button small"
								type="button"
								on:click={openNeighborSignupModal}
							>
								Register as neighbor
							</button>
						</div>
					</div>
				</div>
			</div>
		{:else}
			{#if !isNeighbor()}
				<div class="toolbar">
					<div class="toolbar-left">
						<button
							class={`toolbar-tab ${activePage === "dashboard" ? "toolbar-tab-active" : ""}`}
							on:click={goDashboard}
						>
							Events
						</button>

						{#if isIFCAdmin()}
							<button
								class={`toolbar-tab ${activePage === "buzzcrew" ? "toolbar-tab-active" : ""}`}
								on:click={goBuzzCrew}
							>
								BuzzCrew
							</button>
						{/if}

						<button
							class={`toolbar-tab ${activePage === "account" ? "toolbar-tab-active" : ""}`}
							on:click={goAccount}
						>
							Profile
						</button>

						{#if isChapter()}
							<button
								class={`toolbar-tab ${activePage === "chapter-complaints" ? "toolbar-tab-active" : ""}`}
								on:click={goChapterComplaints}
							>
								Complaints
								{#if pendingChapterComplaintsCount > 0}
									<span
										class="badge"
										style="margin-left:8px;"
									>
										{pendingChapterComplaintsCount}
									</span>
								{/if}
							</button>
						{/if}

						{#if isIFCAdmin()}
							<button
								class={`toolbar-tab ${activePage === "chapters" ? "toolbar-tab-active" : ""}`}
								on:click={() => {
									activePage = "chapters";
									loadChapters();
									loadSignupRequests();
								}}
							>
								Chapters
							</button>

							<button
								class={`toolbar-tab ${activePage === "event-requests" ? "toolbar-tab-active" : ""}`}
								on:click={() => {
									activePage = "event-requests";
									loadEventRequests();
								}}
							>
								Event Requests
								{#if pendingEventRequestsCount > 0}
									<span
										class="badge"
										style="margin-left:8px;"
									>
										{pendingEventRequestsCount}
									</span>
								{/if}
							</button>

							<button
								class={`toolbar-tab ${activePage === "requests" ? "toolbar-tab-active" : ""}`}
								on:click={() => {
									goRequests();
									loadSignupRequests();
								}}
							>
								Emergency Contact Requests
								{#if pendingSignupRequestsCount > 0}
									<span
										class="badge"
										style="margin-left:8px;"
									>
										{pendingSignupRequestsCount}
									</span>
								{/if}
							</button>

							<button
								class={`toolbar-tab ${activePage === "admin-complaints" ? "toolbar-tab-active" : ""}`}
								on:click={goAdminComplaints}
							>
								Complaints
								{#if pendingAdminComplaintsCount > 0}
									<span
										class="badge"
										style="margin-left:8px;"
									>
										{pendingAdminComplaintsCount}
									</span>
								{/if}
							</button>

							<button
								class={`toolbar-tab ${activePage === "admin-neighbors" ? "toolbar-tab-active" : ""}`}
								on:click={goAdminNeighbors}
							>
								Neighbors
							</button>
						{/if}
					</div>

					{#if (activePage === "dashboard" && isChapter()) || isIFCAdmin()}
						<div class="toolbar-right">
							{#if activePage === "dashboard" && isChapter()}
								<button
									class="button small"
									on:click={openNewEventModal}
									>+ New Event</button
								>
							{/if}

							{#if isIFCAdmin()}
								<button
									class="button small"
									type="button"
									on:click={openAccessModal}
								>
									Who can view what?
								</button>
							{/if}
						</div>
					{/if}
				</div>
			{:else if isNeighbor()}
				<div class="toolbar">
					<div class="toolbar-left">
						<button
							class={`toolbar-tab ${activePage === "dashboard" ? "toolbar-tab-active" : ""}`}
							on:click={goDashboard}
						>
							Fraternities
						</button>

						<button
							class={`toolbar-tab ${activePage === "account" ? "toolbar-tab-active" : ""}`}
							on:click={goAccount}
						>
							Profile
						</button>
					</div>
				</div>
			{/if}
			{#if activePage === "account"}
				{#if isNeighbor()}
					<!-- FIND this whole Neighbor Account <section> ... </section> and replace it with: -->
					<section class="panel account-panel">
						<h3>Neighbor Account</h3>
						<div class="account-grid">
							<div>
								<h4>Profile information</h4>

								<div class="field">
									<label>Name</label>
									<input
										class="input"
										bind:value={neighborName}
										placeholder="First Last"
									/>
								</div>

								<div class="field">
									<label>Email</label>
									<input
										class="input"
										value={currentUser?.email || ""}
										readonly
									/>
									<div class="help-text">
										Email is locked to your login.
									</div>
								</div>

								<div class="field">
									<label>Phone</label>
									<input
										class="input"
										bind:value={neighborPhone}
										placeholder="+13035551234"
									/>
								</div>

								<div class="field">
									<label>Address</label>
									<textarea
										class="input"
										rows="3"
										bind:value={neighborAddress}
									></textarea>
								</div>

								<button
									class="button small"
									on:click={saveNeighborAccount}
									disabled={neighborSaving}
								>
									{neighborSaving
										? "Saving..."
										: "Save profile"}
								</button>
							</div>

							<div>
								<h4>Change password</h4>
								<div class="field">
									<label>Current password</label>
									<input
										type="password"
										class="input"
										bind:value={passwordCurrent}
									/>
								</div>
								<div class="field">
									<label>New password</label>
									<input
										type="password"
										class="input"
										bind:value={passwordNew}
									/>
								</div>
								<div class="field">
									<label>Confirm new password</label>
									<input
										type="password"
										class="input"
										bind:value={passwordConfirm}
									/>
								</div>
								{#if passwordMessage}
									<div class="help-text">
										{passwordMessage}
									</div>
								{/if}
								<button
									class="button small"
									on:click={changePassword}
									>Update password</button
								>
							</div>
						</div>

						{#if neighborMessage}
							<div class="help-text account-message">
								{neighborMessage}
							</div>
						{/if}
					</section>
				{:else}
					<section class="panel account-panel">
						<h3>Fraternity Account</h3>
						<div class="account-grid">
							<div>
								<h4>Chapter Information</h4>
								<div class="field">
									<label>Chapter name</label>
									<input
										class="input"
										bind:value={accountFratName}
									/>
								</div>

								<div class="field">
									<label>Residence address</label>
									<textarea
										class="input"
										rows="3"
										bind:value={accountAddress}
									></textarea>

									<button
										class="button small"
										on:click={saveAddress}
										>Save address</button
									>
								</div>

								<div class="field">
									<label>Chapter exec contacts</label>
									<div class="help-text">
										Phones from these contacts are used as
										the chapter's emergency phones (first {MAX_EMERGENCY_PHONES}
										unique).
									</div>

									<div
										style="margin-top:10px; overflow-x:auto;"
									>
										<table
											class="restrictions-table"
											style="width:100%;"
										>
											<thead>
												<tr>
													<th>Name</th>
													<th>Position</th>
													<th>Email</th>
													<th>Phone</th>
													<th></th>
												</tr>
											</thead>
											<tbody>
												{#each accountContacts as c, idx (idx)}
													<tr>
														<td
															><input
																class="input"
																bind:value={
																	accountContacts[
																		idx
																	].fullName
																}
																placeholder="First Last"
															/></td
														>
														<td
															><input
																class="input"
																bind:value={
																	accountContacts[
																		idx
																	].position
																}
																placeholder="Risk Manager"
															/></td
														>
														<td
															><input
																class="input"
																bind:value={
																	accountContacts[
																		idx
																	].email
																}
																placeholder="name@example.com"
															/></td
														>
														<td
															><input
																class="input"
																bind:value={
																	accountContacts[
																		idx
																	].phone
																}
																placeholder="+13035551234"
															/></td
														>
														<td
															style="white-space:nowrap;"
														>
															<button
																class="button small"
																type="button"
																disabled={accountContacts.length <=
																	1}
																on:click={() =>
																	removeContactRow(
																		(v) =>
																			(accountContacts =
																				v),
																		() =>
																			accountContacts,
																		idx,
																	)}
															>
																×
															</button>
														</td>
													</tr>
												{/each}
											</tbody>
										</table>
									</div>

									<div style="margin-top:10px;">
										<button
											class="button small"
											type="button"
											on:click={() =>
												addContactRow(
													(v) =>
														(accountContacts = v),
													() => accountContacts,
												)}
											disabled={accountContacts.length >=
												MAX_CONTACTS}
										>
											+ Add contact
										</button>
									</div>
								</div>

								<div class="field">
									<label>Third Party Security Company</label>
									<input
										class="input"
										bind:value={accountSecurityCompany}
										placeholder="Company name (e.g., Crowe Security, Don Juan Security)"
									/>
									<div class="help-text">
										Name of contracted security company
										(required for party registration)
									</div>
								</div>

								<div class="field">
									<label>Risk Management Plan</label>
									<input
										class="input"
										bind:value={accountRiskPolicy}
										placeholder="https://docs.google.com/document/..."
									/>
									<div class="help-text">
										Google Doc to chapter's risk management
										plan (required for party registration)
									</div>
								</div>
								<button
									class="button small"
									on:click={saveAccount}
									disabled={accountSaving}
								>
									{accountSaving
										? "Saving..."
										: "Save chapter info"}
								</button>
							</div>

							<div>
								<h4>Change password</h4>
								<div class="field">
									<label>Current password</label>
									<input
										type="password"
										class="input"
										bind:value={passwordCurrent}
									/>
								</div>
								<div class="field">
									<label>New password</label>
									<input
										type="password"
										class="input"
										bind:value={passwordNew}
									/>
								</div>
								<div class="field">
									<label>Confirm new password</label>
									<input
										type="password"
										class="input"
										bind:value={passwordConfirm}
									/>
								</div>
								{#if passwordMessage}
									<div class="help-text">
										{passwordMessage}
									</div>
								{/if}
								<button
									class="button small"
									on:click={changePassword}
									>Update password</button
								>
							</div>
						</div>

						{#if accountMessage}
							<div class="help-text account-message">
								{accountMessage}
							</div>
						{/if}
					</section>
				{/if}
			{:else if activePage === "event-requests" && isIFCAdmin()}
	<section class="panel">
		<div
			style="display:flex; align-items:center; justify-content:space-between; gap:12px;"
		>
			<h3>Pending Event Requests</h3>
			<button
				class="button small"
				on:click={loadEventRequests}
				disabled={loadingEventRequests}
			>
				{loadingEventRequests ? "Refreshing..." : "Refresh"}
			</button>
		</div>

		{#if eventRequestsError}
			<div class="login-error" style="margin-top:10px;">
				{eventRequestsError}
			</div>
		{/if}

		{#if loadingEventRequests}
			<div class="muted" style="margin-top:10px;">Loading…</div>
		{:else if !eventRequests.length}
			<div class="muted" style="margin-top:10px;">
				No pending event requests.
			</div>
		{:else}
			<div class="event-requests-grid" style="margin-top:12px;">
				{#each eventRequests as r}
					{@const oics = (r.chapterContacts || []).filter((c) =>
						(r.chapterEmergencyPhones || []).some((p) => last10Digits(p) === last10Digits(c?.phone)),
					)}
					{@const otherContacts = (r.chapterContacts || []).filter((c) => !oics.includes(c))}
					<div class="request-card">
						<div class="request-top">
							<div>
								<div class="request-title">
									<span class="pending-pill">PENDING</span>
									{r.fratName}
								</div>
								<div class="request-sub">
									{formatDateOnly(r.startTime)} • {formatTimeRangeLower(
										r.startTime,
										r.endTime,
									)} • {r.type}{#if r.theme} • {r.theme}{/if}
								</div>
							</div>

							<div class="request-actions">
								{#if confirmApproveEventRequestId === r.id}
									<button class="button small" on:click={() => approveEventRequest(r.id)}>
										Confirm?
									</button>
									<button class="button small" on:click={cancelApproveEventRequest}>
										Cancel
									</button>
								{:else}
									<button class="button small" on:click={() => armApproveEventRequest(r.id)}>
										Approve
									</button>
									<button class="button small" on:click={() => denyEventRequest(r.id)}>
										Deny
									</button>
								{/if}
							</div>
						</div>

						<div class="request-kv">
							<div class="kv">
								<div class="k">Date of Event</div>
								<div class="v">{formatDateOnly(r.startTime)}</div>
							</div>

							<div class="kv">
								<div class="k">Hosting Chapter</div>
								<div class="v">{r.fratName}</div>
							</div>

							<div class="kv">
								<div class="k">Event Street Address</div>
								<div class="v">{r.eventAddress || r.fratAddress || "-"}</div>
							</div>

							<div class="kv">
								<div class="k">Start and End Time</div>
								<div class="v">{formatTimeRangeLower(r.startTime, r.endTime)}</div>
							</div>

							<div class="kv">
								<div class="k">Expected Event Capacity</div>
								<div class="v">{r.capacity || "-"}</div>
							</div>

							<div class="kv">
								<div class="k">Out-of-house Organizer / Artist / DJ</div>
								<div class="v">
									{#if r.hasOrganizer}
										Yes{#if r.organizerName} <span class="muted">({r.organizerName})</span>{/if}
									{:else}
										No
									{/if}
								</div>
							</div>

							<div class="kv">
								<div class="k">Officer(s) in Charge (OIC)</div>
								<div class="v">
									{#if oics.length}
										{#each oics as c, idx}
											{c.fullName || "-"}
											{#if c.position}
												<span class="muted"> — ({c.position})</span>
											{/if}
											<span class="muted"> — </span>{formatPhoneForDisplay(c.phone || "")}
											<span class="muted"> — </span>{c.email || "-"}
											{#if idx < oics.length - 1}<br />{/if}
										{/each}
									{:else}
										<span class="muted">No emergency contacts on file.</span>
									{/if}
								</div>
							</div>

							
							<div class="kv">
								<div class="k">Type of event</div>
								<div class="v">{r.type || "-"}</div>
							</div>


							<div class="kv">
								<div class="k">Security Plan Details</div>
								<div class="v">
									{#if r.securityPlanUrl}
										<a href={r.securityPlanUrl} target="_blank" rel="noreferrer">Open document</a>
									{:else}
										<span class="muted">—</span>
									{/if}
								</div>
							</div>

							<div class="kv">
								<div class="k">Third Party Security Company</div>
								<div class="v">{r.securityCompany || "-"}</div>
							</div>

							<div class="kv">
								<div class="k">Name of Person Filling Out Form</div>
								<div class="v">{r.registrantName || "-"}</div>
							</div>

							<div class="kv">
								<div class="k">Contact Info for Person Filling Out Form</div>
								<div class="v">
									{#if r.registrantPhone}{formatPhoneForDisplay(r.registrantPhone)}{/if}
									{#if r.registrantPhone && r.registrantEmail}<br />{/if}
									{#if r.registrantEmail}{r.registrantEmail}{/if}
									{#if !r.registrantPhone && !r.registrantEmail}<span class="muted">—</span>{/if}
								</div>
							</div>

							<div class="kv">
								<div class="k">Submitted</div>
								<div class="v">{formatMaybeTimestamp(r.createdAt)}</div>
							</div>
						</div>
					</div>
				{/each}
			</div>
		{/if}
	</section>
			{:else if activePage === "requests" && isIFCAdmin()}
				<section class="panel">
					<div
						style="display:flex; align-items:center; justify-content:space-between; gap:12px;"
					>
						<h3>Pending Emergency Contact Addition Requests</h3>
						<button
							class="button small"
							on:click={loadSignupRequests}
							disabled={loadingSignupRequests}
						>
							{loadingSignupRequests
								? "Refreshing..."
								: "Refresh"}
						</button>
					</div>

					{#if signupRequestsError}
						<div class="login-error" style="margin-top:10px;">
							{signupRequestsError}
						</div>
					{/if}

					{#if loadingSignupRequests}
						<div class="muted" style="margin-top:10px;">
							Loading…
						</div>
					{:else if !signupRequests.length}
						<div class="muted" style="margin-top:10px;">
							No pending requests.
						</div>
					{:else}
						<div
							style="margin-top:12px; display:flex; flex-direction:column; gap:10px;"
						>
							{#each signupRequests as r}
								<div
									class="panel"
									style="padding:14px; border-radius:14px;"
								>
									<div
										style="display:flex; align-items:flex-start; justify-content:space-between; gap:12px;"
									>
										<div>
											<div style="font-weight:600;">
												{r.fratName}
											</div>
											<div
												class="muted"
												style="margin-top:4px;"
											>
												{r.fullName} • {r.position ||
													"Member"} • {r.email} • {r.phone}
											</div>
											<div
												class="muted"
												style="margin-top:4px;"
											>
												Submitted: {formatMaybeTimestamp(
													r.createdAt,
												)}
											</div>
										</div>

										<div
											style="display:flex; gap:8px; flex-wrap:wrap; justify-content:flex-end;"
										>
											<button
												class="button small"
												on:click={() =>
													approveSignupRequest(r.id)}
											>
												Approve
											</button>
											<button
												class="button small"
												on:click={() =>
													denySignupRequest(r.id)}
											>
												Deny
											</button>
										</div>
									</div>
									<div
										class="help-text"
										style="margin-top:10px;"
									>
										Approving adds this user to the
										chapter’s exec contacts and adds their
										phone into the chapter’s emergency phone
										list (up to {MAX_EMERGENCY_PHONES}).
									</div>
								</div>
							{/each}
						</div>
					{/if}
				</section>
			{:else if activePage === "admin-complaints" && isIFCAdmin()}
	<section class="panel">
		<div
			style="display:flex; align-items:center; justify-content:space-between; gap:12px;"
		>
			<h3>All Neighbor Complaints</h3>
			<button
				class="button small"
				on:click={loadAllComplaints}
			>
				{loadingComplaints ? "Refreshing..." : "Refresh"}
			</button>
		</div>

		{#if loadingComplaints}
			<div class="muted" style="margin-top:10px;">
				Loading...
			</div>
		{:else if allComplaints.length === 0}
			<div class="muted" style="margin-top:10px;">
				No complaints filed yet.
			</div>
		{:else}
			<div style="display:grid; gap:14px; margin-top:16px;">
				{#each allComplaints as complaint}
					<div
						class="panel"
						style="padding:16px; border-radius:14px; cursor:pointer;"
						on:click={() =>
							openComplaintDetail(complaint)}
					>
						<div
							style="display:flex; justify-content:space-between; align-items:flex-start; gap:12px;"
						>
							<div style="flex:1;">
								<div
									style="font-weight:700; font-size:15px;"
								>
									{complaint.neighborName} → {complaint.chapterName}
								</div>
								<div
									class="muted"
									style="margin-top:4px; font-size:13px;"
								>
									{complaint.neighborPhone} • {complaint.neighborEmail}
								</div>
								<div
									class="muted"
									style="margin-top:2px; font-size:13px;"
								>
									Filed: {formatDateTime(
										complaint.createdAt,
									)}
								</div>

								<div
									style="margin-top:10px; font-size:14px; line-height:1.5;"
								>
									{complaint.complaintText}
								</div>

								{#if complaint.adminNotes}
									<div
										style="margin-top:12px; padding:12px; background:#dbeafe; border-radius:10px; font-size:13px;"
									>
										<div
											style="font-weight:700; margin-bottom:4px;"
										>
											Admin Notes:
										</div>
										{complaint.adminNotes}
									</div>
								{/if}

								<div style="margin-top:12px;">
									<button
										class="button small"
										on:click={(e) => {
											e.stopPropagation();
											openComplaintDetail(complaint);
										}}
									>
										{complaint.status === "resolved" ? "Update Notes" : "Add Notes & Resolve"}
									</button>
								</div>
							</div>

							<span
								class={`badge ${complaint.status === "resolved" ? "badge-muted" : ""}`}
							>
								{complaint.status}
							</span>
						</div>
					</div>
				{/each}
			</div>
		{/if}
	</section>
			{:else if activePage === "admin-neighbors" && isIFCAdmin()}
				<section class="panel">
					<h3>Registered Neighbors</h3>

					{#if loadingNeighbors}
						<div class="muted" style="margin-top:10px;">
							Loading...
						</div>
					{:else if allNeighbors.length === 0}
						<div class="muted" style="margin-top:10px;">
							No neighbors registered yet.
						</div>
					{:else}
						<div style="display:grid; gap:14px; margin-top:16px;">
							{#each allNeighbors as neighbor}
								<div
									class="panel"
									style="padding:16px; border-radius:14px;"
								>
									<div
										style="display:flex; justify-content:space-between; align-items:flex-start; gap:12px;"
									>
										<div style="flex:1;">
											<div
												style="font-weight:700; font-size:15px;"
											>
												{neighbor.name}
												{#if !neighbor.enabled}
													<span
														class="badge badge-muted"
														style="margin-left:8px;"
														>Disabled</span
													>
												{/if}
											</div>
											<div
												class="muted"
												style="margin-top:4px; font-size:13px;"
											>
												{neighbor.email} • {neighbor.phone}
											</div>
											<div
												class="muted"
												style="margin-top:2px; font-size:13px;"
											>
												{neighbor.address}
											</div>
											<div style="margin-top:8px;">
												<span class="badge"
													>Total complaints: {neighbor.totalComplaints ||
														0}</span
												>
											</div>
										</div>

										<div
											style="display:flex; gap:8px; flex-direction:column;"
										>
											<button
												class="button small"
												on:click={() =>
													openNeighborProfile(
														neighbor.id,
													)}
											>
												View Profile
											</button>
											<button
												class="button small"
												on:click={() =>
													toggleNeighborEnabled(
														neighbor.id,
														neighbor.enabled,
													)}
											>
												{neighbor.enabled
													? "Disable"
													: "Enable"}
											</button>
										</div>
									</div>
								</div>
							{/each}
						</div>
					{/if}
				</section>
			{:else if activePage === "chapters" && isIFCAdmin()}
				<section class="event-layout">
					<div class="panel event-list-panel">
						<div class="event-list-header">
							<h3>Chapters</h3>
							<button
								class="button small"
								on:click={startNewChapter}>+ New Chapter</button
							>
						</div>

						{#if loadingChapters}
							<div class="muted">Loading chapters…</div>
						{:else if !chapters.length}
							<div class="muted">No chapters found.</div>
						{:else}
							<div class="chapter-search">
								<input
									class="input"
									type="text"
									placeholder="Search chapters by name…"
									bind:value={chapterSearch}
								/>
							</div>

							{#if filteredChapters.length === 0}
								<div class="muted">
									No chapters match “{chapterSearch}”.
								</div>
							{:else}
								<div class="event-list">
									{#each filteredChapters as ch}
										<button
											class={`event-row ${
												selectedChapter &&
												selectedChapter.username ===
													ch.username
													? "event-row-selected"
													: ""
											}`}
											on:click={() => selectChapter(ch)}
										>
											<div class="event-row-main">
												<div class="event-row-left">
													{#if ch.crestUrl}
														<img
															src={ch.crestUrl}
															alt={(ch.fratName ||
																ch.username) +
																" crest"}
															class="event-row-crest"
														/>
													{/if}
													<div class="event-row-text">
														<div
															class="event-row-title"
														>
															{ch.name ||
																ch.fratName ||
																ch.username}

															{#if ch.enabled === false}
																<span
																	class="badge badge-muted"
																	>Disabled</span
																>
															{/if}
														</div>
														<div
															class="event-row-meta"
														>
															<span class="muted"
																>{ch.fratAddress ||
																	"Address not set"}</span
															>
														</div>
														<div
															class="chapter-stats"
														>
															<span class="badge"
																>Events: {ch
																	.stats
																	?.events ??
																	0}</span
															>
															<span class="badge"
																>Warnings: {ch
																	.stats
																	?.warnings ??
																	0}</span
															>
															<span class="badge"
																>Noise: {ch
																	.stats
																	?.noise ??
																	0}</span
															>
															<span class="badge"
																>Nuisance: {ch
																	.stats
																	?.nuisance ??
																	0}</span
															>
														</div>
													</div>
												</div>
											</div>
										</button>
									{/each}
								</div>
							{/if}
						{/if}
					</div>

					<div class="panel event-detail-panel">
						{#if creatingNew}
							<h3>Create Chapter</h3>
						{:else if selectedChapter}
							<h3>Chapter Details</h3>

							{#if editingEnabled === false}
								<div class="chapter-warning-banner">
									<span class="chapter-warning-dot" />
									<span>
										This chapter is currently disabled and
										cannot register new events until it is
										re-enabled.
									</span>
								</div>
							{/if}
						{:else}
							<div class="muted">
								Select a chapter on the left or create a new
								one.
							</div>
						{/if}

						{#if creatingNew || selectedChapter}
							<div class="chapter-detail-form">
								<div class="chapter-detail-row2">
									<div class="field">
										<label>Username</label>
										{#if creatingNew}
											<input
												class="input"
												bind:value={editingUsername}
											/>
										{:else}
											<input
												class="input input-readonly"
												value={editingUsername}
												readonly
												disabled
											/>
										{/if}
									</div>

									<div class="field">
										<label>Chapter name</label>
										<input
											class="input"
											bind:value={editingName}
										/>
									</div>
								</div>

								<div class="field">
									<label>Residence address</label>
									<div class="address-input-wrap">
										<textarea
											class="input"
											rows="2"
											bind:value={editingAddress}
											on:input={(e) => {
												editingAddress = e.target.value;
												clearTimeout(
													addressSearchTimeout,
												);
												addressSearchTimeout =
													setTimeout(
														() =>
															fetchAddressSuggestions(
																editingAddress,
															),
														150,
													);
											}}
											placeholder="Start typing an address..."
										></textarea>
										{#if addressSuggestions.length > 0}
											<div class="address-suggestions">
												{#each addressSuggestions as s}
													<button
														type="button"
														class="address-suggestion-row"
														on:click={() => {
															editingAddress =
																s.formatted;
															addressSuggestions =
																[];
														}}
													>
														<div
															class="address-suggestion-main"
														>
															{s.formatted}
														</div>
														<div
															class="address-suggestion-sub"
														>
															{s.context}
														</div>
													</button>
												{/each}
											</div>
										{/if}
									</div>
								</div>

								<div class="field">
									<label>Chapter exec contacts</label>
									<div class="help-text">
										Phones from these contacts are used as
										emergency phones (first {MAX_EMERGENCY_PHONES}
										unique).
									</div>

									<div
										style="margin-top:10px; overflow-x:auto;"
									>
										<table
											class="restrictions-table"
											style="width:100%;"
										>
											<thead>
												<tr>
													<th>Name</th>
													<th>Position</th>
													<th>Email</th>
													<th>Phone</th>
													<th></th>
												</tr>
											</thead>
											<tbody>
												{#each editingContacts as c, idx (idx)}
													<tr>
														<td
															><input
																class="input"
																bind:value={
																	editingContacts[
																		idx
																	].fullName
																}
															/></td
														>
														<td
															><input
																class="input"
																bind:value={
																	editingContacts[
																		idx
																	].position
																}
															/></td
														>
														<td
															><input
																class="input"
																bind:value={
																	editingContacts[
																		idx
																	].email
																}
															/></td
														>
														<td
															><input
																class="input"
																bind:value={
																	editingContacts[
																		idx
																	].phone
																}
															/></td
														>
														<td>
															<button
																class="button small"
																type="button"
																disabled={editingContacts.length <=
																	1}
																on:click={() =>
																	removeContactRow(
																		(v) =>
																			(editingContacts =
																				v),
																		() =>
																			editingContacts,
																		idx,
																	)}
															>
																×
															</button>
														</td>
													</tr>
												{/each}

												{#if pendingPhonesForSelectedChapter.length}
													{#each signupRequests.filter((r) => r.status === "pending" && keyify(r.fratName) === keyify(selectedChapter.name || selectedChapter.fratName || selectedChapter.username)) as r (r.id)}
														<tr
															style="opacity:0.65;"
														>
															<td
																><input
																	class="input"
																	value={r.fullName ||
																		"--"}
																	readonly
																	disabled
																/></td
															>
															<td
																><input
																	class="input"
																	value={r.position ||
																		"Member"}
																	readonly
																	disabled
																/></td
															>
															<td
																><input
																	class="input"
																	value={r.email ||
																		"--"}
																	readonly
																	disabled
																/></td
															>
															<td
																><input
																	class="input"
																	value="pending approval"
																	readonly
																	disabled
																/></td
															>
															<td></td>
														</tr>
													{/each}
												{/if}
											</tbody>
										</table>
									</div>

									<div style="margin-top:10px;">
										<button
											class="button small"
											type="button"
											on:click={() =>
												addContactRow(
													(v) =>
														(editingContacts = v),
													() => editingContacts,
												)}
											disabled={editingContacts.length >=
												MAX_CONTACTS}
										>
											+ Add contact
										</button>
									</div>
								</div>

								<div class="chapter-detail-row3">
									<div class="field">
										<label>Crest key (S3 file name)</label>
										<input
											class="input"
											placeholder="APD_ADMIN.png"
											bind:value={editingCrestKey}
										/>
									</div>

									<div class="field">
										<label
											>{creatingNew
												? "Initial password"
												: "New password (optional)"}</label
										>
										<input
											type="password"
											class="input"
											bind:value={editingPassword}
										/>
									</div>
								</div>

								{#if !creatingNew && selectedChapter}
									<div class="field">
										<label>Current password</label>
										<div
											style="display:flex; gap:8px; align-items:center;"
										>
											<input
												class="input"
												readonly
												value={showChapterPassword
													? selectedChapter.password ||
														""
													: "••••••••••••"}
											/>
											<button
												class="button small"
												type="button"
												on:click={() =>
													(showChapterPassword =
														!showChapterPassword)}
											>
												{showChapterPassword
													? "Hide"
													: "Show"}
											</button>
										</div>
										<div class="help-text">
											Use the "new password" field above
											to change the chapter's password.
										</div>
									</div>
								{/if}

								{#if !creatingNew}
									<div class="field">
										<label>Status</label>
										<label class="switch">
											<input
												type="checkbox"
												bind:checked={editingEnabled}
											/>
											<span>Enabled</span>
										</label>
									</div>
								{/if}

								{#if !creatingNew}
									<div class="field">
										<label>Upload new crest</label>
										<input
											type="file"
											accept="image/*"
											on:change={handleCrestFileChange}
											disabled={crestUploadBusy}
										/>
										{#if crestUploadBusy}
											<div class="help-text">
												Uploading crest…
											</div>
										{/if}
										{#if crestUploadError}
											<div class="login-error">
												{crestUploadError}
											</div>
										{/if}
									</div>
								{/if}

								{#if chapterMessage}
									<div class="help-text">
										{chapterMessage}
									</div>
								{/if}

								<div class="chapter-detail-actions">
									<button
										class="button small"
										on:click={saveChapter}
										disabled={chapterSaving}
									>
										{chapterSaving
											? "Saving…"
											: "Save chapter"}
									</button>

									{#if !creatingNew && selectedChapter}
										<button
											class="button small"
											on:click={deleteChapter}
											disabled={chapterSaving}
										>
											Delete chapter
										</button>
									{/if}
								</div>

								{#if !creatingNew && selectedChapter && selectedChapter.recentEvents && selectedChapter.recentEvents.length}
									<div class="chapter-recent-events">
										<h4>Recent events</h4>
										<div class="chapter-recent-list">
											{#each selectedChapter.recentEvents as ev}
												<div
													class={`chapter-recent-card ${ev.status === "cancelled" ? "chapter-recent-card-cancelled" : ""}`}
												>
													<div
														class="chapter-recent-header"
													>
														<span
															class="chapter-recent-title"
														>
															{#if ev.status === "cancelled"}
																<span
																	class="cancelled-pill"
																	>CANCELLED</span
																>
															{/if}
															{ev.title}
														</span>
														<span
															class={`badge badge-type ${ev.status === "cancelled" ? "badge-cancelled" : ""}`}
														>
															{ev.type}
														</span>
													</div>
													<div
														class="chapter-recent-sub"
													>
														{formatDateTime(
															ev.startTime,
														)}
													</div>
													<div
														class="chapter-recent-stats"
													>
														<span
															>Warnings: {ev.warningCount ||
																0}</span
														>
														<span
															>Noise: {ev.noiseTicketCount ||
																0}</span
														>
														<span
															>Nuisance: {ev.nuisanceTicketCount ||
																0}</span
														>
													</div>
												</div>
											{/each}
										</div>
									</div>
								{/if}
							</div>
						{/if}
					</div>
				</section>
			{:else if activePage === "chapter-complaints"}
				<section class="panel">
					<div
						style="display:flex; align-items:center; justify-content:space-between; gap:12px;"
					>
						<h3>Neighbor Complaints</h3>
						<button
							class="button small"
							on:click={loadChapterComplaints}
						>
							{loadingChapterComplaints
								? "Refreshing..."
								: "Refresh"}
						</button>
					</div>

					{#if loadingChapterComplaints}
						<div class="muted" style="margin-top:10px;">
							Loading...
						</div>
					{:else if chapterComplaints.length === 0}
						<div class="muted" style="margin-top:10px;">
							No complaints received.
						</div>
					{:else}
						<div style="display:grid; gap:14px; margin-top:16px;">
							{#each chapterComplaints as complaint}
								<div
									class="panel"
									style="padding:16px; border-radius:14px;"
								>
									<div
										style="display:flex; justify-content:space-between; align-items:flex-start; gap:12px;"
									>
										<div style="flex:1;">
											<div
												style="font-weight:700; font-size:15px;"
											>
												Name visible only to IFC
											</div>
											<div
												class="muted"
												style="margin-top:4px; font-size:13px;"
											>
												{complaint.neighborPhone}
											</div>
											<div
												class="muted"
												style="margin-top:2px; font-size:13px;"
											>
												Filed: {formatDateTime(
													complaint.createdAt,
												)}
											</div>

											<div
												style="margin-top:10px; font-size:14px; line-height:1.5;"
											>
												{complaint.complaintText}
											</div>

											{#if complaint.adminNotes}
												<div
													style="margin-top:12px; padding:12px; background:#dbeafe; border-radius:10px; font-size:13px;"
												>
													<div
														style="font-weight:700; margin-bottom:4px;"
													>
														IFC Admin Notes:
													</div>
													{complaint.adminNotes}
												</div>
											{/if}
										</div>

										<span
											class={`badge ${complaint.status === "resolved" ? "badge-muted" : ""}`}
										>
											{complaint.status}
										</span>
									</div>
								</div>
							{/each}
						</div>
					{/if}
				</section>
			{:else if activePage === "buzzcrew" && isIFCAdmin()}
				<section class="buzz-sheet-wrap">
					<div class="buzz-sheet-toolbar">
						<div class="buzz-sheet-title">
							<div class="buzz-sheet-h1">Buzz Crew</div>
						</div>

						<div class="buzz-sheet-actions">
							<!-- Saved indicator -->
							{#if buzzSaving}
								<span class="muted">Saving...</span>
							{:else if buzzSaveError}
								<span class="text-error">{buzzSaveError}</span>
							{:else}
								<span class="text-success">Saved</span>
							{/if}

							<!-- Buttons -->
							<button
								class="button small"
								type="button"
								on:click={saveBuzzToBackendNow}
							>
								Save
							</button>
							<button
								class="button small"
								type="button"
								on:click={exportBuzzCrewPDF}
							>
								Download PDF
							</button>

							<button
								class="button small"
								type="button"
								on:click={exportBuzzCrewSpreadsheet}
							>
								Export spreadsheet
							</button>

							<button
								class="button small"
								type="button"
								on:click={syncBuzzEventsFromBackend}
							>
								Re-sync parties
							</button>
						</div>
					</div>

					<div class="buzz-sheet">
						<table class="sheet-table">
							<tbody>
								<!-- HEADER -->
								<tr class="sheet-merge-header">
									<td colspan="12">
										<div class="sheet-merge-header-inner">
											<div>
												<strong
													>{buzzData.weekendLabel}</strong
												>
											</div>
											<div>
												Backup Crew: <strong
													>{buzzData.backupCrew}</strong
												>
											</div>
										</div>
									</td>
								</tr>

								<!-- SCHEDULE SECTION -->
								<tr class="sheet-section-title">
									<td colspan="12"
										><strong
											>Weekend Schedule (Thu / Fri / Sat)</strong
										></td
									>
								</tr>

								<tr class="sheet-colhead">
									<td>Date</td>
									<td>Day</td>
									<td>Chapter</td>
									<td>Event Type</td>
									<td>Title</td>
									<td>Address</td>
									<td>Time</td>
									<td>OIC Position</td>
									<td>OIC Name</td>
									<td>OIC Phone #</td>
									<td>Other OIC</td>
									<td>Other OIC #</td>
								</tr>

								{#each buzzRows as r (r.key)}
									{#if r.kind === "day"}
										<!-- <tr class="sheet-day-row">
											<td colspan="12"
												><strong>{r.label}</strong></td
											>
										</tr> -->
									{:else}
										<tr class="sheet-row">
											<td
												><input
													class="sheet-input"
													value={r.ev.date || ""}
													readonly
												/></td
											>
											<td
												><input
													class="sheet-input"
													value={r.ev.day || ""}
													readonly
												/></td
											>

											<td
												><input
													class="sheet-input"
													value={r.ev.chapter || ""}
													readonly
												/></td
											>
											<!-- <td> Editable chapter names disabled per req.
  <input
    class="sheet-input"
    placeholder="Chapter name"
    bind:value={r.ev.chapter}
    on:blur={queueBuzzSave}
  />
</td> -->

											<td
												><input
													class="sheet-input"
													bind:value={r.ev.eventType}
													on:blur={queueBuzzSave}
												/></td
											>
											<td
												><input
													class="sheet-input"
													bind:value={r.ev.theme}
													on:blur={queueBuzzSave}
												/></td
											>
											<td
												><input
													class="sheet-input"
													bind:value={r.ev.address}
													on:blur={queueBuzzSave}
												/></td
											>
											<td
												><input
													class="sheet-input"
													bind:value={r.ev.time}
													on:blur={queueBuzzSave}
												/></td
											>

											<td
												><input
													class="sheet-input"
													placeholder="President / Risk..."
													bind:value={
														r.ev.oicPosition
													}
													on:blur={queueBuzzSave}
												/></td
											>
											<td
												><input
													class="sheet-input"
													placeholder="Name"
													bind:value={r.ev.oicName}
													on:blur={queueBuzzSave}
												/></td
											>
											<td
												><input
													class="sheet-input"
													placeholder="(###) ###-####"
													value={formatPhoneForDisplay(
														r.ev.oicPhone,
													)}
													on:input={(e) =>
														(r.ev.oicPhone =
															e.currentTarget.value)}
													on:blur={queueBuzzSave}
												/>
											</td>
											<td
												><input
													class="sheet-input"
													placeholder="Name"
													bind:value={
														r.ev.otherOicName
													}
													on:blur={queueBuzzSave}
												/></td
											>
											<td
												><input
													class="sheet-input"
													placeholder="(###) ###-####"
													value={formatPhoneForDisplay(
														r.ev.otherOicPhone,
													)}
													on:input={(e) =>
														(r.ev.otherOicPhone =
															e.currentTarget.value)}
													on:blur={queueBuzzSave}
												/>
											</td>
										</tr>
									{/if}
								{/each}

								{#if !buzzData?.events?.length}
									<tr>
										<td colspan="12" class="sheet-empty"
											>No Thu/Fri/Sat parties found for
											this weekend window.</td
										>
									</tr>
								{/if}

								<!-- IFC CONTACTS -->
								<tr class="sheet-section-gap"
									><td colspan="12"></td></tr
								>
								<tr class="sheet-section-title">
									<td colspan="12"
										><strong
											>Positions and Contact Information
											for Buzz Crew</strong
										></td
									>
								</tr>

								<tr class="sheet-colhead">
									<td colspan="3">Title</td>
									<td colspan="3">Name</td>
									<td colspan="3">Email</td>
									<td colspan="2">Phone #</td>
									<td class="sheet-colhead-action"></td>
								</tr>

								{#each buzzData.ifcContacts as c, idx (idx)}
									<tr>
										<td colspan="3"
											><input
												class="sheet-input"
												placeholder="IFC position name"
												bind:value={
													buzzData.ifcContacts[idx]
														.title
												}
												on:blur={queueBuzzSave}
											/></td
										>
										<td colspan="3"
											><input
												class="sheet-input"
												placeholder="Name"
												bind:value={
													buzzData.ifcContacts[idx]
														.name
												}
												on:blur={queueBuzzSave}
											/></td
										>
										<td colspan="3"
											><input
												class="sheet-input"
												placeholder="email@coloradoifc.org"
												bind:value={
													buzzData.ifcContacts[idx]
														.email
												}
												on:blur={queueBuzzSave}
											/></td
										>
										<td colspan="2"
											><input
												class="sheet-input"
												placeholder="(###) ###-####"
												value={formatPhoneForDisplay(
													buzzData.ifcContacts[idx]
														.phone,
												)}
												on:input={(e) => {
													buzzData.ifcContacts[
														idx
													].phone =
														e.currentTarget.value;
													queueBuzzSave();
												}}
											/>
										</td>
										<td class="sheet-delete-cell">
											<button
												class={`delete-x ${c._confirmDelete ? "confirm" : ""}`}
												type="button"
												on:click={() => {
													if (c._confirmDelete) {
														buzzData.ifcContacts.splice(
															idx,
															1,
														);
														buzzData = {
															...buzzData,
														};
														queueBuzzSave();
													} else {
														buzzData.ifcContacts[
															idx
														]._confirmDelete = true;
														buzzData = {
															...buzzData,
														};
														setTimeout(() => {
															delete buzzData
																.ifcContacts[
																idx
															]._confirmDelete;
															buzzData = {
																...buzzData,
															};
														}, 2500);
													}
												}}
											>
												{c._confirmDelete
													? "Confirm?"
													: "✕"}
											</button>
										</td>
									</tr>
								{/each}

								<tr>
									<td colspan="12" class="sheet-actions-row">
										<button
											class="button small"
											type="button"
											on:click={() => {
												buzzData.ifcContacts = [
													...buzzData.ifcContacts,
													{
														title: "",
														name: "",
														email: "",
														phone: "",
													},
												];
												buzzData = { ...buzzData };
												queueBuzzSave();
											}}
										>
											+ Add IFC position
										</button>
									</td>
								</tr>

								<!-- RESOURCES -->
								<tr class="sheet-section-gap"
									><td colspan="12"></td></tr
								>
								<tr class="sheet-section-title">
									<td colspan="12"
										><strong>Necessary Resources</strong
										></td
									>
								</tr>

								<tr class="sheet-colhead">
									<td colspan="3">Title</td>
									<td colspan="3">Name</td>
									<td colspan="3">Email</td>
									<td colspan="2">Phone #</td>
									<td class="sheet-colhead-action"></td>
								</tr>

								{#each buzzData.resources as r, idx (idx)}
									<tr>
										<td colspan="3">
											<input
												class="sheet-input"
												placeholder="Resource title"
												bind:value={
													buzzData.resources[idx]
														.title
												}
												on:blur={queueBuzzSave}
											/>
										</td>
										<td colspan="3">
											<input
												class="sheet-input"
												placeholder="Name"
												bind:value={
													buzzData.resources[idx].name
												}
												on:blur={queueBuzzSave}
											/>
										</td>
										<td colspan="3">
											<input
												class="sheet-input"
												placeholder="email@domain.com"
												bind:value={
													buzzData.resources[idx]
														.email
												}
												on:blur={queueBuzzSave}
											/>
										</td>
										<td colspan="2">
											<input
												class="sheet-input"
												placeholder="(###) ###-#### / N/A"
												value={formatPhoneForDisplay(
													buzzData.resources[idx]
														.phone,
												)}
												on:input={(e) => {
													buzzData.resources[
														idx
													].phone =
														e.currentTarget.value;
													queueBuzzSave();
												}}
											/>
										</td>

										<td class="sheet-delete-cell">
											<button
												class={`delete-x ${r._confirmDelete ? "confirm" : ""}`}
												type="button"
												on:click={() => {
													if (r._confirmDelete) {
														buzzData.resources.splice(
															idx,
															1,
														);
														buzzData = {
															...buzzData,
														};
														queueBuzzSave();
													} else {
														buzzData.resources[
															idx
														]._confirmDelete = true;
														buzzData = {
															...buzzData,
														};

														setTimeout(() => {
															// guard if row was removed / array changed
															if (
																buzzData
																	?.resources?.[
																	idx
																]
															) {
																delete buzzData
																	.resources[
																	idx
																]
																	._confirmDelete;
																buzzData = {
																	...buzzData,
																};
															}
														}, 2500);
													}
												}}
											>
												{r._confirmDelete
													? "Confirm?"
													: "✕"}
											</button>
										</td>
									</tr>
								{/each}

								<tr>
									<td colspan="12" class="sheet-actions-row">
										<button
											class="button small"
											type="button"
											on:click={() => {
												buzzData.resources = [
													...buzzData.resources,
													{
														title: "",
														name: "",
														email: "",
														phone: "",
													},
												];
												buzzData = { ...buzzData };
												queueBuzzSave();
											}}
										>
											+ Add resource
										</button>
									</td>
								</tr>

								<!-- HOUSES -->
								<tr class="sheet-section-gap"
									><td colspan="12"></td></tr
								>
								<tr class="sheet-section-title">
									<td colspan="12"
										><strong
											>Fraternity House + President
											Contact Information</strong
										></td
									>
								</tr>

								<tr class="sheet-colhead">
									<td colspan="2">Fraternity</td>
									<td>Greek Letters</td>
									<td colspan="2">Fraternity Address</td>
									<td colspan="2">President Name</td>
									<td colspan="2">Email</td>
									<td colspan="2">Phone #</td>
									<td>Liaisons</td>
								</tr>

								<!-- =========================
     HOUSES (with delete/confirm)
     NOTE: keep your existing section header + colheads above this
========================= -->
								{#each buzzData.houses as h, idx (idx)}
									<tr>
										<td colspan="2">
											<input
												class="sheet-input"
												placeholder="Chapter name"
												bind:value={
													buzzData.houses[idx].chapter
												}
												on:blur={queueBuzzSave}
											/>
										</td>
										<td>
											<input
												class="sheet-input"
												placeholder="ΑΒΓ"
												bind:value={
													buzzData.houses[idx]
														.greekLetters
												}
												on:blur={queueBuzzSave}
											/>
										</td>
										<td colspan="2">
											<input
												class="sheet-input"
												placeholder="Address"
												bind:value={
													buzzData.houses[idx].address
												}
												on:blur={queueBuzzSave}
											/>
										</td>
										<td colspan="2">
											<input
												class="sheet-input"
												placeholder="President name"
												bind:value={
													buzzData.houses[idx]
														.presidentName
												}
												on:blur={queueBuzzSave}
											/>
										</td>
										<td colspan="2">
											<input
												class="sheet-input"
												placeholder="president@..."
												bind:value={
													buzzData.houses[idx]
														.presidentEmail
												}
												on:blur={queueBuzzSave}
											/>
										</td>
										<td colspan="2">
											<input
												class="sheet-input"
												placeholder="(###) ###-####"
												value={formatPhoneForDisplay(
													buzzData.houses[idx]
														.presidentPhone,
												)}
												on:input={(e) => {
													buzzData.houses[
														idx
													].presidentPhone =
														e.currentTarget.value;
													queueBuzzSave();
												}}
											/>
										</td>
										<td>
											<div class="sheet-cell-with-action">
												<input
													class="sheet-input"
													placeholder="Liaisons"
													bind:value={
														buzzData.houses[idx]
															.liaisons
													}
													on:blur={queueBuzzSave}
												/>

												<button
													class={`delete-x ${h._confirmDelete ? "confirm" : ""}`}
													type="button"
													on:click={() => {
														if (h._confirmDelete) {
															buzzData.houses.splice(
																idx,
																1,
															);
															buzzData = {
																...buzzData,
															};
															queueBuzzSave();
														} else {
															buzzData.houses[
																idx
															]._confirmDelete =
																true;
															buzzData = {
																...buzzData,
															};

															setTimeout(() => {
																if (
																	buzzData
																		?.houses?.[
																		idx
																	]
																) {
																	delete buzzData
																		.houses[
																		idx
																	]
																		._confirmDelete;
																	buzzData = {
																		...buzzData,
																	};
																}
															}, 2500);
														}
													}}
												>
													{h._confirmDelete
														? "Confirm?"
														: "✕"}
												</button>
											</div>
										</td>
									</tr>
								{/each}

								<tr>
									<td colspan="12" class="sheet-actions-row">
										<button
											class="button small"
											type="button"
											on:click={() => {
												buzzData.houses = [
													...buzzData.houses,
													{
														chapter: "",
														greekLetters: "",
														address: "",
														presidentName: "",
														presidentEmail: "",
														presidentPhone: "",
														liaisons: "",
													},
												];
												buzzData = { ...buzzData };
												queueBuzzSave();
											}}
										>
											+ Add chapter row
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</section>
			{:else if !isNeighbor()}
				<!-- Regular Events Dashboard -->
				<section class="event-layout">
					<div class="panel event-list-panel">
						<div class="event-list-header">
							<h3>Registered Events</h3>
							{#if loadingEvents}
								<span class="muted">Loading…</span>
							{/if}
						</div>

						<div class="event-list">
							{#if events.length === 0}
								<div class="muted">No events yet.</div>
							{:else}
								{#each events as ev}
	<button
		class={`event-row ${
			selectedEvent &&
			selectedEvent.id === ev.id
				? "event-row-selected"
				: ""
		} ${ev.status === "cancelled" || ev.status === "denied" ? "event-row-cancelled" : ""}`}
		on:click={() => selectEvent(ev)}
	>
		<div class="event-row-main">
			<div class="event-row-left">
				<CrestImage
					username={ev.chapterUsername}
					fratName={ev.fratName}
					className="event-row-crest"
				/>
				<div class="event-row-text">
					<div
						class="event-row-title"
					>
						{#if ev.status === "denied"}
							<span
								class="denied-pill"
								>DENIED</span
							>
						{:else if ev.status === "approved"}
							<span
								class="approved-pill"
								>APPROVED</span
							>
						{:else if ev.status === "pending"}
							<span
								class="pending-pill"
								>PENDING</span
							>
						{/if}
						[{ev.fratName ||
							ev.chapterUsername}]
						– {ev.title}
					</div>
					<div class="event-row-meta">
						<span
							class={`badge badge-type ${ev.status === "cancelled" || ev.status === "denied" ? "badge-cancelled" : ""}`}
						>
							{ev.type}
						</span>
						<span
							class={`muted ${ev.status === "cancelled" || ev.status === "denied" ? "muted-cancelled" : ""}`}
						>
							{formatDateTime(
								ev.startTime,
							)}
						</span>
					</div>
				</div>
			</div>
		</div>
	</button>
{/each}
							{/if}
						</div>
					</div>

					<div
	class={`panel event-detail-panel ${selectedEvent && (selectedEvent.status === "cancelled" || selectedEvent.status === "denied") ? "event-detail-cancelled" : ""}`}
>
	{#if selectedEvent}
		<h3>
			Event Details
			{#if selectedEvent && selectedEvent.status === "cancelled"}
				— CANCELLED
			{:else if selectedEvent && selectedEvent.status === "denied"}
				— DENIED BY IFC
			{/if}
		</h3>

							<div class="event-detail-grid">
								<div>
									<div class="detail-label">Chapter</div>
									<div class="detail-value">
										{selectedEvent.fratName ||
											selectedEvent.chapterUsername}
									</div>
								</div>

								<div>
									<div class="detail-label">Event title</div>
									<div class="detail-value">
										{selectedEvent.title}
									</div>
								</div>

								<div>
									<div class="detail-label">Type</div>
									<div class="detail-value capitalize">
										{selectedEvent.type}
									</div>
								</div>

								<div>
									<div class="detail-label">Start</div>
									<div class="detail-value">
										{formatDateTime(
											selectedEvent.startTime,
										)}
									</div>
								</div>

								<div>
									<div class="detail-label">End</div>
									<div class="detail-value">
										{formatDateTime(selectedEvent.endTime)}
									</div>
								</div>

								{#if getChapterContactsForSelectedEvent().length}
									<div
										class="detail-address"
										style="margin-top: 6px;"
									>
										<div class="detail-label">
											Chapter exec contacts
										</div>

										<div
											style="margin-top:8px; overflow-x:auto;"
										>
											<table
												class="restrictions-table"
												style="width:100%;"
											>
												<thead>
													<tr>
														<th>Name</th>
														<th>Position</th>
														<th>Email</th>
														<th>Phone</th>
													</tr>
												</thead>
												<tbody>
													{#each getChapterContactsForSelectedEvent() as c, idx (idx)}
														<tr>
															<td
																>{c.fullName ||
																	""}</td
															>
															<td
																>{c.position ||
																	"Member"}</td
															>
															<td
																>{c.email ||
																	""}</td
															>
															<td
																>{c.phone ||
																	""}</td
															>
														</tr>
													{/each}
												</tbody>
											</table>
										</div>

										<div
											class="help-text"
											style="margin-top:8px;"
										>
											Emergency phones are derived from
											these contacts (first {MAX_EMERGENCY_PHONES}
											unique phones).
										</div>
									</div>
								{:else}
									<div
										class="detail-address"
										style="margin-top: 6px;"
									>
										<div class="detail-label">
											Chapter exec contacts
										</div>
										<div class="detail-value">Not set</div>
									</div>
								{/if}

								<div class="detail-address">
									<div class="detail-label">
										Fraternity address
									</div>

									<div class="detail-value-with-actions">
										<span class="detail-value">
											{bestEffortFratAddressFromEvent(
												selectedEvent,
											) || "Not set"}
										</span>

										{#if bestEffortFratAddressFromEvent(selectedEvent)}
											<button
												type="button"
												class="icon-button subtle-icon"
												on:click={() =>
													copyToClipboard(
														bestEffortFratAddressFromEvent(
															selectedEvent,
														),
														"Address",
													)}
												aria-label="Copy address"
												title="Copy address"
											>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													stroke-width="2"
												>
													<rect
														x="9"
														y="9"
														width="13"
														height="13"
														rx="2"
														ry="2"
													></rect>
													<path
														d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
													></path>
												</svg>
											</button>

											<button
												type="button"
												class="icon-button subtle-icon"
												on:click={() =>
													openInMaps(
														bestEffortFratAddressFromEvent(
															selectedEvent,
														),
													)}
												aria-label="Open in Maps"
												title="Open in Maps"
											>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													viewBox="0 0 24 24"
													fill="none"
													stroke="currentColor"
													stroke-width="2"
												>
													<path
														d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"
													></path>
													<circle
														cx="12"
														cy="10"
														r="3"
													></circle>
												</svg>
											</button>
										{/if}
									</div>
								</div>

								<div class="quick-actions">
									{#if eventEmergencyPhones && eventEmergencyPhones.length}
										<button
											class="quick-action-btn"
											on:click={() =>
												copyToClipboard(
													eventEmergencyPhones.join(
														"\n",
													),
													"Emergency phones",
												)}
										>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												stroke-width="2"
											>
												<rect
													x="9"
													y="9"
													width="13"
													height="13"
													rx="2"
													ry="2"
												></rect>
												<path
													d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
												></path>
											</svg>
											Copy Emergency Phones
										</button>
									{/if}

									<button
										class="quick-action-btn"
										type="button"
										on:click={() =>
											copyToClipboard(
												`${selectedEvent.title} - ${formatDateTime(selectedEvent.startTime)} to ${formatDateTime(selectedEvent.endTime)}`,
												"Event details",
											)}
									>
										<svg
											xmlns="http://www.w3.org/2000/svg"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											stroke-width="2"
										>
											<rect
												x="9"
												y="9"
												width="13"
												height="13"
												rx="2"
												ry="2"
											></rect>
											<path
												d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
											></path>
										</svg>
										Copy Event Info
									</button>
								</div>

								{#if copyStatus}
									<div
										class={`inline-status ${copyStatusType === "error" ? "inline-status-error" : "inline-status-success"}`}
									>
										{copyStatus}
									</div>
								{/if}
							</div>

							<div class="ticket-section">
								<h4>Noise / Nuisance Tickets</h4>
								<div class="ticket-badges">
									<span class="ticket-pill"
										>Warnings: {selectedEvent.warningCount ||
											0}</span
									>
									<span class="ticket-pill"
										>Noise tickets: {selectedEvent.noiseTicketCount ||
											0}</span
									>
									<span class="ticket-pill"
										>Nuisance tickets: {selectedEvent.nuisanceTicketCount ||
											0}</span
									>
								</div>

								{#if isPolice()}
									<div class="ticket-actions">
										<button
											class="button small"
											on:click={() =>
												issueTicket("warning")}
											>Send 20-minute warning</button
										>
										<button
											class="button small"
											on:click={() =>
												issueTicket("noise")}
											>Issue noise ticket</button
										>
										<button
											class="button small"
											on:click={() =>
												issueTicket("nuisance")}
											>Issue nuisance ticket</button
										>
									</div>
								{/if}
							</div>

							{#if (isChapter() && selectedEvent.chapterUsername === currentUser.username) || isIFCAdmin()}
	<div
		class="event-actions"
		style="margin-top: 16px;"
	>
		<button
			type="button"
			class={`button small ghost cancel-button ${!canCancelSelectedEvent ? "cancel-button-disabled" : ""} ${cancelConfirmEventId === selectedEvent?.id ? "cancel-confirm" : ""}`}
			disabled={!canCancelSelectedEvent ||
				isSelectedEventCancelledOrDenied}
			on:click={() => {
				if (
					!canCancelSelectedEvent ||
					isSelectedEventCancelledOrDenied
				)
					return;

				if (
					cancelConfirmEventId ===
					selectedEvent.id
				) {
					cancelEvent(selectedEvent.id);
				} else {
					armCancelConfirm(
						selectedEvent.id,
					);
				}
			}}
		>
			{#if selectedEvent.status === "cancelled"}
				Event Cancelled
			{:else if selectedEvent.status === "denied"}
				Event Denied by IFC
			{:else if cancelConfirmEventId === selectedEvent.id}
				Confirm?
			{:else}
				Cancel Event
			{/if}
		</button>

		{#if eventDeleteMessage}
			<div
				class={`inline-alert ${eventDeleteIsError ? "inline-alert-error" : "inline-alert-success"}`}
			>
				{eventDeleteMessage}
			</div>
		{/if}
	</div>
{/if}
						{:else}
							<div class="muted">
								Select an event to view details and ticket
								history.
							</div>
						{/if}
					</div>
				</section>
			{:else}
				<!-- Neighbor Dashboard (when logged in as neighbor) -->
				{#if isNeighbor()}
					{#if activePage === "dashboard"}
						<section class="panel">
							<h3>Nearby Fraternities</h3>

							{#if loadingNearbyChapters}
								<div class="muted">
									Loading nearby chapters...
								</div>
							{:else if nearbyChapters.length === 0}
								<div class="muted">
									No fraternity chapters found within 300 feet
									of your address.
								</div>
							{:else}
								<div
									style="display:grid; gap:16px; margin-top:16px;"
								>
									{#each nearbyChapters as chapter}
										<div
											class="panel"
											style="padding:16px; border-radius:14px;"
										>
											<div
												style="display:flex; align-items:flex-start; gap:12px;"
											>
												{#if chapter.crestUrl}
													<img
														src={chapter.crestUrl}
														alt="{chapter.name} crest"
														class="event-row-crest"
													/>
												{/if}
												<div style="flex:1;">
													<div
														style="font-weight:700; font-size:16px;"
													>
														{chapter.name}
													</div>
													<div
														class="muted"
														style="margin-top:4px;"
													>
														{chapter.address}
													</div>
													<div
														class="muted"
														style="margin-top:4px;"
													>
														Distance: {Math.round(
															chapter.distance,
														)} feet
													</div>

													{#if chapter.hasAccess}
														<div
															style="margin-top:14px; padding-top:14px; border-top:1px solid #eef0f6;"
														>
															<div
																style="font-weight:700; margin-bottom:8px;"
															>
																Emergency
																Contacts:
															</div>
															{#if chapter.contacts && chapter.contacts.length}
																<div
																	style="display:flex; flex-direction:column; gap:6px;"
																>
																	{#each chapter.contacts as contact}
																		<div
																			style="font-size:14px;"
																		>
																			<span
																				style="font-weight:600;"
																				>{contact.position ||
																					"Member"}</span
																			>
																			{#if contact.fullName}—
																				{contact.fullName}{/if}
																			{#if contact.phone}
																				<br
																				/>
																				<a
																					href="tel:{contact.phone}"
																					style="color:#2563eb; text-decoration:none;"
																				>
																					{formatPhoneForDisplay(
																						contact.phone,
																					)}
																				</a>
																			{/if}
																		</div>
																	{/each}
																</div>
															{:else}
																<div
																	class="muted"
																>
																	No contacts
																	available
																</div>
															{/if}
														</div>

														<button
															class="button small"
															style="margin-top:12px;"
															on:click={() =>
																openComplaintModal(
																	chapter,
																)}
														>
															File Another
															Complaint
														</button>
													{:else}
														<div
															style="margin-top:14px; padding:12px; background:#fef3c7; border:1px solid #fde68a; border-radius:12px; color:#92400e; font-size:14px;"
														>
															To view emergency
															contacts, please
															file a noise
															complaint (access
															granted for 24
															hours)
														</div>

														<button
															class="button small"
															style="margin-top:12px;"
															on:click={() =>
																openComplaintModal(
																	chapter,
																)}
														>
															File Noise Complaint
														</button>
													{/if}
												</div>
											</div>
										</div>
									{/each}
								</div>
							{/if}
						</section>

						<section class="panel" style="margin-top:24px;">
							<h3>Your Complaints</h3>

							{#if loadingNeighborComplaints}
								<div class="muted">Loading...</div>
							{:else if neighborComplaints.length === 0}
								<div class="muted">
									No complaints filed yet.
								</div>
							{:else}
								<div
									style="display:grid; gap:12px; margin-top:16px;"
								>
									{#each neighborComplaints as complaint}
										<div
											class="panel"
											style="padding:14px; border-radius:14px;"
										>
											<div
												style="display:flex; justify-content:space-between; align-items:flex-start; gap:12px;"
											>
												<div style="flex:1;">
													<div
														style="font-weight:700;"
													>
														{complaint.chapterName}
													</div>
													<div
														class="muted"
														style="margin-top:4px; font-size:13px;"
													>
														{formatDateTime(
															complaint.createdAt,
														)}
													</div>
													<div
														style="margin-top:8px; font-size:14px;"
													>
														{complaint.complaintText}
													</div>

													{#if complaint.adminNotes}
														<div
															style="margin-top:10px; padding:10px; background:#dbeafe; border-radius:8px; font-size:13px;"
														>
															<div
																style="font-weight:700; margin-bottom:4px;"
															>
																Admin Notes:
															</div>
															{complaint.adminNotes}
														</div>
													{/if}
												</div>

												<span
													class={`badge ${complaint.status === "resolved" ? "badge-muted" : ""}`}
												>
													{complaint.status}
												</span>
											</div>
										</div>
									{/each}
								</div>
							{/if}
						</section>
					{:else if activePage === "account"}
						<!-- Neighbor Account Page -->
						<section class="panel account-panel">
							<h1>Neighbor Account</h1>
							<p class="help-text">
								Your neighbor profile details.
							</p>

							<div class="form-grid" style="margin-top:14px;">
								<div class="field">
									<label>Name</label>
									<input
										class="input"
										bind:value={neighborEditName}
										placeholder="Your name"
									/>
								</div>
								<div class="field">
									<label>Email</label>
									<input
										class="input"
										bind:value={neighborEditEmail}
										placeholder="you@example.com"
									/>
								</div>
								<div class="field">
									<label>Phone</label>
									<input
										class="input"
										bind:value={neighborEditPhone}
										placeholder="(303) 555-0123"
									/>
								</div>
								<div class="field">
									<label>Address</label>
									<input
										class="input"
										bind:value={neighborEditAddress}
										placeholder="1234 Main St, Boulder, CO 80302"
									/>
								</div>
							</div>

							<div
								style="margin-top:12px; display:flex; gap:12px; align-items:center;"
							>
								<button
									class="button primary"
									on:click={saveNeighborAccount}
									disabled={neighborEditSaving}
								>
									{neighborEditSaving
										? "Saving..."
										: "Save changes"}
								</button>
								{#if neighborEditMessage}
									<div class="help-text">
										{neighborEditMessage}
									</div>
								{/if}
							</div>

							<div class="help-text" style="margin-top:12px;">
								Total Complaints Filed: <b
									>{neighborComplaints.length}</b
								>
							</div>
						</section>
					{/if}
				{/if}
			{/if}
		{/if}
	</main>

	{#if showSignupModal}
		<div class="modal-backdrop" on:click={closeSignupModal}></div>

		<div class="modal" on:click|stopPropagation>
			<div class="modal-card">
				<div class="modal-header">
					<h3>Chapter Exec Sign Up</h3>
					<button class="modal-close" on:click={closeSignupModal}
						>×</button
					>
				</div>

				{#if signupStep === "form"}
					<div class="field">
						<label>Fraternity</label>
						<select class="input" bind:value={suFratName}>
							<option value="" disabled selected>Select…</option>
							{#each FRAT_OPTIONS as f}
								<option value={f}>{f}</option>
							{/each}
						</select>
					</div>

					<div class="field">
						<label>Full name</label>
						<input
							class="input"
							bind:value={suFullName}
							placeholder="First Last"
						/>
					</div>

					<div class="field">
						<label>Fraternity Address</label>
						<textarea
							class="input"
							rows="2"
							bind:value={suAddress}
							placeholder="1205 College Ave, Boulder CO 80302"
						></textarea>
					</div>
					<div class="field">
						<label>Position (within fraternity)</label>
						<input
							class="input"
							bind:value={suPosition}
							placeholder="President, Risk Manager, Social Chair..."
						/>
					</div>

					<div class="field">
						<label>Email</label>
						<input
							class="input"
							bind:value={suEmail}
							placeholder="name@example.com"
						/>
					</div>
					<div class="field">
						<label>Phone (SMS)</label>
						<input
							class="input"
							bind:value={suPhone}
							placeholder="3035551234"
						/>
					</div>

					<div class="field">
						<label>Confirm phone</label>
						<input
							class="input"
							bind:value={suPhoneConfirm}
							placeholder="3035551234"
						/>
					</div>

					{#if suError}
						<div class="login-error">{suError}</div>
					{/if}

					<div class="modal-actions">
						<button
							class="button small ghost"
							type="button"
							on:click={closeSignupModal}
						>
							Cancel
						</button>
						<button
							class="button small"
							type="button"
							on:click={() => {
								suError = validateSignup();
								if (!suError) signupStep = "verify";
							}}
						>
							Continue
						</button>
					</div>
				{:else if signupStep === "verify"}
					<div
						class="help-text"
						style="margin-bottom:10px; color:white;"
					>
						Verify your info. When you confirm, your request will be
						sent to IFC for approval.
					</div>

					<div class="verify-list">
						<div>
							<span class="muted">Fraternity:</span>
							{suFratName}
						</div>
						<div><span class="muted">Name:</span> {suFullName}</div>
						<div>
							<span class="muted">Position:</span>
							{suPosition}
						</div>
						<div>
							<span class="muted">Address:</span>
							{suAddress}
						</div>
						<div><span class="muted">Email:</span> {suEmail}</div>
						<div>
							<span class="muted">Phone:</span>
							{normalizeUSPhone(suPhone)}
						</div>
					</div>

					{#if suError}
						<div class="login-error">{suError}</div>
					{/if}

					<div class="modal-actions">
						<button
							class="button small ghost"
							type="button"
							on:click={() => (signupStep = "form")}
						>
							Back
						</button>
						<button
							class="button small"
							type="button"
							on:click={submitSignup}
							disabled={suBusy}
						>
							{suBusy ? "Submitting..." : "Confirm & Submit"}
						</button>
					</div>
				{:else}
					<div class="help-text" style="color:white;">
						{suSuccess}
					</div>
					<div class="modal-actions">
						<button
							class="button small"
							type="button"
							on:click={closeSignupModal}
						>
							Done
						</button>
					</div>
				{/if}
			</div>
		</div>
	{/if}

	<!-- svelte-ignore a11y-click-events-have-key-events -->
	<!-- svelte-ignore a11y-no-static-element-interactions -->
	{#if showAccessModal && isIFCAdmin()}
		<div class="modal-backdrop" on:click={closeAccessModal}></div>

		<!-- svelte-ignore a11y-click-events-have-key-events -->
		<div class="modal" on:click|stopPropagation>
			<div class="modal-card access-modal-card">
				<div class="modal-header">
					<h3>Who can view what?</h3>
					<button class="modal-close" on:click={closeAccessModal}
						>×</button
					>
				</div>

				<div class="access-grid">
					<div class="access-col">
						<h4 class="access-title">Chapter Accounts</h4>
						<ul class="access-list">
							<li>
								· View and edit their own account basics (name,
								emergency phones).
							</li>
							<li>
								· Register new events and view their event
								details.
							</li>
							<li>
								· Cancel their own events any time before 3
								hours of said event, after that they must
								contact IFC to cancel.
							</li>
							<li>
								· Cannot manage other chapters or edit other
								chapters’ accounts.
							</li>
						</ul>
					</div>

					<div class="access-col">
						<h4 class="access-title">Police Accounts</h4>
						<ul class="access-list">
							<li>
								· View registered events and event details
								needed for response.
							</li>
							<li>
								· View chapter contacts + emergency phones for
								response.
							</li>
							<li>
								· Send warning / noise / nuisance tickets (SMS)
								from the event page.
							</li>
							<li>
								· Cannot register events, cancel events, or edit
								chapter accounts.
							</li>
							<li>· No access to the Chapters admin page.</li>
						</ul>
					</div>

					<div class="access-col access-col-wide">
						<h4 class="access-title">IFC Admin</h4>
						<ul class="access-list">
							<li>
								· Full access to the Chapters admin page: view,
								search, and select chapters; create new
								chapters; delete chapters.
							</li>
							<li>
								· Edit any fraternity account details: chapter
								name, residence address, exec contacts, derived
								emergency phones, crest key.
							</li>
							<li>
								· Enable or disable a chapter (toggle status).
							</li>
							<li>
								· Set or change a chapter’s password (and view
								current password in the admin UI).
							</li>
							<li>
								· View recent events and stats per chapter
								(warning notices, noise tickets, nuisance
								tickets).
							</li>
							<li>
								· Manage pending signup requests: approve (adds
								requester to contacts and emergency phones), or
								deny.
							</li>
							<li>
								· View all registered events and details; can
								cancel any chapter’s event at any time.
							</li>
							<li>
								· Open the “Who can view what?” access guide
								modal.
							</li>
						</ul>
					</div>
				</div>

				<div class="modal-actions">
					<button
						class="button small ghost"
						type="button"
						on:click={closeAccessModal}
					>
						Close
					</button>
				</div>
			</div>
		</div>
	{/if}

	{#if showNewEventModal}
		<div class="modal-backdrop" on:click={closeNewEventModal}></div>

		<div class="modal" on:click|stopPropagation>
			<div class="modal-card">
				<div class="modal-header">
					<h3>Register New Event</h3>
					<button class="modal-close" on:click={closeNewEventModal}
						>×</button
					>
				</div>

				<div class="field">
					<label>Event title / occasion</label>
					<input
						class="input"
						placeholder="Homecoming, Brotherhood Night, Rush BBQ"
						bind:value={newEventTitle}
					/>
				</div>

				<div class="field">
					<label>Event type</label>
					<select class="input" bind:value={newEventType}>
						{#each EVENT_TYPES as t}
							<option value={t.value}>{t.label}</option>
						{/each}
					</select>
				</div>
				{#if isParty(newEventType)}
	<div class="field">
		<label>Expected Event Capacity</label>
		<input
			class="input"
			type="number"
			min="1"
			placeholder="300"
			bind:value={newEventCapacity}
		/>
	</div>

	<div class="field">
		<label>Event Street Address</label>
		<input
			class="input"
			placeholder="1012 14th Street"
			bind:value={newEventStreetAddress}
		/>
	</div>

	<div class="field">
		<label>Name of Person Filling Out Form</label>
		<select
			class="input"
			bind:value={newEventRegistrantPickerPhone}
			on:change={syncRegistrantFromEmergencyPicker}
		>
			<option value="">Select an emergency contact…</option>
			{#each getEmergencyContactDropdownOptions(currentUser) as c}
				<option value={normalizeUSPhone(c.phone || "")}>
					{c.fullName || "-"}{#if c.position} ({c.position}){/if}
					{#if c.phone} — {formatPhoneForDisplay(c.phone)}{/if}
				</option>
			{/each}
		</select>
		<div class="help-text">
			This must be one of your chapter Emergency Contacts (Account page).
		</div>
	</div>

	<div class="field">
		<label>Contact Information for Person Filling Out Form</label>
		<div style="display:flex; gap:10px; flex-wrap:wrap;">
			<input
				class="input"
				placeholder="Email"
				style="min-width:260px; flex:1;"
				bind:value={newEventRegistrantEmail}
			/>
			<input
				class="input"
				placeholder="Phone"
				style="min-width:200px; flex:1;"
				bind:value={newEventRegistrantPhone}
			/>
		</div>
	</div>


	<div class="field">
		<label>Hired out-of-house Event Organizer or Artist/DJ?</label>
		<div style="display:flex; align-items:center; gap:12px; flex-wrap:wrap;">
			<label class="switch" style="margin:0;">
				<input
					type="checkbox"
					bind:checked={newEventHasOrganizer}
				/>
				<span>Yes</span>
			</label>

			{#if newEventHasOrganizer}
				<input
					class="input"
					placeholder="Organizer/Artist name (optional)"
					bind:value={newEventOrganizerName}
					style="min-width:260px;"
				/>
			{/if}
		</div>
	</div>

	{#if !currentUser.securityCompany || !currentUser.riskPolicy}
		<div class="missing-requirements-banner">
			<span class="warning-icon">⚠️</span>
			<div>
				<div class="banner-title">Missing Required Information</div>
				<div class="banner-text">
					Please complete your chapter account settings before registering a party:
				</div>
				<ul class="banner-list">
					{#if !currentUser.securityCompany}
						<li>Third Party Security Company</li>
					{/if}
					{#if !currentUser.riskPolicy}
						<li>Risk Management Plan</li>
					{/if}
				</ul>
			</div>
		</div>
	{/if}
{/if}
				<div
					class={`datetime-grid ${isParty(newEventType) ? "datetime-grid-party" : ""}`}
				>
					<div class="field">
						<label>Start date & time</label>
						<div class="datetime-stack">
							<input
								type="date"
								class="input date-input"
								bind:value={startDate}
							/>
							<div class="time-row">
								<input
									class="input time-input"
									type="number"
									min="1"
									max="12"
									bind:value={startHour}
								/>
								<input
									class="input time-input"
									type="number"
									min="0"
									max="59"
									bind:value={startMinute}
								/>
								<select
									class="input time-input"
									bind:value={startAmPm}
								>
									<option>AM</option>
									<option>PM</option>
								</select>
							</div>
						</div>
					</div>

					<div class="field">
						<label>End date & time</label>
						<div class="datetime-stack">
							<input
								type="date"
								class="input date-input"
								bind:value={endDate}
							/>
							<div class="time-row">
								<input
									class="input time-input"
									type="number"
									min="1"
									max="12"
									bind:value={endHour}
								/>
								<input
									class="input time-input"
									type="number"
									min="0"
									max="59"
									bind:value={endMinute}
								/>
								<select
									class="input time-input"
									bind:value={endAmPm}
								>
									<option>AM</option>
									<option>PM</option>
								</select>
							</div>
						</div>
					</div>

					{#if isParty(newEventType)}
						<div class="field presets-field">
							<label>Presets</label>
							<div class="presets-buttons">
								<button
									type="button"
									class="button small"
									on:click={() => applyPartyPreset(4)}
									>Thu</button
								>
								<button
									type="button"
									class="button small"
									on:click={() => applyPartyPreset(5)}
									>Fri</button
								>
								<button
									type="button"
									class="button small"
									on:click={() => applyPartyPreset(6)}
									>Sat</button
								>
							</div>
							<div class="help-text">
								Automatically fills in data for next selected
								date, from 10:00 PM → 2:00 AM
							</div>
						</div>
					{/if}
				</div>

				{#if isParty(newEventType)}
					<div class="restrictions-card">
						<div class="restrictions-title">Restrictions</div>

						<ul class="restrictions-list">
							<li>
								<strong>Scheduling window:</strong> Parties must
								be
								<strong
									>scheduled Sunday through Wednesday night</strong
								>
								and must
								<strong
									>take place Thursday through Saturday night</strong
								>.
							</li>
							<li>
								<strong>Duration:</strong> Events may last a
								maximum of
								<strong>4 hours</strong> and must
								<strong>end by 2:00 AM</strong>.
							</li>
							<li>
								<strong>Registration requirement:</strong> Blackout
								dates are dates where events may not be registered.
								If your chapter is found to have held an event without
								registering it, your chapter will face the appropriate
								penalties.
							</li>
						</ul>

						<div class="restrictions-table-wrap">
							<div class="restrictions-table-title">
								<strong>Blackout Dates – Spring 2026</strong>
							</div>
							<table class="restrictions-table">
								<thead>
									<tr><th>Date(s)</th><th>Reason</th></tr>
								</thead>
								<tbody>
									<tr
										><td>Thu Jan 8th – Sun Jan 11th</td><td
											>First Weekend Back</td
										></tr
									>
									<tr
										><td>Mar 17th</td><td
											>Saint Patrick’s Day</td
										></tr
									>
									<tr><td>Apr 20th</td><td>4/20</td></tr>
									<tr
										><td>Fri Apr 24th – Sun Apr 26th</td><td
											>Last Weekend After Classes</td
										></tr
									>
								</tbody>
							</table>
						</div>
					</div>
				{/if}

				{#if newEventError}
					<div class="login-error">{newEventError}</div>
				{/if}

				<div class="modal-actions">
					<button
						class="button small"
						type="button"
						on:click={closeNewEventModal}>Cancel</button
					>
					<button
						class="button small"
						type="button"
						on:click={createEvent}>Save event</button
					>
				</div>
			</div>
		</div>
	{/if}
	{#if showBuzzEventModal}
		<div class="modal-backdrop" on:click={closeBuzzEventModal}></div>

		<div class="modal" on:click|stopPropagation>
			<div class="modal-card">
				<div class="modal-header">
					<h3>BuzzCrew — Event Details</h3>
					<button class="modal-close" on:click={closeBuzzEventModal}
						>×</button
					>
				</div>

				{#if buzzEditEvent}
					<div class="field">
						<label>Chapter</label>
						<input
							class="input"
							placeholder="Chapter name"
							bind:value={buzzEditEvent.chapter}
						/>
					</div>

					<div class="field">
						<label>Event type</label>
						<input
							class="input"
							placeholder="Party / Date Dash / Brotherhood..."
							bind:value={buzzEditEvent.eventType}
						/>
					</div>

					<div class="field">
						<label>Theme</label>
						<input
							class="input"
							placeholder="Theme / title"
							bind:value={buzzEditEvent.theme}
						/>
					</div>

					<div class="field">
						<label>Address</label>
						<input
							class="input"
							placeholder="Address"
							bind:value={buzzEditEvent.address}
						/>
					</div>

					<div class="field">
						<label>Time</label>
						<input
							class="input"
							placeholder="10:00pm - 2:00am"
							bind:value={buzzEditEvent.time}
						/>
					</div>

					<div class="chapter-detail-row2">
						<div class="field">
							<label>OIC position</label>
							<input
								class="input"
								placeholder="President / Risk Manager"
								bind:value={buzzEditEvent.oicPosition}
							/>
						</div>
						<div class="field">
							<label>OIC name</label>
							<input
								class="input"
								placeholder="Name"
								bind:value={buzzEditEvent.oicName}
							/>
						</div>
					</div>

					<div class="chapter-detail-row2">
						<div class="field">
							<label>OIC phone #</label>
							<input
								class="input"
								placeholder="(###) ###-####"
								bind:value={buzzEditEvent.oicPhone}
							/>
						</div>
						<div class="field">
							<label>Other OIC</label>
							<input
								class="input"
								placeholder="Name"
								bind:value={buzzEditEvent.otherOicName}
							/>
						</div>
					</div>

					<div class="field">
						<label>Other OIC phone #</label>
						<input
							class="input"
							placeholder="(###) ###-####"
							bind:value={buzzEditEvent.otherOicPhone}
						/>
					</div>

					<div class="modal-actions">
						<button
							class="button small ghost"
							type="button"
							on:click={closeBuzzEventModal}
						>
							Cancel
						</button>
						<button
							class="button small"
							type="button"
							on:click={saveBuzzEventEdits}
						>
							Save
						</button>
					</div>
				{/if}
			</div>
		</div>
	{/if}

	<!-- Neighbor Signup Modal -->
	{#if showNeighborSignup}
		<div class="modal-backdrop" on:click={closeNeighborSignupModal}></div>
		<div class="modal" on:click|stopPropagation>
			<div class="modal-card">
				<div class="modal-header">
					<h3>Register as Neighbor</h3>
					<button
						class="modal-close"
						on:click={closeNeighborSignupModal}>×</button
					>
				</div>

				<div class="field">
					<label>Full Name</label>
					<input
						class="input"
						bind:value={neighborSignupData.name}
						placeholder="Jane Doe"
					/>
				</div>

				<div class="field">
					<label>Email</label>
					<input
						class="input"
						type="email"
						bind:value={neighborSignupData.email}
						placeholder="jane@example.com"
					/>
				</div>

				<div class="field">
					<label>Phone</label>
					<input
						class="input"
						bind:value={neighborSignupData.phone}
						placeholder="(303) 555-1234"
					/>
				</div>

				<div class="field">
					<label>Address</label>
					<div class="address-input-wrap">
						<textarea
							class="input"
							rows="2"
							bind:value={neighborSignupData.address}
							on:input={(e) => {
								neighborSignupData.address = e.target.value;
								clearTimeout(addressSearchTimeout);
								addressSearchTimeout = setTimeout(
									() =>
										fetchAddressSuggestions(
											neighborSignupData.address,
										),
									150,
								);
							}}
							placeholder="Start typing your address..."
						></textarea>
						{#if addressSuggestions.length > 0}
							<div class="address-suggestions">
								{#each addressSuggestions as s}
									<button
										type="button"
										class="address-suggestion-row"
										on:click={() => {
											neighborSignupData.address =
												s.formatted;
											addressSuggestions = [];
										}}
									>
										<div class="address-suggestion-main">
											{s.formatted}
										</div>
										<div class="address-suggestion-sub">
											{s.context}
										</div>
									</button>
								{/each}
							</div>
						{/if}
					</div>
					<div class="help-text">
						We'll use this to find nearby fraternity chapters
						(within 300 feet)
					</div>
				</div>

				<div class="field">
					<label>Password</label>
					<input
						class="input"
						type="password"
						bind:value={neighborSignupData.password}
					/>
				</div>

				<div class="field">
					<label>Confirm Password</label>
					<input
						class="input"
						type="password"
						bind:value={neighborSignupData.confirmPassword}
					/>
				</div>

				{#if neighborSignupError}
					<div class="login-error">{neighborSignupError}</div>
				{/if}

				<div class="modal-actions">
					<button
						class="button small ghost"
						on:click={closeNeighborSignupModal}>Cancel</button
					>
					<button
						class="button small"
						on:click={submitNeighborSignup}
						disabled={neighborSignupBusy}
					>
						{neighborSignupBusy ? "Registering..." : "Register"}
					</button>
				</div>
			</div>
		</div>
	{/if}

	<!-- Complaint Modal -->
	{#if showComplaintModal}
		<div class="modal-backdrop" on:click={closeComplaintModal}></div>
		<div class="modal" on:click|stopPropagation>
			<div class="modal-card">
				<div class="modal-header">
					<h3>File Noise Complaint</h3>
					<button class="modal-close" on:click={closeComplaintModal}
						>×</button
					>
				</div>

				{#if selectedChapter}
					<div
						style="
      margin-bottom:16px;
      padding:14px 14px;
      border-radius:14px;
      background:
        linear-gradient(135deg, #0b1633 0%, #143b72 100%),
        linear-gradient(115deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.06) 22%, rgba(255,255,255,0) 55%);
      box-shadow:
        0 10px 24px rgba(0,0,0,0.28),
        inset 0 1px 0 rgba(255,255,255,0.14);
      border:1px solid rgba(255,255,255,0.10);
    "
					>
						<div
							style="font-weight:800; color:#f8fafc; letter-spacing:0.2px;"
						>
							{selectedChapter.name}
						</div>
						<div
							style="font-size:13px; margin-top:4px; color:rgba(248,250,252,0.80);"
						>
							{selectedChapter.address}
						</div>
					</div>
				{/if}

				<div class="field">
					<label>Description (minimum 10 words)</label>
					<textarea
						class="input"
						rows="5"
						bind:value={complaintText}
						placeholder="Describe the noise issue: time, volume, bass levels, impact on your residence, etc..."
					></textarea>
					<div class="help-text">
						Word count: {complaintText
							.trim()
							.split(/\s+/)
							.filter((w) => w.length > 0).length} / 10 minimum
					</div>
				</div>

				<div
					class="help-text"
					style="margin-top:8px; padding:10px; background:#fef3c7; border-radius:8px; color:#92400e;"
				>
					Filing a complaint will notify a chapter's exec team via
					email, and IFC will review the issue. Chapters cannot see
					your name or email unless you include it in the complaint
					description.
				</div>

				{#if complaintError}
					<div class="login-error">{complaintError}</div>
				{/if}

				<div class="modal-actions">
					<button
						class="button small ghost"
						on:click={closeComplaintModal}>Cancel</button
					>
					<button
						class="button small"
						on:click={submitComplaint}
						disabled={complaintBusy}
					>
						{complaintBusy ? "Submitting..." : "Submit Complaint"}
					</button>
				</div>
			</div>
		</div>
	{/if}

	<!-- Admin Complaint Detail Modal -->
{#if showComplaintDetailModal && selectedComplaint}
	<div class="modal-backdrop" on:click={closeComplaintDetailModal}></div>
	<div class="modal" on:click|stopPropagation>
		<div class="modal-card">
			<div class="modal-header">
				<h3>Complaint Details</h3>
				<button
					class="modal-close"
					on:click={closeComplaintDetailModal}>×</button
				>
			</div>

			<div style="margin-bottom:16px;">
				<div style="font-weight:700; font-size:15px;">
					{selectedComplaint.neighborName} → {selectedComplaint.chapterName}
				</div>
				<div class="muted" style="margin-top:4px; font-size:13px;">
					{selectedComplaint.neighborPhone} • {selectedComplaint.neighborEmail}
				</div>
				<div class="muted" style="margin-top:2px; font-size:13px;">
					Filed: {formatDateTime(selectedComplaint.createdAt)}
				</div>
				<span
					class={`badge ${selectedComplaint.status === "resolved" ? "badge-muted" : ""}`}
					style="margin-top:8px;"
				>
					{selectedComplaint.status}
				</span>
			</div>

			<div class="field">
				<label>Complaint</label>
				<div
					style="padding:12px; background:#f3f4f6; border-radius:10px; font-size:14px; line-height:1.5; color: black;"
				>
					{selectedComplaint.complaintText}
				</div>
			</div>

			<div class="field">
				<label>Admin Notes (visible to neighbor and chapter)</label>
				<textarea
					class="input"
					rows="4"
					bind:value={complaintAdminNotes}
					placeholder="Add notes about resolution, follow-up actions, etc..."
				></textarea>
			</div>

			<div class="modal-actions">
				<button
					class="button small ghost"
					on:click={closeComplaintDetailModal}>Close</button
				>
				{#if selectedComplaint.status !== "resolved"}
					<button
						class="button small"
						on:click={resolveComplaint}
						disabled={resolvingComplaint}
					>
						{resolvingComplaint
							? "Resolving..."
							: "Mark as Resolved"}
					</button>
				{:else}
					<button
						class="button small"
						on:click={updateComplaintNotes}
						disabled={resolvingComplaint}
					>
						{resolvingComplaint
							? "Updating..."
							: "Update Notes"}
					</button>
				{/if}
			</div>
		</div>
	</div>
{/if}

	<!-- Neighbor Profile Modal -->
	{#if showNeighborProfileModal && selectedNeighbor}
		<div class="modal-backdrop" on:click={closeNeighborProfileModal}></div>
		<div class="modal" on:click|stopPropagation>
			<div class="modal-card" style="max-width:700px;">
				<div class="modal-header">
					<h3>Neighbor Profile</h3>
					<button
						class="modal-close"
						on:click={closeNeighborProfileModal}>×</button
					>
				</div>

				<div style="margin-bottom:20px;">
					<div style="font-weight:700; font-size:16px;">
						{selectedNeighbor.name}
						{#if !selectedNeighbor.enabled}
							<span
								class="badge badge-muted"
								style="margin-left:8px;">Disabled</span
							>
						{/if}
					</div>
					<div class="muted" style="margin-top:4px;">
						{selectedNeighbor.email} • {selectedNeighbor.phone}
					</div>
					<div class="muted" style="margin-top:2px;">
						{selectedNeighbor.address}
					</div>
					<div style="margin-top:10px;">
						<span class="badge"
							>Total complaints: {selectedNeighbor.totalComplaints ||
								0}</span
						>
					</div>

					<button
						class="button small"
						style="margin-top:12px;"
						on:click={() =>
							toggleNeighborEnabled(
								selectedNeighbor.id,
								selectedNeighbor.enabled,
							)}
					>
						{selectedNeighbor.enabled
							? "Disable Account"
							: "Enable Account"}
					</button>
				</div>

				<h4 style="margin-bottom:12px;">Complaint History</h4>

				{#if neighborProfileComplaints.length === 0}
					<div class="muted">No complaints filed.</div>
				{:else}
					<div
						style="display:grid; gap:12px; max-height:400px; overflow-y:auto; color:black;"
					>
						{#each neighborProfileComplaints as complaint}
							<div
								class="panel"
								style="padding:12px; border-radius:10px;"
							>
								<div style="font-weight:700; font-size:14px;">
									{complaint.chapterName}
									<span
										class={`badge ${complaint.status === "resolved" ? "badge-muted" : ""}`}
										style="margin-left:8px; font-size:11px;"
									>
										{complaint.status}
									</span>
								</div>
								<div
									class="muted"
									style="font-size:12px; margin-top:2px;"
								>
									{formatDateTime(complaint.createdAt)}
								</div>
								<div style="margin-top:6px; font-size:13px;">
									{complaint.complaintText}
								</div>
								{#if complaint.adminNotes}
									<div
										style="margin-top:8px; padding:8px; background:#dbeafe; border-radius:6px; font-size:12px;"
									>
										<strong>Admin Notes:</strong>
										{complaint.adminNotes}
									</div>
								{/if}
							</div>
						{/each}
					</div>
				{/if}

				<div class="modal-actions">
					<button
						class="button small"
						on:click={closeNeighborProfileModal}>Close</button
					>
				</div>
			</div>
		</div>
	{/if}
</div>
